import { applyReturnAnswer, returnRpcArgs, type ReturnAnswer, type ReturnLine } from './supplier-return'
import { itemsCountOf } from './service-charge'
import { chunkSourceArg, purchaseSourceArgs, stockSourceArg, type PaidSource } from './paid-source'
import { selectOpenShift, selectLastClosedShift } from './branch-scope'
import { create } from 'zustand'
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware'
import { toast } from 'sonner'
import { rsStatus, type OutgoingStatus } from './rsge-status'
import { createClient } from './supabase/client'
import { allRows, allRowsWith, linesOfSales } from './supabase/all-rows'
import { SALES_WINDOW_DAYS } from './history-window'
import { windowStart, bootEdge, hasOlder, needsOlder, clampToFirst, mergeById, sortSalesDesc, bootRecentFrom, mergeKeepExisting, bootHistoryCommits, historyRetryAction, prependUnique, appendUnique } from './history-window'
import { clearFeatureCache, featuresFromKv, FEATURE_KEYS, type FeatureKey } from './features'
import { businessDay, parseDayStartHour } from './business-day'
import { parseSalaryAmount, salaryLimitFrom, type SalaryHint } from './drawer-float'
import { clubSettingsFromKv, withoutClubColumns, type ClubStatus } from './club'
import { clubRefusal } from './club-mail'
import { CLUB_RULES_VERSION } from './club-rules'
import { afterClubRefusal, withoutStatus } from './club-till'
import { parsePriceMismatch, PRICE_REFRESH_TABLES } from './reprice'
import { mapHeldCart } from './held-carts'
import { lotsEpochOk, type StockLot } from './expiry'
import { customerRefreshed, mapPriceGroup } from './price-groups'
import { packErrorKey } from './pack-errors'
import { t, translate, type Key } from './i18n'
import { serverRefusalKey } from './server-refusals'
import { supplierDeleteRefusal } from './supplier-debt'
import { receiptOnProducts } from './receive'
import { returnedToShelf, soldFromShelf } from './service'
import { roundWeight } from './weight'
import { offlineShelf, replyShelfPlan, type ShelfStamp } from './sale-cache'
import type { Promotion, PromotionItem, PromotionKind } from './promotions'
import type { PlatformBilling } from './subscription'
import { pickMembership, type SupportSession, type SupportStatus, type SupportDuration } from './support'
import { parseGate, type PlatformGate } from './admin-gate'
import type { WaybillDraft } from './waybill-out'
import {
  loadOfflineQueue, enqueueOfflineSale, removeFromOfflineQueue,
  markOfflineSaleFailed, isBrowserOnline, isNetworkError, paymentLanded,
  reuseAttempt, saleAttemptKey, returnAttemptKey, type UnsavedAttempt, offlinePayload, liveSalesFilter, onlineOnlySale,
} from './offline'
import { SCALE_DEFAULTS, type ScaleValueType } from './scale-barcode'
import { CATEGORY_TEMPLATES, templateIn, templateProfile } from './category-templates'
import { reportError } from './error-report'
import { isStoreCode, nextStoreCodes, variantName } from './variants'
import { readProductCacheEntry, scheduleProductCache, flushProductCache, clearProductCache } from './product-cache'
import { acceptDelta, cacheSnapshot, freshIdentity, offlineCopy, refreshPath, warmCopy } from './warm-products'
import {
  OPENING_PURCHASE_NOTE, findOpeningPurchase, missingOpeningLines, openingAfterSalesCount, openingPurchaseLines,
  type OpeningAnswer, type OpeningItem, type OpeningPurchaseLine,
} from './opening-stock'
import { mergeChanged, newestStamp, sinceFor, MAX_CHANGED } from './product-sync'
import { tgEnabled } from './telegram'
import { allCodes, codeOwner } from '@/lib/barcodes'
import { FIT_CHUNK, fitsOf, oemOf, parseFitRefusal, type FitEdit, type FitRefusal } from '@/lib/fitment'
import { duplicateCodeOf, isDuplicateCode, parseCodeRefusal, type CodeRefusal } from '@/lib/code-errors'
import { normalizeSerial, type SerialLookup } from './serials'
import { mapRepair, type Repair, type RepairDraft } from './repairs'
import { mapOnlineOrder, onlineErrorKey, ONLINE_ORDER_SELECT, type OnlineOrder } from './online-orders'
import { mapStoreSite, type StoreSite, type StoreSitePatch } from './online-listing'
import { parseSyncFlags } from './sync-flags'
import { exchangeErrorOf, mapCandidatePage, parseExchangeDays, returnRails, stockDelta, type CandidatePage, type ExchangeErrorKey } from './exchange'
import {
  type Category,
  type Product,
  type ProductUnit,
  type Sale,
  type SaleItem,
  type FiscalStatus,
  type HeldCart,
  type HeldCartItem,
  type PriceGroup,
  type QuoteSnapshot,
  type DiscountType,
  type Supplier,
  type Purchase,
  type PurchaseItem,
  type SupplierPayment,
  type SupplierReturn,
  type SupplierReturnItem,
  type Customer,
  type CustomerPayment,
  type Cashier,
  type Shift,
  type PayMethod,
  type CardProvider,
  type TaxStatus,
  type Expense,
  type ExpenseCategory,
  type RecurringExpense,
  type RecurringMode,
  type CartRemoval,
  type PriceChange,
  type Branch,
  type StockTransfer,
  type StockTransferItem,
  type ProductAddon,
  type ProductIngredient,
  type ProductConversion,
  type ProductBreakdown,
  type BreakdownPart,
  type SupplierProductCode,
  type CatalogProduct,
  type CashMovement,
  type CashDirection,
  type CashMovementReason,
  type DrawerExpenseCategory, type BankCode, CASHIER_COLUMNS,
  type Prepaid, type PrepaidKind, type PrepaidMethod, type PrepaidMove, type HeldCartItem as PrepaidLine } from './mock-data'
import { mapPrepaid, mapPrepaidMove, prepaidErrorKey, prepaidReturnRails } from './prepaid'
import type { DemandRecord, SupplierOrder, SupplierOrderItem } from './mock-data'
import { mapDemand } from './demand'
import { mapSupplierOrder, mapSupplierOrderItem, type DraftLine, type LastSupplier, type SaveOrderInput } from './supplier-order'
import { latestOnly } from './demand-queue'
import { applyBillReply, mapBatch, mapBill, mapHall, mapTable, parseTableRef, mapKitchenCards, mapReadyGroups, mapSeated, type Batch, type Bill, type DiningTable, type Hall, type KitchenCard, type PingStation, type ReadyGroup, type Seated, type Station, type TableShape } from './tables'
import { mapVisits, type Visit } from './tables'
import { confirmOpKey, confirmWithFreshAddons, mapRequests, type GuestRequest } from './table-requests'
import { acceptBillReply, acceptsRead, branchSwitchTables, pendingOp, settleOp, splitServiceProducts, stationDeviceId, tableErrorCode, tableErrorKey, tableSettingsFromKv, tablesNeedReload, tillDutiesFor, tombstoneAfterLoad, type TillDuties } from './tables-store'
import { mapCheckPrint, type CheckPrint, type CheckSnapshot } from './check-print'
import { mapWaiterStats, type WaiterStatRow } from './waiter-stats'
import { mapShift } from './shift-row'

const supabase = createClient()

/** 125: the lines of a read's sales, fetched BY those sales (lib/supabase/all-rows.ts
 *  `linesOfSales`) — `sale_items_between` recomputed its whole window for every page
 *  and a 6-store chain's boot died on the statement timeout. A failed sales read is
 *  the lines' failure too. */
function linesOf(sales: { data: any[] | null; error: unknown }): Promise<{ data: any[] | null; error: unknown }> {
  if (sales.error) return Promise.resolve({ data: null, error: sales.error })
  return linesOfSales<any>((ids, a, b) => supabase.rpc('sale_items_of', { p_sales: ids }).order('id').range(a, b),
    (sales.data ?? []).map((row: { id: string }) => row.id))
}

/**
 * Sales in [from, to) — null is open — newest first, and their lines (127).
 *
 * `sales_page` picks each page on the index before it reads a row; the table's
 * own offset read fetched every skipped sale as well — 1 s for the last page
 * of 45 000 at the 6-store scale test, 9.6 s for the 45 pages. Each page's
 * lines are asked for the moment the page arrives (`allRowsWith`); they used
 * to wait for the last page, 19 s more. `lines` false skips them: a cashier,
 * whom the server gives none anyway (125) — 180 empty requests a boot there.
 *
 * A sale committed DURING the read shifts every later page by one: it arrives
 * twice, at the foot of one page and the head of the next, and every total
 * would count it twice until the next read. Both lists are made unique by id.
 */
function readSales(from: string | null, to: string | null, lines: Promise<boolean>) {
  const both = allRowsWith<any, any>(
    (a, b) => supabase.rpc('sales_page', { p_offset: a, p_limit: b - a + 1, p_from: from, p_to: to }),
    async (rows) => (await lines) ? linesOf({ data: rows, error: null }) : { data: [], error: null },
  )
  return {
    salesRead: both.then(({ rows }) => rows.error ? rows : { data: mergeById<any>([], rows.data ?? []), error: null }),
    itemsRead: both.then(({ followed }) => followed.error ? followed : { data: mergeById<any>([], followed.data ?? []), error: null }),
  }
}

// 089: the older-history loaders run one at a time. Both mutate the same
// range edge, and a fetch decided on a stale edge would leave a gap or
// double-load a range; queueing keeps the invariant in lib/history-window.ts.
let historyChain: Promise<void> = Promise.resolve()

// 1.24 — every hydrate, offline boot and tenant clear is a new generation;
// the boot's history (phase 2) commits only into the generation that started
// it (bootHistoryCommits) — nothing may land in another branch or shop.
let bootGen = 0
// 1.65 — the lazy slices' reads: only the newest lands, and a local change outdates every read in flight
// (Codex's final review #2: an older read re-showed a need just closed)
const demandReads = latestOnly()
const orderReads = latestOnly()
const tableReads = latestOnly()   // 1.66 — a reply applied after a read started makes that read stale
// 1.71 (review m1) — the add-on rules' re-reads (a live signal, the confirm's retry): only the newest lands, and the
// owner's own save on this device outdates a read already in flight
const addonReads = latestOnly()
const waiterReads = latestOnly()  // 1.67 — the waiter's ready feed: an older read never replaces a newer one
const kitchenReads = latestOnly() // 1.67 — the kitchen board, the same rule
const seatedReads = latestOnly()  // 1.68 — the paid tables still seated, the same rule
const visitReads = latestOnly()   // 1.69 — the parties at the tables, the same rule
const requestReads = latestOnly() // 1.69 — the guests' open requests, the same rule
// 1.66 (Codex final #5, #6) — bills that left the list (paid / cancelled), at the sequence they left; and the op of a
// call that ended in doubt, kept for the next press of the same thing
const tableGone = new Map<string, number>()
let tableSeq = 0
const tableOps = new Map<string, { op: string; at: number }>()
/** 1.62 — counts batch reads, so only the newest lands (lotsEpochOk). */
let lotsEpoch = 0
// 153 (Codex): the newest read or save of the shop's site wins — a slow read
// started before a save must not put the old values back on screen.
let siteSeq = 0
// What phase 2 needs to continue where phase 1 stopped: [from, edge).
let bootPending: { gen: number; from: string; edge: string; firstAt: string | null | undefined } | null = null

type StoreGet = () => StoreState
type StoreSet = (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>)) => void

/**
 * 1.24 — phase 2 of the boot: the 90-day history behind the till, read
 * after `isHydrated` so the till opens on the recent range alone. Queued on
 * the history chain (never interleaving with ensureSalesSince /
 * ensureCustomerSales); merged so rows already in the store win (realtime,
 * refreshLive, a sale just made); dropped if another hydrate started.
 */
function loadBootHistory(get: StoreGet, set: StoreSet): Promise<void> {
  const job = bootPending
  if (!job) return historyChain
  const run = async () => {
    if (!bootHistoryCommits({ gen: job.gen, currentGen: bootGen, edge: job.edge, currentEdge: get().salesLoadedFrom })) return
    // Already loaded back to the window (a screen's ensureSalesSince did it
    // after a failure): nothing left to read.
    if (Date.parse(job.edge) <= Date.parse(job.from)) {
      bootPending = null
      set({ historyReady: true, historyFailed: false })
      return
    }
    const { salesRead, itemsRead } = readSales(job.from, job.edge, Promise.resolve(get().userRole !== 'cashier'))
    const [older, olderItems] = await Promise.all([salesRead, itemsRead])
    if (!bootHistoryCommits({ gen: job.gen, currentGen: bootGen, edge: job.edge, currentEdge: get().salesLoadedFrom })) return
    if (older.error || olderItems.error) {
      console.error('boot history load error', { older, olderItems })
      set({ historyFailed: true })
      toast.error(t('store.historyFailed'), { duration: 8000 })
      return
    }
    bootPending = null
    set((state) => ({
      sales: sortSalesDesc(mergeKeepExisting(state.sales, (older.data ?? []).map(mapSale))),
      saleItems: mergeKeepExisting(state.saleItems, (olderItems.data ?? []).map(mapSaleItem)),
      salesLoadedFrom: bootEdge(job.from, job.firstAt),
      historyReady: true,
      historyFailed: false,
    }))
  }
  const safe = () => run().catch((e) => {
    console.error('boot history', e)
    if (job.gen === bootGen) set({ historyFailed: true })
  })
  historyChain = historyChain.then(safe, safe)
  return historyChain
}
// Customers whose out-of-window rows are already here — one fetch each per
// hydrate, cleared when hydrate resets the window.
const customerSalesLoaded = new Set<string>()

export interface Settings {
  companyName: string
  companyId: string
  address: string
  phone: string
  taxStatus: TaxStatus   // 'none' | 'small' (1%) | 'vat' (18%)
  bankDetails: string    // optional IBAN/bank line used in debt-reminder messages
  // 2026-09-26 — yesterday by mail at 08:00 to the owner (app/api/owner-report).
  // Default OFF: a shop is mailed only after its owner switches it on.
  ownerReport: boolean
  // 123 (2026-09-30) — the owner's Telegram pushes (app/api/telegram), KV
  // tgMorning / tgShift / tgEvening / tgSale / tgLoss / tgWeek / tgWaybill. A key absent = the owner's
  // default of 2026-09-30: the reports, loss control and the week ON, every sale OFF — the same
  // rule the minute tick applies (lib/telegram.ts `tgEnabled`).
  tgMorning: boolean
  tgShift: boolean
  tgEvening: boolean
  tgSale: boolean
  tgLoss: boolean
  tgWeek: boolean
  tgWaybill: boolean
  /** B2 — a new order from the shop's website (default ON; only a shop with a store gets any). */
  tgOrder: boolean
  // 2026-09-28 — the till's manual-discount field. null = the owner has not
  // chosen: the shop's behaviour decides (lib/pos-discount.ts).
  posDiscount: boolean | null
  // 2026-09-28 — „მარტივი რეჟიმი" (lib/features.ts): the owner's choice per
  // feature, KV `feature.<key>`; a key absent = the shop's behaviour decides.
  features: Partial<Record<FeatureKey, boolean>>
  // 2026-09-26 — the owner hid the first-day checklist (components/first-day-banner.tsx)
  firstDayDone: boolean
  // 2026-09-26 (step 3b) — the template the shop chose: its PROFILE (lib/category-templates.ts)
  shopType: string
  // 119 — the float the owner keeps in the drawer for change. At close the
  // app proposes to leave it and reminds (never refuses) when less is left.
  floatKeep: boolean
  floatAmount: number
  // 2026-10-03 — the hour the shop's business day starts, 0 (midnight, the
  // default) … 23. A night shop's night is one day (lib/business-day.ts).
  dayStartHour: number
  // 137 — the cashier's daily salary, paid from the count at close.
  salaryKeep: boolean
  salaryAmount: number
  // Electronic scale (weight-embedded EAN-13 labels) — default OFF.
  scaleEnabled: boolean
  scalePrefixes: string          // comma-separated 2-digit prefixes, e.g. "21,22"
  scalePluDigits: 4 | 5
  scaleValueType: ScaleValueType // 'weight_g' | 'price_tetri'
  // kasa.ge fiscalization feature flag — default OFF (mock mode until real creds)
  fiscalKasaEnabled: boolean
  // 2026-10-01 — a fiscal device is connected to this shop's till. OFF (the
  // default) hides every fiscal control and alarm: the till's switch, the
  // kiosk's receipt, the retry, the failed-receipt warnings, the reversal.
  // Most shops' register is the bank's terminal, which the app cannot drive yet.
  fiscalDevice: boolean
  // 1.36 (157) — how many days after the first purchase a cashier may
  // exchange an item; past it the owner's PIN. 0–365, default 14.
  exchangeDays: number
  // CCTV playback URL template for the events journal ({YYYY}{MM}{DD}{hh}{mm}{ss}{unix});
  // empty = the "camera time" box shows timestamp + copy only
  cameraUrlTemplate: string
  // Self-checkout kiosk: which bank terminal the kiosk's card sales are
  // recorded against, and whether the (assisted) cash path is offered.
  kioskCardProvider: CardProvider
  kioskAllowCash: boolean
  /** Opt-in: contribute this shop's barcodes/names/photos to the shared catalog. */
  shareCatalog: boolean
  /** ISO timestamp of the last downloaded backup — drives the stale nudge. */
  lastBackupAt: string
  /**
   * 100 — the driver this shop last sent goods with. A shop uses the same
   * one week after week, and his personal number has to be right: RS.GE
   * checks it against the registry and refuses a waybill that gets it wrong
   * (-1014). Typed once, offered every time after.
   */
  lastDriverName: string
  lastDriverTin: string
  lastDriverCar: string
  /** 085: loyalty — a sale with a customer earns `loyaltyPct` % of what was
   *  paid as bonus ₾. Read by create_sale from the settings rows; these two
   *  are the till's copy for showing the customer what they will get. */
  loyaltyEnabled: boolean
  loyaltyPct: number
  /** 188 (1.63) — the club's monthly threshold in GEL of bonus earned (null = nobody is a member) and the
   *  poster's registration code (club_rotate_code). The switch is features.club. */
  clubThreshold: number | null
  clubCode: string
  /** 196 (1.68) — the café's service rate in % (0 = none; G1). Written only by `tableServiceSet` — it makes the
   *  service product the bills need — never by `updateSettings` (whose keys are these names, not `table.*`). */
  tableServicePct: number
  /** 196 (1.68) — a paid table is free at once (a counter-pay café); off (the default) = it stays „გადახდილია"
   *  until a waiter or the till frees it (G22). Written only by `setTableFreeOnPay`. */
  tableFreeOnPay: boolean
  /**
   * May a CASHIER login count a stocktake (blind)? Each owner decides — some
   * want staff counting, some count themselves (owner, 2026-09-11).
   * `null` = never asked: the stocktake screen puts the question to the owner
   * once. Enforced in the database (`stocktake_count` / `stocktake_state`,
   * migration 070), not here — this is only what the screens show.
   */
  cashierStocktake: boolean | null
}

/** 112 — see `addModelVariants`. */
export type ModelVariantsInput = {
  model_id?: string
  model_name: string
  category_id: string
  purchase_price: number
  sale_price: number
  wholesale_price: number | null
  photo_url: string | null
  /** `photo_url` here is the size's colour's own photo, over the model's (2026-09-28). */
  variants: { size: string | null; color: string | null; barcode: string | null; quantity: number; photo_url?: string | null }[]
  /** 153 — the website marks every new size takes (`newSizeMarks`); absent = the table's defaults. */
  site?: { online?: boolean; online_hidden?: boolean }
  /** 183 — who paid for the sizes' stock („პირადი თანხით" …) */
  paid_source?: PaidSource | null
}

/** 112 — see `updateModel`. */
export type ModelPatch = {
  model_name?: string
  category_id?: string
  sale_price?: number
  wholesale_price?: number | null
  photo_url?: string | null
  /** A photo per colour (2026-09-28): size id → the photo it now shows —
   *  only the sizes that change (`photoWrites` in lib/variants.ts). */
  photos?: Record<string, string | null>
  /** 153 — the website mark of the shop's mode, for every size */
  online?: boolean
  online_hidden?: boolean
}

export interface StoreState {
  categories: Category[]
  products: Product[]
  /**
   * The sale history in memory is a WINDOW, not the table (089, 2026-09-15):
   * hydrate loads the last SALES_WINDOW_DAYS and the screens that need
   * more ask for it. `sales`/`saleItems` always cover one contiguous range
   * [salesLoadedFrom, now] — see lib/history-window.ts for why that shape
   * matters. `salesLoadedFrom === null` = the whole history is loaded.
   */
  sales: Sale[]
  saleItems: SaleItem[]
  salesLoadedFrom: string | null
  /** When the shop's first sale was (null = no sales; undefined = not known). */
  salesFirstAt: string | null | undefined
  /** An older range is being fetched right now — screens show it, and the
   *  Excel export waits for it rather than writing a partial month. */
  salesLoading: boolean
  /** 1.24 — the boot's 90-day history has arrived (phase 2). False while it
   *  loads in the background; screens that show anything older than today
   *  read it through lib/history-gate.ts. True offline (nothing is loading). */
  historyReady: boolean
  /** 1.24 — phase 2 failed; `retryHistory` asks again. */
  historyFailed: boolean
  /** 1.27 — which hydrate the sales in memory came from: a new one on every
   *  hydrate, offline boot and tenant clear. The accounting page's server
   *  sums are keyed by it (two hydrates can produce the same edge). */
  salesGeneration: number
  retryHistory: () => void
  heldCarts: HeldCart[]
  suppliers: Supplier[]
  purchases: Purchase[]
  purchaseItems: PurchaseItem[]
  supplierPayments: SupplierPayment[]
  customers: Customer[]
  customerPayments: CustomerPayment[]
  /** 107 — money put into / taken out of the drawer during shifts. */
  cashMovements: CashMovement[]
  /** 174 — gift card / deposit moves (issue, redeem, return, refund); the drawer's cash terms read them */
  prepaidMoves: PrepaidMove[]
  /** 170 — goods that went back to a supplier (owner and accountant read them). */
  supplierReturns: SupplierReturn[]
  supplierReturnItems: SupplierReturnItem[]
  cashiers: Cashier[]
  shifts: Shift[]
  expenses: Expense[]
  /** 121 — monthly costs (rent, salary, utilities). RLS: owner + accountant. */
  recurringExpenses: RecurringExpense[]
  /** 122 — what left a cart unsold, and price changes; the sales window's
   *  90 days. RLS: owner + accountant (a till writes removals, never reads). */
  cartRemovals: CartRemoval[]
  priceChanges: PriceChange[]
  /** 1.65 (191) — the till's needs (open ones) and the supplier orders (open + the sales window). Read when
   *  their screen opens, re-read on a broadcast — never at boot; null = not read on this device. Owner
   *  (needs) and owner + accountant (orders) only, by RLS. */
  demands: DemandRecord[] | null
  supplierOrders: { orders: SupplierOrder[]; items: SupplierOrderItem[] } | null
  /** 1.66 (192) — a café's halls, tables and OPEN bills with their lines. Read when the tables screen opens, re-read
   *  on a broadcast — never at boot; null = not read on this device. Memory only (never persisted). */
  halls: Hall[] | null
  diningTables: DiningTable[] | null
  bills: Record<string, Bill> | null
  /** 1.67 (193) — the waiter's ready feed and the kitchen board, read only on the device that opened them;
   *  null = not read here. Memory only. */
  waiterReady: ReadyGroup[] | null
  kitchenCards: KitchenCard[] | null
  /** 196 (1.68) — the shop's service product (the oldest marked row, the server's own pick); never in `products`.
   *  null = none yet (no rate was ever set). */
  serviceProductId: string | null
  /** 196 — the marked rows themselves, kept apart from `products`: the product copy and the delta's count hold them */
  serviceRows: Product[]
  /** 196 — the paid tables whose guests are still seated (G22); read with the tables; null = not read here */
  seated: Seated[] | null
  /** 1.69 (197) — the parties at this branch's tables (a scan, „სტუმარი დაჯდა", a bill), read with the tables where the
   *  switch `tableQr` is on; null = not read here */
  visits: Visit[] | null
  /** 1.69 (198) — the guests' open requests of this branch as the database shows them to THIS login (a waiter: his own
   *  party's and nobody's; a till: all); null = not read here */
  guestRequests: GuestRequest[] | null
  /** 196 — this device's café duties (the bar's queue, the waiters' checks) for the shop and the branch they were
   *  chosen in (G11); persisted; read through `tillDutiesFor` */
  tillDuties: TillDuties | null
  // Multi-branch: the org's locations + this DEVICE's active branch.
  // products[].quantity shows the ACTIVE branch's stock when >1 branch
  // (org-wide total when single-branch — identical numbers by invariant).
  branches: Branch[]
  activeBranchId: string | null
  // Multi-branch Stage 3: history of stock moved between branches.
  stockTransfers: StockTransfer[]
  stockTransferItems: StockTransferItem[]
  // Combo links (migration 044): selling a trigger product prompts the
  // cashier to add its add-ons (coffee → cup, lid).
  productAddons: ProductAddon[]
  // 182 (1.59): what a product or a category includes in its price — a drink's
  // cup, lid, grams of a kg bag. The SERVER takes them (create_sale); the till
  // reads these rows only to draw the hint and keep the add-on strip honest.
  productIngredients: ProductIngredient[]
  // "1 sealed jar → N cups" stock conversions (migration 046).
  productConversions: ProductConversion[]
  /** 1.55 — one → many breakdowns (172), newest first. */
  productBreakdowns: ProductBreakdown[]
  // Supplier code -> our product (migration 047), for waybill matching.
  supplierCodes: SupplierProductCode[]
  // 086: the owner's promotions (price + bundle), with their products.
  promotions: Promotion[]
  /** 178 (1.58) — the shop's price groups; the till prices a grouped customer's cart with them */
  priceGroups: PriceGroup[]
  /** 181 (I4) — the groups were read for this shop; until then a grouped customer's sale waits */
  priceGroupsRead: boolean
  /** 1.63 — the club standing the server last answered, per customer (a card scan, a link). Display only:
   *  the server decides a club price at the sale (190). */
  clubStatuses: Record<string, ClubStatus>
  /** 1.61 (184) — the shop's open batches (qty > 0), every branch; the till's note, the owner's window */
  stockLots: StockLot[]
  /** 1.61 — the batches were read for this shop: until then the owner's window says nothing and marks nothing */
  stockLotsLoaded: boolean
  refreshStockLots: () => Promise<void>
  /** 186 — „ჩამოწერა" of one batch (write_off_stock books the money); false on refusal */
  writeOffLot: (lotId: string, qty: number, note?: string) => Promise<boolean>
  /** 186 — re-date a batch / move units between it and the undated batch; the stock never changes */
  editLot: (lotId: string, expiry: string | null, qty: number) => Promise<boolean>
  /** 186 — „ვადიანი პროდუქტი" on (the shelf becomes a batch of `expiry`, or undated) / off */
  setTrackExpiry: (productId: string, on: boolean, expiry?: string | null) => Promise<boolean>
  settings: Settings
  pin: string
  currentOrg: CurrentOrg | null
  /** A platform admin at any assurance level (`platformGate !== 'none'`):
   *  the menu link and the no-shop redirect — he must REACH the console to
   *  enter his code. Never a reason to allow anything; the database decides. */
  isPlatformAdmin: boolean
  /** 1.48 — what /platform must still ask (lib/admin-gate.ts). Not persisted:
   *  it belongs to this session's sign-in, not to the device. */
  platformGate: PlatformGate
  /** This user's role in the current org (migration 057). `owner` sees
   *  everything; `cashier` sees the POS only. NULL until hydrate finishes —
   *  treat that as "not an owner yet", never as owner. */
  userRole: 'owner' | 'cashier' | 'accountant' | null
  isHydrated: boolean
  /** The last boot read failed on the server (not the network): the shop is there, the rows did not come.
   *  The hydrator shows „ვერ ჩაიტვირთა — თავიდან ცდა", never „no shop on this account" (2026-09-30:
   *  a 6-store chain's boot hit a statement timeout and its owner read „მაღაზია ვერ მოიძებნა"). */
  loadFailed: boolean
  /**
   * Which signed-in user the persisted cache belongs to.
   *
   * The cache is a whole shop — products, settings, shifts, cashiers, the
   * org itself — and it outlives a sign-out. Without this, signing in as
   * somebody else in the same browser showed THEIR PREDECESSOR'S SHOP:
   * `isHydrated` was still true from the previous session, so the guard in
   * StoreHydrator skipped hydrate() entirely and nothing ever overwrote it.
   * Reported live on 2026-09-09 by two different owners.
   */
  cachedUserId: string | null
  /** Wipe the tenant-scoped cache. Called when the signed-in user changes and
   *  on sign-out — see the comment on `cachedUserId`. */
  clearTenantCache: () => void
  isLocked: boolean
  /** True while the shop has never chosen a screen PIN, so the lock would open
   *  with DEFAULT_LOCK_PIN — see `lock`. */
  pinIsDefault: boolean
  /** The „choose a PIN first" dialog. 'lock' locks the screen once a PIN is
   *  set; 'set' only sets it (the kiosk card). null = closed. */
  pinSetup: null | 'lock' | 'set'
  openPinSetup: (mode: 'lock' | 'set') => void
  closePinSetup: () => void
  /** A counter the header's shift control watches. The POS banner bumps it
   *  rather than opening a close dialog of its own: the POS page swaps to its
   *  „ცვლა დახურულია" screen the moment the shift closes, which would unmount
   *  a dialog it owned before the Z-report it shows next. */
  closeShiftAsked: number
  askCloseShift: () => void
  isMobileSidebarOpen: boolean
  isDesktopSidebarCollapsed: boolean
  /** 1.39 — a narrow screen's menu opened OVER the page for a moment
   *  (lib/sidebar-fit.ts). Never persisted. While it is up the screen is the
   *  menu's: the till's hotkeys stand down (Codex, 1.39 review). */
  sidebarPeek: boolean
  // One-shot handoff from the header quick-search to the warehouse list.
  globalSearch: string

  // Self-checkout kiosk mode — a per-DEVICE lock (persisted): while on, the
  // KioskGuard bounces every app route back to /kiosk until the admin PIN
  // turns it off. The device stays logged in — kiosk is a lock, not a login.
  kioskMode: boolean
  setKioskMode: (on: boolean) => void
  /** 1.67 — this device is locked to the kitchen screen (persisted like kioskMode; leaving needs the system PIN) */
  kitchenMode: { station: 'kitchen' | 'bar' | 'all' } | null
  setKitchenMode: (mode: { station: 'kitchen' | 'bar' | 'all' } | null) => void

  /** `offline`: the caller already knows the server is unreachable (the
   *  session check failed on the network) — go to the offline copy at once
   *  instead of waiting out postgrest's retries. */
  hydrate: (opts?: { offline?: boolean }) => Promise<void>
  /**
   * Make sure sales back to `fromISO` (null = the whole history) are in
   * memory. No-op when they already are. Serialised: two screens asking at
   * once queue, and the second re-checks what the first brought.
   */
  ensureSalesSince: (fromISO: string | null) => Promise<void>
  /** One customer's sales and returns from BEFORE the loaded range — the
   *  credit ledger must show a debt's origin even when it is a year old. */
  ensureCustomerSales: (customerId: string) => Promise<void>
  setMobileSidebarOpen: (open: boolean) => void
  setDesktopSidebarCollapsed: (collapsed: boolean) => void
  setSidebarPeek: (open: boolean) => void
  setGlobalSearch: (q: string) => void
  // Transient handoff (NOT persisted): "print labels for these products" —
  // set by the RS.GE receive flow, consumed by the warehouse label dialog.
  labelQueue: string[] | null
  setLabelQueue: (ids: string[] | null) => void
  /** Keepz is set up for this shop (082): the „QR" chip shows on the till
   *  only then — an unusable chip on every card sale confuses a cashier. */
  qrPayReady: boolean
  refreshQrPay: () => Promise<void>
  // Offline mode: network state + the locally-queued sales counter.
  isOnline: boolean
  setOnline: (v: boolean) => void
  offlinePending: number
  isSyncingOffline: boolean
  /** 137 — the salary ceiling close_shift named for a shift (`SALARY_LIMIT:<n>`):
   *  the close dialog proposes it instead of its own copy, which a cashier's
   *  login (no expenses in the store) cannot compute. */
  salaryCeilingHint: SalaryHint | null
  syncOfflineSales: () => Promise<void>
  /** true when the rows were written (1.63: the club card reports only what landed) */
  updateSettings: (s: Partial<Settings>) => Promise<boolean>
  setPin: (pin: string) => Promise<boolean>
  /** 101 — the money PIN: written, never read back. Owner only. */
  setMoneyPin: (pin: string) => Promise<boolean>
  /** 101 — true while the money PIN is still the same value as the screen
   *  lock, which every cashier can read. Answered by the server; the value
   *  itself never travels. */
  moneyPinShared: boolean
  checkMoneyPinShared: () => Promise<void>
  requestUpgrade: () => Promise<void>
  /** 097 — where and how much to pay (the one platform_billing row); null until loaded. */
  platformBilling: PlatformBilling | null
  loadPlatformBilling: () => Promise<PlatformBilling | null>
  /** 097 — the owner says „I transferred" (mails the platform admin). */
  claimPayment: (months: 1 | 12, bank: string | null) => Promise<boolean>
  /** 100 — RS.GE's own lists for the outgoing waybill form (types, units, transport). */
  waybillRefs: { types: RsgeRef[]; units: RsgeRef[]; transTypes: RsgeRef[] } | null
  /** 100 — why the lists did not come, in RS.GE's own words. Empty lists and a
   *  refused call look identical on a screen; this is what tells them apart. */
  /** `noCreds` — the shop has not connected RS.GE at all (no service user):
   *  a different sentence from „RS.GE refused us". */
  waybillRefsError: { message: string; raw?: string; noCreds?: boolean } | null
  /** 100 — RS.GE's refusal of a save, kept ON THE SCREEN. A toast leaves
   *  before a code can be written down, and the code is the whole answer. */
  waybillSaveError: { message: string; code?: string | null; codeText?: string | null; raw?: string; sent?: string } | null
  loadWaybillRefs: () => Promise<{ types: RsgeRef[]; units: RsgeRef[]; transTypes: RsgeRef[] } | null>
  /** 100 — the buyer's name as the tax office spells it, from their code. */
  rsgeNameFromTin: (tin: string) => Promise<string | null>
  /** 100 — save an outgoing waybill as a DRAFT and remember it against the
   *  sale — or, 129, against the branch transfer it documents. */
  saveOutgoingWaybill: (draft: WaybillDraft, saleId: string | null, transferId?: string | null) => Promise<OutgoingWaybill | null>
  /** 100 — a waybill RS.GE itself wrote, fetched only when a save was
   *  refused for a reason we cannot read. Read-only, owner only. */
  rsgeSample: {
    waybillId: string | null; waybill: string | null; templates: string | null
    /** Which call answered: by internal id, or by the № on the paper. The
     *  distinction is the whole point — they are different numbers. */
    via?: 'id' | 'number' | null
  } | null
  fetchRsgeSample: (id?: string) => Promise<void>
  /** 100 — the waybill this sale already has, if any. */
  getOutgoingWaybill: (saleId: string) => Promise<OutgoingWaybill | null>
  /** 129 — the internal-transport waybill this transfer already has, if any. */
  getTransferWaybill: (transferId: string) => Promise<OutgoingWaybill | null>
  /** 100 — every waybill this shop issued from here, newest first. */
  listOutgoingWaybills: () => Promise<OutgoingWaybill[]>
  /**
   * 100 — ask RS.GE what became of the ones we still think are drafts, and
   * remember the answer. RS.GE is the truth about a document's status; our row
   * goes stale the moment the owner activates one in the cabinet.
   */
  /**
   * 100 — ACTIVATE one saved waybill at RS.GE. The only call in this store
   * that files a tax document, and it is never reached except from a button
   * the owner pressed and confirmed. Returns the row as RS.GE now describes
   * it, or null when RS.GE refused (it says why in a toast).
   */
  activateOutgoingWaybill: (row: OutgoingWaybill) => Promise<OutgoingWaybill | null>
  refreshOutgoingStatuses: (rows: OutgoingWaybill[]) => Promise<{
    rows: OutgoingWaybill[]
    /** Ids RS.GE could not answer for. A check that failed and a status that
     *  did not change look identical on screen — this is what tells them
     *  apart, and without it „შენახული" could mean „we never asked". */
    failed: { id: string; error: string }[]
  }>
  /** 098 — set when THIS login is inside a shop as MartePOS support (a temporary membership). */
  supportSession: SupportSession | null
  /** 098 — the owner opens the door: a one-time code (shown once) for 1 / 4 / 24 hours. */
  createSupportGrant: (canWrite: boolean, minutes: SupportDuration, note: string) => Promise<{ id: string; code: string; valid_until: string } | null>
  /** 098 — the owner sends the open request to the console (and a mail to the admin). */
  sendSupportRequest: (grantId: string) => Promise<boolean>
  /** 098 — the owner shuts the door, or support leaves. */
  endSupportSession: () => Promise<boolean>
  fetchSupportStatus: () => Promise<SupportStatus | null>
  lock: () => void
  unlock: (pin: string) => boolean
  addCategory: (cat: Omit<Category, 'id' | 'created_at'>) => Promise<boolean>
  /** Top-level categories by name, in ONE insert (all or none) — the Excel import's missing ones. */
  addCategories: (names: string[]) => Promise<Category[] | null>
  updateCategory: (id: string, cat: Partial<Category>) => Promise<boolean>
  /** Create a starter category tree. Returns how many rows were added. */
  applyCategoryTemplate: (templateKey: string) => Promise<number>
  deleteCategory: (id: string) => Promise<void>
  /** The row as saved, or null when the database refused (the store toasted).
   *  `renumber`: the barcode is a shop code this device numbered — if another
   *  device took it in between, number again once instead of failing. */
  addProduct: (p: Omit<Product, 'id' | 'created_at'>, opts?: { renumber?: boolean; /** 183 — who paid for its stock */ paidSource?: PaidSource | null }) => Promise<Product | null>
  /**
   * Gives each of these products that has NO barcode the shop's next own code
   * (29…), so a label can carry it and the till can scan it (2026-09-27, the
   * owner: cups and toys that came without one). A product that meanwhile got
   * a barcode is left alone. Returns id → code for the ones written.
   */
  assignStoreCodes: (ids: string[]) => Promise<Record<string, string>>
  /** `quiet`: no toast on failure — for the till's periodic refresh, where
   *  a blip every three minutes must not nag the cashier. */
  /**
   * The lines of ONE purchase, read from the database and merged into the
   * cache.
   *
   * `purchaseItems` is loaded once at boot, and three write paths add a
   * purchase without its lines: addProduct, importProducts and restockProduct
   * (neither RPC returns them). So a purchase made during this session had an
   * empty line list in memory while the database held it perfectly well — and
   * the detail dialog read that emptiness as „nothing was saved", which is a
   * claim about the database it had never checked. Ask instead.
   */
  fetchPurchaseItems: (purchaseId: string) => Promise<PurchaseItem[]>
  /** 114: reads only what changed since the last read, unless `full`. */
  refreshProducts: (opts?: { quiet?: boolean; full?: boolean }) => Promise<void>
  /**
   * The Excel importer's new products. Written 500 at a time (133): one
   * insert of 30 000 rows ran past the 8 s a request may take and nothing
   * was written. Answers how many were created — on a refusal, the ones
   * before it stay, and the importer says so.
   */
  importProducts: (
    products: Omit<Product, 'id' | 'created_at'>[],
    onProgress?: (done: number) => void,
    /** 183 — who paid for the new products' stock */
    paidSource?: PaidSource | null,
  ) => Promise<{ created: number; failed: boolean; /** 1.44: the code another product holds */ code?: string }>
  /**
   * 133 — a branch's opening stock (or any restock of many existing products)
   * in `restock_products` calls of 250 lines: the same server-side rule as a
   * single restock (units at the active branch, the cost blended into the
   * org-wide average), ONE purchase for the whole list. Answers how many lines
   * were written; on a refusal the earlier calls stay written.
   */
  restockProducts: (
    items: { product_id: string; quantity: number; unit_cost: number; /** 1.61 (185) — the pack date */ expiry_date?: string | null }[],
    note: string,
    onProgress?: (done: number) => void,
    /** 183 — who paid; the first call writes it, the later ones keep it */
    paidSource?: PaidSource | null,
  ) => Promise<{ done: number; failed: boolean }>
  /**
   * 134 — goods carried over from another program (FINA): `import_opening_stock`
   * 500 lines a call. A barcode finds the product or creates it; a given
   * quantity BECOMES the active branch's stock (set, not added — the same
   * file uploaded twice changes nothing); NO purchase and no expense — unless
   * `bookPurchase` (1.37): then ONE „საწყისი ნაშთი" purchase per branch holds
   * what the file put on the shelf, finished across uploads (`booking`).
   */
  importOpeningStock: (
    items: OpeningItem[],
    afterSalesOk: boolean,
    onProgress?: (done: number) => void,
    opts?: { bookPurchase?: boolean },
  ) => Promise<{ done: number; created: number; failed: boolean; booking?: OpeningBooking; refusal?: CodeRefusal & { index: number }
    /** 1.62 — the product each written item became, by the items' index (the fitment merge needs them) */
    ids: string[] }>
  /** Read-only guard before an opening import starts any client mutation. */
  preflightOpeningStock: () => Promise<{ ok: true } | { ok: false; sales: number }>
  /** 134 — which purchases have a line naming this product (name or barcode). The
   *  lines are no longer held in the browser; null = the search failed. */
  searchPurchaseLines: (query: string) => Promise<Set<string> | null>
  /**
   * 112 — a model's sizes in one go: every size a product of its own, named
   * „model · size · colour", with the supplier's barcode where one was typed
   * and the shop's next in-store code (29…) where not. `model_id` omitted =
   * a new model; given = more sizes for that one. Returns the rows as saved
   * (the label dialog prints them), or null when the database refused.
   */
  addModelVariants: (m: ModelVariantsInput) => Promise<Product[] | null>
  /** 112 — what a model's sizes share (name, category, price, photo),
   *  changed on every size at once. The purchase price is not here: it is
   *  each size's own blended cost. */
  updateModel: (modelId: string, patch: ModelPatch) => Promise<boolean>
  /** Every size of a model at once (2026-09-28). Sold lines keep their names. */
  deleteModel: (modelId: string) => Promise<boolean>
  /** true when every write it attempted landed (the terminal's receive log
   *  lists only what the database accepted). */
  updateProduct: (id: string, p: Partial<Product>, stockIn?: { quantity: number; unit_cost: number; paid_source?: PaidSource | null; /** 1.61 — the pack date */ expiry_date?: string | null }) => Promise<boolean>
  /** 158 — append codes to products atomically, against the CURRENT rows (the
   *  till's attach; the receive import, before any stock). Owner login only;
   *  all or nothing per call of ≤ 1000. Only the code fields are merged
   *  locally — quantity and cost stay as the branch lens has them. */
  addProductCodes: (items: { product_id: string; codes: string[] }[]) =>
    Promise<{ ok: true; products: Product[] } | { ok: false; refusal: CodeRefusal | null; offline: boolean }>
  /** 187 (1.62) — what parts fit and their OEM numbers, as additions and removals applied to the rows as the
   *  database holds them (the card's diff, the Excel merge). `FIT_CHUNK` rows a call, one after another; each call
   *  is all or nothing. Owner login only. `failedIndex` = the first row of the chunk that failed. */
  editProductFitment: (rows: FitEdit[], onProgress?: (done: number) => void) =>
    Promise<{ ok: true; done: number } | { ok: false; done: number; failedIndex: number; refusal: FitRefusal | null; offline: boolean }>
  /** ჩამოწერა — units that will never be sold leave the shelf, and their
   *  cost is booked as an operating expense (076). Returns false on refusal. */
  writeOffStock: (productId: string, quantity: number, reason: string, note?: string) => Promise<boolean>
  deleteProduct: (id: string) => Promise<void>
  addSale: (
    sale: Pick<Sale, 'total' | 'payment_method' | 'items_count' | 'is_fiscal'> &
      /** 103 — a split: what each rail took. Omit BOTH for a single-rail sale
       *  and the server keeps its old behaviour exactly. */
      { paid_cash?: number; paid_card?: number } &
      { discount?: number; customer_id?: string | null; customer_name?: string | null; paid?: number; card_provider?: CardProvider | null; client_ref?: string; override_pin?: string | null; bonus_redeemed?: number; cash_received?: number | null; change_kept?: number },
    items: Omit<SaleItem, 'id' | 'sale_id' | 'unit_cost'>[],
    /** 1.57 — a gift card (its whole code) or a deposit this sale spends; the rails then go split */
    opts?: { prepaid?: { id: string; amount: number; code?: string } | null; /** 1.58 — a line at the customer's group price: online only */ groupPriced?: boolean }
  ) => Promise<(Sale & { replayed?: boolean }) | null>
  // Held (parked) carts — persisted in Supabase so they survive refresh.
  holdCart: (cart: {
    label?: string | null
    items: HeldCartItem[]
    discount: number
    discount_type: DiscountType | null
    discount_value: number
    total: number
    items_count: number
    /** 178 (1.58): the customer comes back with the cart; a kept quote keeps its terms */
    customer_id?: string | null
    quote?: QuoteSnapshot | null
  }) => Promise<boolean>   // 181 (I6): false = not kept; the cart stays on screen
  deleteHeldCart: (id: string) => Promise<void>
  // Suppliers & purchasing
  addSupplier: (s: Pick<Supplier, 'name' | 'tin' | 'phone' | 'contact' | 'address' | 'note'>) => Promise<Supplier | null>
  updateSupplier: (id: string, s: Partial<Supplier>) => Promise<void>
  deleteSupplier: (id: string) => Promise<void>
  createPurchase: (
    purchase: { supplier_id: string | null; supplier_name: string | null; total: number; paid: number; items_count: number; note: string | null; rsge_waybill_id?: string | null;
      /** 171 — where the paid money came from, and the dialog's ref (a retry books nothing twice) */
      paid_source: PaidSource | null; client_ref: string },
    /** 1.61 (185): a line may carry its pack date (expiry_date) — read by the delivery, not stored on the line */
    items: (Omit<PurchaseItem, 'id' | 'purchase_id'> & { expiry_date?: string | null })[]
  ) => Promise<Purchase | null>
  /** 1.65 (191) — the needs and the supplier orders. Each read answers whether it landed. */
  loadDemands: () => Promise<boolean>
  /** the needs an order holds, read from the server now (an edit must not trust the device's copy — Codex #3) */
  readOrderNeeds: (orderId: string) => Promise<DemandRecord[] | null>
  closeDemands: (ids: string[]) => Promise<boolean>
  loadSupplierOrders: () => Promise<boolean>
  saveSupplierOrders: (orders: SaveOrderInput[]) => Promise<'ok' | 'need_taken' | 'error'>
  editSupplierOrder: (id: string, lines: DraftLine[], note: string | null, needIds: string[]) => Promise<boolean>
  setSupplierOrderStatus: (id: string, status: 'closed' | 'cancelled') => Promise<boolean>
  markOrderSent: (id: string) => Promise<void>
  /** 1.66 (192) — the café. Every bill change goes through a table_order_* function, which answers the whole bill. */
  loadTables: () => Promise<boolean>
  billOpen: (tableId: string, guests: number | null) => Promise<Bill | null>
  billAdd: (billId: string, productId: string, qty: number, note?: string | null) => Promise<Bill | null>
  /** qty 0 takes an unsent line off (logged); null keeps the quantity and only sets the note */
  billEditLine: (lineId: string, qty: number | null, note: string | null) => Promise<Bill | null>
  billSend: (billId: string) => Promise<{ bill: Bill; batch: Batch | null } | null>
  billCancelPortion: (lineId: string, qty: number, reason: string, pin: string | null) => Promise<{ bill: Bill; batch: Batch } | 'PIN' | null>
  billReprint: (batchId: string) => Promise<Batch | null>
  /** a stored slip by its bill and number (the „ასლი" of an older batch) */
  findBatch: (billId: string, n: number, kind: 'send' | 'cancel') => Promise<Batch | null>
  /** the tables the given sales paid (the history's line and its reprint); empty where none, or before 192 */
  tablesOfSales: (saleIds: string[]) => Promise<Map<string, { table_name: string; hall_name: string | null; guests: number | null }>>
  billMarkPrinted: (billId: string, total: number) => Promise<Bill | null>
  billGuests: (billId: string, guests: number | null) => Promise<Bill | null>
  billMove: (billId: string, toTableId: string) => Promise<Bill | null>
  billCancel: (billId: string, reason: string | null, pin: string | null) => Promise<{ bill: Bill; batch: Batch | null } | 'PIN' | null>
  /** 1.67 (193) — the kitchen marks dishes ready (or takes a tap back while not served); never moves the money revision */
  linesReady: (lineIds: string[], ready: boolean) => Promise<boolean>
  /** 1.67 — the waiter marks dishes served (or takes it back) */
  linesServed: (lineIds: string[], served: boolean) => Promise<boolean>
  billTake: (billId: string) => Promise<Bill | null>
  billCheckRequest: (billId: string, on: boolean) => Promise<Bill | null>
  /** a bill opened and left with nothing on it closes; one with any line stays */
  billCloseIfEmpty: (billId: string) => Promise<'closed' | 'kept' | 'failed'>
  /** the device's branch (activeBranchId) — one branch per board (Codex 193 #4) */
  loadWaiterReady: () => Promise<boolean>
  loadKitchenQueue: (station: 'kitchen' | 'bar' | 'all') => Promise<boolean>
  /** 1.68 — a till sends its duties too: 'bar' and 'check' (196 takes 1..3 of kitchen · bar · check) */
  kitchenPing: (stations: PingStation[], deviceId: string) => Promise<void>
  /** the stations of the device's branch with a screen seen in the last 3 minutes; null = could not tell */
  stationsOnline: () => Promise<PingStation[] | null>
  /** 196 (1.68) — the owner's service rate (0–30, one decimal); the first call makes the service product */
  tableServiceSet: (pct: number) => Promise<boolean>
  setTableFreeOnPay: (on: boolean) => Promise<boolean>
  /** the guests left: the table's paid bill stops holding it (G22) */
  tableFree: (tableId: string) => Promise<boolean>
  loadSeated: () => Promise<boolean>
  /** 1.69 (198) — the guests' requests: read; „შეკვეთის აღება" (a lost claim answers who took it); „ნახვა" freezes an
   *  order for the confirm sheet; the confirm sends exactly those lines (+ the device-computed add-ons) as their own
   *  batch; „უარყოფა" with a reason; „მივდივარ" / „ანგარიშის ბეჭდვა" closes a call / runs the bill request */
  loadGuestRequests: () => Promise<boolean>
  requestClaim: (id: string) => Promise<{ ok: true } | { ok: false; by: string | null } | null>
  requestOpen: (id: string) => Promise<GuestRequest | null>
  /** `addons` is asked for on each send: 1.70 (A11) — refused for stale add-on rules, the rules are read again
   *  (`reloadAddonRules`) and the confirm goes once more with add-ons computed from them. 1.71 (M2): a refusal carries
   *  its code (the sheet decides with `afterConfirmRefusal`); a confirm that went through is `ok` even when its bill
   *  is not taken into this device's list (another session by now: then no batch either — nothing printed for it) */
  requestConfirm: (id: string, version: number, lines: { line_id: string; qty: number }[], addons: () => { product_id: string; qty: number }[]) =>
    Promise<{ ok: true; bill: Bill | null; batch: Batch | null } | { ok: false; code: string | null }>
  /** 1.70 — the add-on rules read again (the boot's own query); false when the read failed or the session changed */
  reloadAddonRules: () => Promise<boolean>
  requestDismiss: (id: string, reason: string) => Promise<boolean>
  requestDone: (id: string) => Promise<{ check: boolean } | null>
  /** 1.69 (197) — a party sat down (a staff visit), the table freed (the party left), a reservation mark set / cleared */
  tableSeat: (tableId: string) => Promise<boolean>
  tableVisitEnd: (tableId: string) => Promise<boolean>
  tableReserve: (tableId: string, at: string | null, name: string | null) => Promise<boolean>
  /** claim a check's printing before printing it (G13–G16); `claimed` false = another device has it — print nothing */
  checkClaim: (a: { billId: string; mode: 'auto' | 'answer' | 'new' | 'copy'; requestAt: string | null; copyOf: string | null;
    snapshot: CheckSnapshot | null }) => Promise<{ claimed: boolean; print: CheckPrint; token: string | null } | null>
  /** how the printing went — only the claimant, with its token */
  checkPrintDone: (printId: string, token: string, state: 'sent' | 'failed') => Promise<boolean>
  /** the paper's code → its printing and its bill (the till's scan, G18); null = not this shop's or not found */
  checkFind: (code: string) => Promise<{ print: CheckPrint; bill: Bill } | null>
  /** each waiter's numbers for a period of a branch (G10–G12); null = refused or failed (said) */
  waiterStats: (branchId: string, from: string, to: string) => Promise<WaiterStatRow[] | null>
  /** this device's café duties, for the shop and the branch it is on now */
  setTillDuties: (d: { bar: boolean; checks: boolean }) => void
  /** the owner's layout (RLS: owner only); a table with history is retired (active false), never deleted */
  saveHall: (h: { id?: string; branch_id: string; name: string; sort?: number; active?: boolean }) => Promise<boolean>
  saveTables: (rows: { id?: string; branch_id: string; hall_id: string | null; name: string; seats: number | null; sort?: number; active?: boolean; shape?: TableShape | null }[]) => Promise<boolean>
  lastSuppliers: (productIds: string[]) => Promise<Map<string, LastSupplier>>
  /** 1.47: true only when the server booked it — the dialog says „paid" on true alone. */
  paySupplier: (supplierId: string, amount: number, note?: string | null, method?: PayMethod, cardProvider?: CardProvider | null) => Promise<boolean>
  // Customers & credit (ნისია)
  addCustomer: (c: Pick<Customer, 'name' | 'phone' | 'note'> & Partial<Pick<Customer, 'tin' | 'legal_name' | 'legal_address' | 'price_group_id'>>) => Promise<Customer | null>
  updateCustomer: (id: string, c: Partial<Customer>) => Promise<void>
  /** 085: owner-only (the RPC refuses a cashier). Signed amount; returns the new balance. */
  adjustBonus: (customerId: string, amount: number, note?: string) => Promise<number | null>
  refreshCustomers: () => Promise<void>
  /** 106 — another device changed these tables (the Realtime signal names
   *  them); re-read only those slices. See components/live-sync.tsx. */
  refreshLive: (tables: string[]) => Promise<void>
  /** 170 — the returns to suppliers, after the boot and on their live signal. */
  refreshSupplierReturns: () => Promise<void>
  /** 1.57 — money a customer leaves with us (174): online only; null when refused (the store said why). */
  refreshPrepaidMoves: () => Promise<void>
  issuePrepaid: (p: {
    kind: PrepaidKind; amount: number; method: PrepaidMethod; cardProvider: BankCode | null
    customerId?: string | null; customerName?: string | null; customerPhone?: string | null
    note?: string | null; items?: PrepaidLine[]; dueDate?: string | null; expiresAt?: string | null; code?: string | null
  }) => Promise<Prepaid | null>
  refundPrepaid: (id: string, amount: number, method: PrepaidMethod, cardProvider: BankCode | null, note: string | null) => Promise<Prepaid | null>
  listPrepaids: (query: string, openOnly: boolean) => Promise<Prepaid[] | null>
  findPrepaid: (code: string) => Promise<Prepaid | null>
  setPrepaidDetails: (id: string, d: { note: string | null; dueDate: string | null; expiresAt: string | null }) => Promise<Prepaid | null>
  prepaidHistory: (id: string) => Promise<PrepaidMove[]>
  /** 170 — owner only, online only; null when refused (the store said why). */
  returnToSupplier: (r: { supplier_id: string; settle: 'debt' | 'cash'; purchase_id: string | null; note: string | null; lines: ReturnLine[]; clientRef: string }) => Promise<SupplierReturn | null>
  /** 086 — promotions. Writes are owner-only (RLS); the till only reads. */
  refreshPromotions: () => Promise<void>
  // 178 (1.58) — price groups: members read them, the owner makes and changes them (never deletes)
  refreshPriceGroups: () => Promise<void>
  /** 181 (Codex's re-check, I4) — one customer read again when the cart picks him (his group may have changed elsewhere) */
  refreshCustomer: (id: string) => Promise<void>
  // 1.63 — the loyalty club (188–190). Each answers a refusal CODE (lib/club.ts clubErrorKey has its words).
  /** A scanned card → its customer in this shop and the club standing; offline only from the loaded customers. */
  resolveCard: (token: string) => Promise<{ customerId: string; status: ClubStatus | null } | { error: string }>
  /** The till puts a customer on the club: the code goes to the e-mail (the Worker), never to the till. */
  clubLinkStart: (input: { customerId?: string | null; name: string; phone: string; email: string }) => Promise<{ customerId: string; mailed: boolean } | { error: string }>
  /** The code the customer reads out: the e-mail confirmed, the card issued, its link mailed. */
  clubLinkConfirm: (customerId: string, code: string) => Promise<{ ok: true; mailed: boolean } | { error: string }>
  clubPhoneConfirm: (customerId: string) => Promise<{ ok: true } | { error: string }>
  clubSendCard: (customerId: string) => Promise<{ ok: true; mailed: boolean } | { error: string }>
  /** Owner: the old card dies at once, the new link goes to the confirmed e-mail. */
  clubRevoke: (customerId: string) => Promise<{ ok: true; mailed: boolean } | { error: string }>
  /** Owner: „კლუბიდან წაშლა" — the person erased, the sales kept anonymous. */
  clubAnonymize: (customerId: string) => Promise<{ ok: true } | { error: string }>
  /** Owner: a new poster code; the old poster stops at once. */
  clubRotateCode: () => Promise<string | null>
  clubStatusOf: (customerId: string) => ClubStatus | null
  /** The cached standing goes; the next pick asks the server (a customer picked again, a held cart resumed). */
  clubForget: (customerId: string) => void
  /** Owner: the monthly threshold in GEL (settings key `club.threshold`; null = nobody is a member). */
  setClubThreshold: (value: number | null) => Promise<boolean>
  addPriceGroup: (g: Pick<PriceGroup, 'name' | 'rule' | 'pct'>) => Promise<PriceGroup | null>
  updatePriceGroup: (id: string, patch: Partial<Pick<PriceGroup, 'name' | 'pct' | 'active'>>) => Promise<boolean>
  /** 180 (1.58) — a product's box: its size, its price (a one-product bundle) and its code; null clears it */
  setProductPack: (productId: string, pack: { qty: number; price: number | null; barcode: string | null } | null) => Promise<boolean>
  savePromotion: (p: {
    id?: string; name: string; kind: PromotionKind; pct: number | null; bundle_price: number | null
    starts_at: string | null; ends_at: string | null; active: boolean; items: PromotionItem[]
  }) => Promise<boolean>
  setPromotionActive: (id: string, active: boolean) => Promise<void>
  deletePromotion: (id: string) => Promise<void>
  deleteCustomer: (id: string) => Promise<boolean>
  /** 096 — the sale's invoice, if one was issued (null = none). */
  getInvoice: (saleId: string) => Promise<import('./invoice').InvoiceRow | null>
  /** 096 — issue (or return the existing) numbered invoice for a sale. */
  issueInvoice: (saleId: string, customerId: string) => Promise<import('./invoice').InvoiceRow | null>
  /** 1.47: true only when the server booked it — the dialog says „received" on true alone. */
  payCustomer: (customerId: string, amount: number, note?: string | null, method?: PayMethod, cardProvider?: CardProvider | null) => Promise<boolean>
  // Cashiers & shifts (ცვლები)
  addCashier: (c: Pick<Cashier, 'name' | 'pin' | 'branch_id' | 'max_discount_pct'>) => Promise<Cashier | null>
  updateCashier: (id: string, c: Partial<Cashier>) => Promise<boolean>
  deleteCashier: (id: string) => Promise<void>
  /** 116 — the SERVER finds the cashier by PIN (and counts wrong ones); the
   *  till never holds the PINs. */
  openShiftWithPin: (pin: string, openingCash: number) => Promise<Shift | null>
  /** 116 — the owner's view of the PINs (staff page, backup); null for
   *  anyone else or on failure. */
  cashierPins: () => Promise<Map<string, string> | null>
  /** 119 — `takeout`: what stays in the drawer and why the rest left. */
  closeShift: (
    shiftId: string,
    closingCash: number,
    takeout?: { float: number; reason: 'owner' | 'bank' | 'other' | null; note?: string | null; bank?: BankCode | null; salary?: number; overridePin?: string | null },
  ) => Promise<Shift | null>
  /** 107 — money into or out of the OPEN shift's drawer, with a reason. An
   *  `expense` withdrawal also books the expense. Online only: the server
   *  holds the shift lock that keeps it apart from a close. */
  recordCashMovement: (m: {
    direction: CashDirection
    amount: number
    reason: CashMovementReason
    note?: string | null
    expenseCategory?: DrawerExpenseCategory | null
    /** 120 — for „ბანკში შეტანა": which bank */
    bank?: BankCode | null
  }) => Promise<boolean>
  /** 107 — only while the shift is open, by the owner or whoever wrote it. */
  deleteCashMovement: (id: string) => Promise<boolean>
  // Branches (multi-branch)
  setActiveBranch: (id: string) => Promise<void>
  addBranch: (b: { name: string; address?: string | null }) => Promise<Branch | null>
  updateBranch: (id: string, b: Partial<Pick<Branch, 'name' | 'address'>>) => Promise<void>
  deleteBranch: (id: string) => Promise<void>
  createStockTransfer: (
    fromBranch: string,
    toBranch: string,
    items: { product_id: string; product_name: string; barcode: string | null; quantity: number }[],
    note?: string | null,
  ) => Promise<StockTransfer | null>
  /** Look one barcode up in the SHARED catalog (048). null when unknown. */
  lookupCatalog: (barcode: string) => Promise<CatalogProduct | null>
  /**
   * Browse the shared catalog for items this shop does NOT already stock.
   * `search` matches name or barcode; empty returns the head of the list.
   */
  browseCatalog: (search: string, limit?: number) => Promise<CatalogProduct[]>
  /** Look several barcodes up at once. Keyed by barcode; misses are absent. */
  lookupCatalogMany: (barcodes: string[]) => Promise<Map<string, CatalogProduct>>
  /**
   * Create real products from chosen catalog rows. Stock is 0 — the catalog
   * knows what a thing IS, never how many you have — so nothing is logged as a
   * purchase. Returns how many were created.
   */
  importFromCatalog: (
    rows: {
      barcode: string
      name: string
      unit: ProductUnit
      photo_url: string | null
      category_id: string
      purchase_price: number
      sale_price: number
    }[],
  ) => Promise<number>
  /**
   * Fill missing photos from the shared catalog, matched by barcode.
   * Returns how many products got one.
   */
  fillPhotosFromCatalog: () => Promise<number>
  /** Push this org's barcoded products to the shared catalog (needs consent). */
  contributeCatalog: () => Promise<number>
  /** Remove everything this org contributed to the shared catalog. */
  withdrawCatalog: () => Promise<number>
  /**
   * 182 (1.59) — the owner's one save of a scope's add-ons AND its recipe
   * („ფასში შედის"), in one server transaction (set_product_links): a cup moved
   * from „ავტომატური" to „ფასში შედის" is never in both lists or in neither.
   * False (and nothing changed here) when the server refused it.
   */
  setProductLinks: (
    scope: { productId: string } | { categoryId: string },
    addons: { addon_id: string; auto_add: boolean }[],
    ingredients: { ingredient_id: string; qty: number }[],
  ) => Promise<boolean>
  /**
   * Remember that this supplier calls this product by this code. Upserts, so
   * re-linking a code simply repoints it.
   */
  linkSupplierCode: (
    supplierTin: string,
    supplierCode: string,
    productId: string,
    /** Pieces per supplier unit (078). Omitted = leave what is stored. */
    packSize?: number,
  ) => Promise<void>
  /** 1 unit of source → N units of target, atomically, cost carried over. */
  convertProduct: (args: {
    sourceId: string
    sourceQty: number
    targetId: string
    targetQty: number
    note?: string | null
  }) => Promise<ProductConversion | null>
  /** 1.55 — one source → 1–20 parts, the cost split by sale value (break_down_product, 172). */
  breakDownProduct: (args: {
    sourceId: string
    sourceQty: number
    parts: { productId: string; qty: number }[]
    note?: string | null
    /** One per dialog session: a retry after a lost reply replays instead of moving stock twice. */
    clientRef: string
  }) => Promise<ProductBreakdown | null>
  // Operational expenses (ხარჯები)
  /** `branch_id` is the branch the CALLER is showing; omit it and the
   *  device's active branch is used. See the note in the implementation. */
  addExpense: (e: { category: ExpenseCategory; amount: number; note?: string | null; vendor_tin?: string | null; expense_date?: string; branch_id?: string | null }) => Promise<void>
  deleteExpense: (id: string) => Promise<void>
  /** 121 — add (no id) or change a monthly cost. `thisMonth`: its day has
   *  passed this month and this month is not in the books yet. An AUTO cost
   *  whose day has come books at once; those rows come back in `booked`. */
  saveRecurringExpense: (r: {
    id?: string | null
    category: ExpenseCategory
    amount: number
    day: number
    mode: RecurringMode
    note?: string | null
    vendor_tin?: string | null
    branch_id?: string | null
    active?: boolean
    thisMonth?: boolean
  }) => Promise<{ recurring: RecurringExpense; booked: Expense[] } | null>
  /** „მარტივი რეჟიმი": the owner's choice for one feature; null = back to
   *  the shop's behaviour. The discount keeps its own key (`posDiscount`). */
  setFeature: (key: FeatureKey, on: boolean | null) => Promise<boolean>
  /** 121 — the waiting month of an ASK cost: book `amount`, or skip it (null). */
  answerRecurringExpense: (id: string, amount: number | null) => Promise<boolean>
  /** 121 — what it already booked stays in the books. */
  deleteRecurringExpense: (id: string) => Promise<boolean>
  addReturn: (
    original: Sale,
    items: Omit<SaleItem, 'id' | 'sale_id' | 'unit_cost'>[],
    /** 1.47 — one per opening of the return dialog: a press repeated in it after „not saved" is the same return. */
    session?: string,
  ) => Promise<Sale | null>
  /** 128 — every sale and return that named this IMEI / serial, newest first.
   *  Null when the server could not be asked (offline, error). */
  lookupSerial: (serial: string) => Promise<SerialLookup | null>
  /** 1.36 — the sales an item could come back from (`exchange_candidates`; null = could not ask). */
  exchangeCandidates: (q: { productId?: string | null; modelId?: string | null; receipt?: string | null; before?: string | null }) => Promise<CandidatePage | null>
  /** 1.36 — one exchange, one transaction. Never throws. `definitive: false` = the reply was lost: retry with the SAME clientRef. */
  createExchange: (req: ExchangeRequest) => Promise<ExchangeOutcome>
  /** 128 — a whole category at once: warranty months (null = none) and/or
   *  „sold by IMEI" (pieces only). Undefined = leave as it is. Returns how
   *  many products changed, null on failure. */
  setCategoryUnitRules: (
    categoryIds: string[],
    patch: { warranty_months?: number | null; track_serial?: boolean },
  ) => Promise<number | null>
  /** 130 — repairs, newest first (the page reads them; they are not hydrated). */
  listRepairs: () => Promise<Repair[] | null>
  addRepair: (draft: RepairDraft) => Promise<Repair | null>
  updateRepair: (id: string, patch: Partial<Pick<Repair, 'status' | 'technician' | 'final_price' | 'note' | 'estimate' | 'pickup_sale_id' | 'customer_phone' | 'problem' | 'accessories' | 'condition'>>) => Promise<Repair | null>
  /** 130 — a repair on its way to the till to be charged; the POS takes it into the cart. */
  pendingRepairCharge: Repair | null
  /** 130 — an intake prefilled from elsewhere (the warranty card); the repairs page opens it. */
  pendingRepairIntake: Partial<RepairDraft> | null
  /** B2 — the website's orders, newest first (the page reads them; they are not hydrated). */
  listOnlineOrders: () => Promise<OnlineOrder[] | null>
  /** B2 — confirm (for a branch), ship, or cancel with a reason (152). A sale is the only way to deliver. */
  setOnlineOrderStatus: (id: string, status: 'confirmed' | 'shipped' | 'cancelled', reason?: string | null, branchId?: string | null) => Promise<boolean>
  /** 153 — the shop's website (null = it has none; only the platform admin makes one). */
  storeSite: StoreSite | null
  /** 153 — read after the boot, never failing it. */
  refreshStoreSite: () => Promise<void>
  /** 153 — the owner's content of the website (never its address or on/off). */
  updateStoreSite: (patch: StoreSitePatch) => Promise<boolean>
  /** 153 — how many products each way of listing would show (the database's rule). */
  storeListingCount: () => Promise<{ manual: number; auto: number } | null>
  /** 153 — a category (with its sub-shelves) at once; the exact number changed, null on failure. */
  setCategoryOnline: (categoryIds: string[], action: 'on_with_photo' | 'off' | 'hide' | 'unhide') => Promise<number | null>
  updateSaleFiscal: (
    saleId: string,
    fiscal: { fiscal_status: FiscalStatus; fiscal_id?: string | null; fiscal_data?: Record<string, unknown> | null }
  ) => Promise<void>
}

const DEFAULT_SETTINGS: Settings = {
  companyName: '',
  companyId: '',
  address: '',
  phone: '',
  taxStatus: 'none',
  bankDetails: '',
  ownerReport: false,
  tgMorning: true,
  tgShift: true,
  tgEvening: true,
  tgSale: false,
  tgLoss: true,
  tgWeek: true,
  tgWaybill: true,
  tgOrder: true,
  posDiscount: null,
  features: {},
  firstDayDone: false,
  shopType: '',
  floatKeep: false,
  floatAmount: 20,
  dayStartHour: 0,
  salaryKeep: false,
  salaryAmount: 0,
  scaleEnabled: false,
  scalePrefixes: SCALE_DEFAULTS.prefixes,
  scalePluDigits: SCALE_DEFAULTS.pluDigits,
  scaleValueType: SCALE_DEFAULTS.valueType,
  fiscalKasaEnabled: false,
  fiscalDevice: false,
  exchangeDays: 14,
  cameraUrlTemplate: '',
  kioskCardProvider: 'tbc',
  kioskAllowCash: false,
      shareCatalog: false,
      lastBackupAt: '',
      lastDriverName: '',
      lastDriverTin: '',
      lastDriverCar: '',
  cashierStocktake: null,
  loyaltyEnabled: false,
  loyaltyPct: 0,
  clubThreshold: null,
  clubCode: '',
  tableServicePct: 0,
  tableFreeOnPay: false,
}

export type RsgeRef = { id: string; name: string }

/** 1.36 — what the exchange dialog sends (`create_exchange`, migration 157). */
export type ExchangeRequest = {
  originalId: string
  back: { sale_item_id: string; quantity: number }[]
  next: { product_id: string; quantity: number; paired_item: string | null; promotion_id: string | null; promo_discount: number }[]
  paidCash: number
  paidCard: number
  cardProvider: CardProvider | null
  clientRef: string
  overridePin: string | null
}
export type ExchangeOutcome =
  | { ok: true; exchangeId: string; credited: number; difference: number; ret: Sale; sale: Sale; retItems: SaleItem[]; saleItems: SaleItem[] }
  | { ok: false; key: ExchangeErrorKey | 'lost'; params: Record<string, string>; definitive: boolean }

/** 100 — a saved outgoing waybill, as the shop keeps it. */
export type OutgoingWaybill = {
  id: string
  sale_id: string | null
  /** 129 — the branch transfer this internal-transport waybill documents. */
  transfer_id?: string | null
  rs_id: string
  rs_number: string | null
  waybill_type: string | null
  buyer_tin: string
  buyer_name: string
  full_amount: number
  /** 102 — see `rsStatus`: the two negatives are different things. */
  status: OutgoingStatus
  created_at: string
}

// The signed-in user's organization (tenant) + their platform role.
export type OrgPlan = 'trial' | 'pro' | 'enterprise'
export type OrgStatus = 'active' | 'suspended'
export interface CurrentOrg {
  id: string
  name: string
  plan: OrgPlan
  status: OrgStatus
  trial_ends_at: string | null
  upgrade_requested: boolean
  /** 097 — the shop's number on a transfer (MP-0007), the paid-until date, a negotiated price, an open claim */
  billing_no: number | null
  paid_until: string | null
  custom_price_month: number | null
  pay_claim_at: string | null
  pay_claim_months: number | null
  pay_claim_amount: number | null
}

// ---- Row mappers: Postgres numeric/decimal can arrive as strings, so coerce. ----
const num = (v: unknown) => (v == null ? 0 : Number(v))

function mapProduct(r: any): Product {
  return {
    id: r.id,
    name: r.name,
    barcode: r.barcode ?? null,
    // 158 — the extras; a missing column (an old answer) is none
    extra_barcodes: Array.isArray(r.extra_barcodes) ? r.extra_barcodes : [],
    // 180 (1.58) — the box: its size and its own code (only the till's scan reads the code)
    pack_qty: r.pack_qty == null ? null : num(r.pack_qty),
    pack_barcode: r.pack_barcode ?? null,
    category_id: r.category_id ?? '',
    purchase_price: num(r.purchase_price),
    sale_price: num(r.sale_price),
    wholesale_price: r.wholesale_price == null ? null : num(r.wholesale_price),
    quantity: num(r.quantity),
    unit: r.unit ?? 'piece',
    photo_url: r.photo_url ?? null,
    plu_code: r.plu_code ?? null,
    expiry_date: r.expiry_date ?? null,
    track_expiry: r.track_expiry === true,   // 1.61 (184)
    updated_at: r.updated_at ?? undefined,
    model_id: r.model_id ?? null,
    model_name: r.model_name ?? null,
    size: r.size ?? null,
    color: r.color ?? null,
    track_serial: r.track_serial === true,
    warranty_months: r.warranty_months == null ? null : num(r.warranty_months),
    is_service: r.is_service === true,   // 169
    // 196 (1.68) — the shop's service product's mark; only a marked row carries the field
    ...(r.system_kind === 'table_service' ? { system_kind: 'table_service' as const } : {}),
    // 187 (1.62) — what the part fits and its OEM numbers; an old answer (no column) is none
    fits: Array.isArray(r.fits) ? r.fits : [],
    oem_codes: Array.isArray(r.oem_codes) ? r.oem_codes : [],
    // 188 (1.63) — a club member's price; an old answer (no column) is none
    club_price: r.club_price == null ? null : num(r.club_price),
    // 153 — the website marks (149 dropped `online` here; a warm copy without
    // them is refused by warmCopy and read again)
    online: r.online === true,
    online_hidden: r.online_hidden === true,
    created_at: r.created_at,
  }
}

/** A row of list_products_changed (114) through the lens, as the boot and
 *  refreshProducts both read it: org_quantity keeps the org-wide sum. */
function mapChangedRow(r: any, lens: string | null): Product {
  const pr = mapProduct(r)
  return lens ? { ...pr, org_quantity: pr.quantity, quantity: num(r.branch_quantity) } : pr
}

/** 196 (1.68) — one empty list for „no service rows", so a list with none never looks changed */
const NO_SERVICE_ROWS: Product[] = []

/** 196 (1.68) — a server list of products into the store: the shop's service product never reaches `products` (no
 *  screen lists it), its rows are kept in `serviceRows` for the copy and the delta's count (lib/tables-store). */
function productsPatch(rows: Product[]): { products: Product[]; serviceRows: Product[]; serviceProductId: string | null } {
  const s = splitServiceProducts(rows)
  return { products: s.products, serviceRows: s.service.length > 0 ? s.service : NO_SERVICE_ROWS, serviceProductId: s.serviceId }
}

function mapSale(r: any): Sale {
  return {
    id: r.id,
    total: num(r.total),
    discount: num(r.discount),
    bonus_redeemed: num(r.bonus_redeemed),
    bonus_earned: num(r.bonus_earned),
    cash_received: r.cash_received == null ? null : num(r.cash_received),
    change_kept: num(r.change_kept),
    payment_method: r.payment_method,
    paid_cash: num(r.paid_cash),
    paid_card: num(r.paid_card),
    paid_prepaid: num(r.paid_prepaid),   // 175 — 0 from a server before 175
    customer_id: r.customer_id ?? null,
    customer_name: r.customer_name ?? null,
    paid: num(r.paid),
    shift_id: r.shift_id ?? null,
    cashier_id: r.cashier_id ?? null,
    cashier_name: r.cashier_name ?? null,
    items_count: num(r.items_count),
    created_at: r.created_at,
    type: r.type ?? 'sale',
    reversal_of: r.reversal_of ?? null,
    card_provider: r.card_provider ?? null,
    branch_id: r.branch_id ?? null,
    is_fiscal: !!r.is_fiscal,
    fiscal_status: r.fiscal_status ?? 'none',
    fiscal_id: r.fiscal_id ?? null,
    fiscal_data: r.fiscal_data ?? null,
    fiscalized_at: r.fiscalized_at ?? null,
    received_at: r.received_at ?? null,
    sync_flags: parseSyncFlags(r.sync_flags),
    exchange_id: r.exchange_id ?? null,
    exchange_bought_at: r.exchange_bought_at ?? null,
  }
}


function mapSupplier(r: any): Supplier {
  return {
    id: r.id,
    name: r.name,
    tin: r.tin ?? null,
    phone: r.phone ?? null,
    contact: r.contact ?? null,
    address: r.address ?? null,
    note: r.note ?? null,
    balance: num(r.balance),
    created_at: r.created_at,
  }
}

function mapPurchase(r: any): Purchase {
  return {
    id: r.id,
    supplier_id: r.supplier_id ?? null,
    supplier_name: r.supplier_name ?? null,
    total: num(r.total),
    paid: num(r.paid),
    items_count: num(r.items_count),
    note: r.note ?? null,
    rsge_waybill_id: r.rsge_waybill_id ?? null,
    branch_id: r.branch_id ?? null,
    paid_source: r.paid_source ?? null,          // 171
    cash_movement_id: r.cash_movement_id ?? null,
    created_at: r.created_at,
  }
}

// Log a bookkeeping-only purchase for stock added outside the supplier
// module (Excel import, manual add, quick restock) so the money spent
// acquiring it shows up in the "შესყიდვები / სრული ხარჯი" totals. Does NOT
// touch stock (the caller already set it) or supplier balances — the
// record_stock_purchase RPC only writes purchases + purchase_items.
//
// `branchId` is where the stock physically went — every caller has just
// written it to the ACTIVE branch (`writeBranchQty`), so the same id goes
// here. Without it (before 074) the row had no branch and a branch-filtered
// accounting page showed „საქონლის შესყიდვა ₾0.00" for a restock the owner
// had made a minute earlier (2026-09-12).
async function recordStockPurchase(
  items: { product_id: string | null; product_name: string; barcode: string | null; quantity: number; unit_cost: number }[],
  note: string,
  branchId: string | null,
  paidSource: PaidSource | null = null,
): Promise<Purchase | null> {
  const payload = items.filter((i) => i.quantity > 0)
  if (payload.length === 0) return null
  // 133: 30 000 lines in one call took 37 s (one INSERT and trigger a line).
  // The first 1 000 open the purchase, the rest are added to it 1 000 a call.
  // 183: who paid — each chunk sends it when it carries money; the server keeps the first one written
  const first = payload.slice(0, PURCHASE_CHUNK)
  const { data, error } = await supabase.rpc('record_stock_purchase', { p_items: first, p_note: note, p_branch_id: branchId, ...chunkSourceArg(first, paidSource) })
  if (error) { console.error('recordStockPurchase', error); return null }
  let purchase = data ? mapPurchase(data) : null
  for (let start = PURCHASE_CHUNK; purchase && start < payload.length; start += PURCHASE_CHUNK) {
    const part = payload.slice(start, start + PURCHASE_CHUNK)
    const more = await supabase.rpc('append_stock_purchase', { p_purchase_id: purchase.id, p_items: part, ...chunkSourceArg(part, paidSource) })
    if (more.error) {
      // The stock is written; the purchase holds what it was given so far.
      console.error('recordStockPurchase append', more.error)
      toast.error(t('store.x.purchasePartial', { done: start, total: payload.length }))
      break
    }
    purchase = mapPurchase(more.data)
  }
  return purchase
}

const PURCHASE_CHUNK = 1000

type StockLine = { product_id: string | null; product_name: string; barcode: string | null; quantity: number; unit_cost: number }

/**
 * A restock of an EXISTING product: the units and the money in one call, and
 * one transaction (075).
 *
 * The client sends what happened — „+10 at ₾1.00" — and the server answers
 * with what is true afterwards. It used to send the SUM it had worked out
 * from a number read when the tab was opened, so a till that sold two units
 * in between had them silently restored; the weighted cost blended against
 * that same stale quantity, and the purchase row was a second round trip that
 * could fail on its own, leaving stock nobody paid for.
 *
 * Only 'add' mode comes here. „ახალი მთლიანი ნაშთი" is an absolute statement
 * by somebody looking at the shelf and must stay absolute.
 */
async function restockProduct(
  productId: string,
  quantity: number,
  unitCost: number,
  branchId: string | null,
  note: string,
  paidSource: PaidSource | null = null,
  /** 1.61 (185) — the pack's best-before; null = not given (an undated batch) */
  expiryDate: string | null = null,
): Promise<{ purchase: Purchase | null; purchasePrice: number; branchQty: number; orgQty: number } | null> {
  const src = stockSourceArg(quantity * unitCost, paidSource)   // 183
  const { data, error } = await supabase.rpc('restock_product_dated', {
    p_expiry_date: expiryDate,
    p_product_id: productId,
    p_quantity: quantity,
    p_unit_cost: unitCost,
    p_branch_id: branchId,
    p_note: note,
    ...(src ? { p_paid_source: src } : {}),
  })
  if (error || !data) { console.error('restockProduct', error); return null }
  const d = data as Record<string, unknown>
  return {
    purchase: d.purchase ? mapPurchase(d.purchase) : null,
    purchasePrice: num(d.purchase_price),
    branchQty: num(d.branch_quantity),
    orgQty: num(d.org_quantity),
  }
}

function mapPurchaseItem(r: any): PurchaseItem {
  return {
    id: r.id,
    purchase_id: r.purchase_id,
    product_id: r.product_id ?? null,
    product_name: r.product_name,
    barcode: r.barcode ?? null,
    quantity: num(r.quantity),
    unit_cost: num(r.unit_cost),
    total_cost: num(r.total_cost),
  }
}

/**
 * ONE branch's stock, whole, or nothing.
 *
 * A failed read returns NOTHING, never a partial map: the caller lenses
 * `products[].quantity` through this result, and half a map rewrites every
 * unseen product to zero — the shop reads as sold out and the till refuses to
 * sell, without a single error on screen. The paged walk this replaced had two
 * more roads to the same place (an unordered `range()`, a short page taken for
 * the last one) and cost a 30 000-product branch 60–80 s at every boot: 500
 * rows a request, one after another, RLS checking the org on every row up to
 * the offset (the 6-store scale test, 2026-09-30).
 *
 * 126 `branch_stock_of` answers the whole branch at once, {product_id:
 * quantity}, the org read once — 30 000 rows in ~0.15 s. NULL means the
 * caller has no shop; that is treated like an error.
 */
async function loadBranchStock(branchId: string): Promise<Map<string, number> | null> {
  const { data, error } = await supabase.rpc('branch_stock_of', { p_branch: branchId })
  if (error || !data || typeof data !== 'object') {
    if (error) console.error('branch_stock_of error', error)
    return null
  }
  const byId = new Map<string, number>()
  for (const [id, q] of Object.entries(data as Record<string, unknown>)) byId.set(id, num(q))
  return byId
}

type ProductLoad = { error: unknown; rows: Product[]; watermark: string | null; lens: string | null }

/**
 * The whole catalogue through the lens — the boot's path when the device's
 * copy cannot be used (1.25), and the same read hydrate always made: the 126
 * pages and the branch's stock together.
 *
 * ONLY the active branch. A previous version loaded every branch's stock here
 * so the accounting page could value another branch's shelves — which put
 * thousands of rows and N blocking round-trips into the startup path of a
 * till that only ever sells, and cached a copy that went stale the moment a
 * transfer moved anything. That page now asks the database for one number per
 * branch instead (`branch_stock_value`, migration 062). A failed stock read
 * leaves the org-wide quantities alone rather than lensing through a partial
 * map — a half-loaded lens reads as „everything is sold out" and the till
 * stops selling — and then the list carries no lens.
 */
async function readAllProducts(lens: string | null): Promise<ProductLoad> {
  const [prods, byId] = await Promise.all([
    allRows<any>((a, b) => supabase.rpc('list_products_page', { p_offset: a, p_limit: b - a + 1 })),
    lens ? loadBranchStock(lens) : Promise.resolve(null),
  ])
  if (prods.error) return { error: prods.error, rows: [], watermark: null, lens: null }
  let rows: Product[] = (prods.data ?? []).map(mapProduct)
  const watermark = newestStamp(rows)
  // org_quantity keeps what the DB holds (the org-wide sum) before the branch
  // lens replaces `quantity` — the „all branches" cards read it.
  if (lens && byId) rows = rows.map((p) => ({ ...p, org_quantity: p.quantity, quantity: byId.get(p.id) ?? 0 }))
  return { error: null, rows, watermark, lens: lens && byId ? lens : null }
}

// 170 — goods back to a supplier (written only by return_to_supplier)
function mapSupplierReturn(r: any): SupplierReturn {
  return {
    id: r.id, supplier_id: r.supplier_id, branch_id: r.branch_id ?? null, shift_id: r.shift_id ?? null,
    purchase_id: r.purchase_id ?? null, total: num(r.total), settle: r.settle === 'cash' ? 'cash' : 'debt',
    cash_movement_id: r.cash_movement_id ?? null, cost_diff: num(r.cost_diff), note: r.note ?? null,
    created_by: r.created_by ?? null, created_at: r.created_at,
  }
}

function mapSupplierReturnItem(r: any): SupplierReturnItem {
  return {
    id: r.id, return_id: r.return_id, product_id: r.product_id ?? null, purchase_item_id: r.purchase_item_id ?? null,
    product_name: r.product_name ?? '', barcode: r.barcode ?? null, quantity: num(r.quantity),
    unit_credit: num(r.unit_credit), total_credit: num(r.total_credit), unit_cost: num(r.unit_cost), cost_diff: num(r.cost_diff),
  }
}

function mapSupplierPayment(r: any): SupplierPayment {
  return {
    id: r.id,
    supplier_id: r.supplier_id,
    amount: num(r.amount),
    note: r.note ?? null,
    method: r.method ?? 'cash',
    card_provider: r.card_provider ?? null,
    shift_id: r.shift_id ?? null,
    branch_id: r.branch_id ?? null,
    created_at: r.created_at,
  }
}

function mapCustomer(r: any): Customer {
  return {
    id: r.id,
    name: r.name,
    phone: r.phone ?? null,
    note: r.note ?? null,
    balance: num(r.balance),
    bonus_balance: num(r.bonus_balance),
    tin: r.tin ?? null,
    legal_name: r.legal_name ?? null,
    legal_address: r.legal_address ?? null,
    price_group_id: r.price_group_id ?? null,   // 178 (1.58)
    // 188 (1.63) — the club identity; an older answer (no columns) is none
    email: r.email ?? null,
    email_verified_at: r.email_verified_at ?? null,
    card_token: r.card_token ?? null,
    phone_norm: r.phone_norm ?? null,
    phone_confirmed_at: r.phone_confirmed_at ?? null,
    joined_via: r.joined_via ?? null,
    anonymized_at: r.anonymized_at ?? null,
    created_at: r.created_at,
  }
}

function mapCustomerPayment(r: any): CustomerPayment {
  return {
    id: r.id,
    customer_id: r.customer_id,
    amount: num(r.amount),
    note: r.note ?? null,
    method: r.method ?? 'cash',
    card_provider: r.card_provider ?? null,
    shift_id: r.shift_id ?? null,
    branch_id: r.branch_id ?? null,
    created_at: r.created_at,
  }
}

function mapExpense(r: any): Expense {
  return {
    id: r.id,
    category: r.category ?? 'other',
    amount: num(r.amount),
    note: r.note ?? null,
    vendor_tin: r.vendor_tin ?? null,
    expense_date: r.expense_date,
    created_at: r.created_at,
    branch_id: r.branch_id ?? null,
    product_id: r.product_id ?? null,
    quantity: r.quantity == null ? null : num(r.quantity),
    cash_movement_id: r.cash_movement_id ?? null,
    recurring_id: r.recurring_id ?? null,
    shift_id: r.shift_id ?? null,
  }
}

function mapRecurringExpense(r: any): RecurringExpense {
  return {
    id: r.id,
    category: r.category ?? 'other',
    amount: num(r.amount),
    note: r.note ?? null,
    vendor_tin: r.vendor_tin ?? null,
    branch_id: r.branch_id ?? null,
    day_of_month: Number(r.day_of_month),
    mode: r.mode === 'ask' ? 'ask' : 'auto',
    active: r.active !== false,
    next_due: String(r.next_due).slice(0, 10),
    created_at: r.created_at,
  }
}

const numOrNull = (v: unknown) => (v == null ? null : num(v))

function mapCartRemoval(r: any): CartRemoval {
  return {
    id: r.id,
    group_id: r.group_id,
    kind: r.kind,
    product_id: r.product_id ?? null,
    product_name: r.product_name ?? '',
    quantity: num(r.quantity),
    unit_price: num(r.unit_price),
    amount: num(r.amount),
    cart_total: numOrNull(r.cart_total),
    after_payment: r.after_payment === true,
    shift_id: r.shift_id ?? null,
    cashier_id: r.cashier_id ?? null,
    cashier_name: r.cashier_name ?? null,
    branch_id: r.branch_id ?? null,
    happened_at: r.happened_at,
    created_at: r.created_at,
  }
}

function mapPriceChange(r: any): PriceChange {
  return {
    id: r.id,
    product_id: r.product_id ?? null,
    product_name: r.product_name ?? '',
    old_sale_price: numOrNull(r.old_sale_price),
    new_sale_price: numOrNull(r.new_sale_price),
    old_wholesale_price: numOrNull(r.old_wholesale_price),
    new_wholesale_price: numOrNull(r.new_wholesale_price),
    changed_by: r.changed_by ?? null,
    changed_by_role: r.changed_by_role ?? null,
    shift_id: r.shift_id ?? null,
    cashier_name: r.cashier_name ?? null,
    txid: Number(r.txid),
    created_at: r.created_at,
  }
}

/** 122 — the two loss-control logs, over the sales window. The till's own
 *  RLS returns nothing; a cashier's device does not ask. */
async function readLossLogs(since: string): Promise<{ removals: CartRemoval[] | null; prices: PriceChange[] | null }> {
  const [rm, pc] = await Promise.all([
    allRows((a, b) => supabase.from('cart_removals').select('*').gte('happened_at', since).order('happened_at', { ascending: false }).order('id').range(a, b)),
    allRows((a, b) => supabase.from('price_changes').select('*').gte('created_at', since).order('created_at', { ascending: false }).order('id').range(a, b)),
  ])
  if (rm.error) console.error('cart_removals load error', rm.error)
  if (pc.error) console.error('price_changes load error', pc.error)
  return {
    removals: rm.error ? null : (rm.data ?? []).map(mapCartRemoval),
    prices: pc.error ? null : (pc.data ?? []).map(mapPriceChange),
  }
}

/**
 * 122 — a product write carries the device's open shift, so the database's
 * price log can say whose shift it was: a shop often shares the owner's login
 * at the till, and „the owner changed it" would then say nothing.
 */
function withShift<Q extends { setHeader(name: string, value: string): Q }>(q: Q): Q {
  const shift = selectOpenShift(useStore.getState())
  return shift ? q.setHeader('x-shift-id', shift.id) : q
}

/** Newest expense day first — booked months can land behind today's rows. */
const byExpenseDateDesc = (a: Expense, b: Expense) => b.expense_date.localeCompare(a.expense_date)

function mapCashMovement(r: any): CashMovement {
  return {
    id: r.id,
    shift_id: r.shift_id ?? null,
    branch_id: r.branch_id ?? null,
    direction: r.direction === 'in' ? 'in' : 'out',
    amount: num(r.amount),
    reason: r.reason ?? 'other',
    note: r.note ?? null,
    bank: r.bank ?? null,
    doc: r.doc === 'supplier_return' || r.doc === 'purchase' ? r.doc : null,   // 170
    created_by: r.created_by ?? null,
    created_at: r.created_at,
  }
}

function mapCashier(r: any): Cashier {
  return {
    id: r.id,
    name: r.name,
    active: r.active ?? true,
    branch_id: r.branch_id ?? null,
    max_discount_pct: r.max_discount_pct == null ? null : Number(r.max_discount_pct),
    created_at: r.created_at,
    user_id: r.user_id ?? null,
    kind: r.kind === 'waiter' ? 'waiter' : 'cashier',
  }
}


function mapBranch(r: any): Branch {
  return {
    id: r.id,
    name: r.name,
    address: r.address ?? null,
    is_default: !!r.is_default,
    active: r.active ?? true,
    created_at: r.created_at,
  }
}

function mapStockTransfer(r: any): StockTransfer {
  return {
    id: r.id,
    from_branch: r.from_branch,
    to_branch: r.to_branch,
    items_count: num(r.items_count),
    note: r.note ?? null,
    created_by: r.created_by ?? null,
    created_at: r.created_at,
  }
}

function mapStockTransferItem(r: any): StockTransferItem {
  return {
    id: r.id,
    transfer_id: r.transfer_id,
    product_id: r.product_id ?? null,
    product_name: r.product_name,
    barcode: r.barcode ?? null,
    quantity: num(r.quantity),
    unit_cost: r.unit_cost == null ? null : num(r.unit_cost),
  }
}

function mapProductAddon(r: any): ProductAddon {
  return {
    id: r.id,
    product_id: r.product_id ?? null,
    category_id: r.category_id ?? null,
    addon_id: r.addon_id,
    sort_order: r.sort_order ?? 0,
    auto_add: r.auto_add === true,
  }
}

function mapProductIngredient(r: any): ProductIngredient {
  return {
    id: r.id,
    product_id: r.product_id ?? null,
    category_id: r.category_id ?? null,
    ingredient_id: r.ingredient_id,
    qty: num(r.qty),
    sort_order: r.sort_order ?? 0,
  }
}

function mapSupplierCode(r: any): SupplierProductCode {
  return {
    id: r.id,
    supplier_tin: r.supplier_tin,
    supplier_code: r.supplier_code,
    product_id: r.product_id,
    pack_size: r.pack_size == null ? 1 : num(r.pack_size),
  }
}

function mapBreakdownPart(r: any): BreakdownPart {
  return {
    id: r.id,
    position: num(r.position),
    product_id: r.product_id ?? null,
    product_name: r.product_name,
    unit: r.unit,
    quantity: num(r.quantity),
    sale_price: num(r.sale_price),
    sale_value: num(r.sale_value),
    cost: num(r.cost),
    unit_cost: num(r.unit_cost),
    cost_before: num(r.cost_before),
    cost_after: num(r.cost_after),
  }
}

/** A header row, with `product_breakdown_parts` embedded (the hydrate) or `parts` beside it (the RPC's reply). */
function mapProductBreakdown(r: any, parts?: any[]): ProductBreakdown {
  return {
    id: r.id,
    branch_id: r.branch_id,
    source_product_id: r.source_product_id ?? null,
    source_name: r.source_name,
    source_unit: r.source_unit,
    source_qty: num(r.source_qty),
    source_unit_cost: num(r.source_unit_cost),
    total_cost: num(r.total_cost),
    basis: r.basis === 'quantity' ? 'quantity' : 'value',
    parts_qty: r.parts_qty == null ? null : num(r.parts_qty),
    note: r.note ?? null,
    created_by: r.created_by ?? null,
    created_at: r.created_at,
    parts: ((parts ?? r.product_breakdown_parts ?? []) as any[]).map(mapBreakdownPart).sort((a, b) => a.position - b.position),
  }
}

function mapProductConversion(r: any): ProductConversion {
  return {
    id: r.id,
    branch_id: r.branch_id,
    source_product_id: r.source_product_id ?? null,
    source_name: r.source_name,
    source_qty: num(r.source_qty),
    target_product_id: r.target_product_id ?? null,
    target_name: r.target_name,
    target_qty: num(r.target_qty),
    total_cost: num(r.total_cost),
    unit_cost: num(r.unit_cost),
    note: r.note ?? null,
    created_by: r.created_by ?? null,
    created_at: r.created_at,
  }
}

// ---- Multi-branch helpers ----
// The active branch is a per-DEVICE choice (the till by the door stays on
// its branch even if the owner logs in elsewhere), so it lives in
// localStorage, not in org settings.
const ACTIVE_BRANCH_KEY = 'pos-active-branch'
const loadActiveBranch = (): string | null => {
  try { return localStorage.getItem(ACTIVE_BRANCH_KEY) } catch { return null }
}
const saveActiveBranch = (id: string | null) => {
  try {
    if (id) localStorage.setItem(ACTIVE_BRANCH_KEY, id)
    else localStorage.removeItem(ACTIVE_BRANCH_KEY)
  } catch { /* SSR / private mode */ }
}

/**
 * The branch lens lives in `lib/branch-scope.ts` — pure, so it is pinned by
 * `lib/branch-scope.test.ts` without booting the store or Supabase.
 * Re-exported here because every caller already imports from the store.
 */
export { selectOpenShift, selectLastClosedShift }
export { matchesActiveBranch, openShiftElsewhere, shiftAge, staleOpenShifts, openedAgo } from './branch-scope'

/** What the screen lock opens with until the owner chooses a PIN. Everybody
 *  can guess it, which is why `lock` asks for a real one first. */
export const DEFAULT_LOCK_PIN = '1234'
export type { ShiftLens } from './branch-scope'

/**
 * Add-on resolution lives in `lib/addon-suggest.ts` — it is pure, and the
 * till's suggestion maths is tested against it without booting Supabase.
 * Re-exported here because every caller already imports it from the store.
 */
export { resolveAddons, offeredAddons } from './addon-suggest'

function mapSaleItem(r: any): SaleItem {
  return {
    id: r.id,
    sale_id: r.sale_id,
    product_id: r.product_id,
    product_name: r.product_name,
    barcode: r.barcode ?? null,
    quantity: num(r.quantity),
    unit_price: num(r.unit_price),
    total_price: num(r.total_price),
    unit_cost: num(r.unit_cost),
    promotion_id: r.promotion_id ?? null,
    promo_discount: num(r.promo_discount),
    serials: Array.isArray(r.serials) && r.serials.length > 0 ? (r.serials as string[]) : null,
    warranty_until: r.warranty_until ?? null,
    ingredient_of: r.ingredient_of ?? null,   // 182 — a ₾0 line the drink took
  }
}

/** Empty category_id ('' from the UI) is not a valid UUID — send NULL instead. */
function sanitizeProductWrite<T extends { category_id?: string | null }>(p: T): T {
  if (!('category_id' in p)) return p
  return { ...p, category_id: p.category_id ? p.category_id : null }
}

// Absolute quantity write in multi-branch mode. products.quantity is the
// org-wide SUM there, so a form's "set qty to N" must land on the ACTIVE
// branch's product_stock row (trigger A refolds the sum); writing
// products.quantity directly would mirror a wrong DELTA into the default
// branch. Single-branch orgs keep the legacy products.quantity path.
async function writeBranchQty(productId: string, qty: number, branchId: string): Promise<boolean> {
  const { error } = await supabase
    .from('product_stock')
    .upsert({ branch_id: branchId, product_id: productId, quantity: qty }, { onConflict: 'branch_id,product_id' })
  if (error) { console.error('writeBranchQty', error); return false }
  return true
}

/**
 * 107 — the drawer functions' refusals, in words a cashier can act on. Kept
 * out of SERVER_REFUSALS (lib/server-refusals) on purpose: AMOUNT_INVALID is raised by other
 * functions too, and their screens say it their own way.
 */
const CASH_MOVEMENT_REFUSALS: [string, Key][] = [
  ['AMOUNT_INVALID', 'store.drawer.amount'],
  ['NOTE_REQUIRED', 'store.drawer.note'],
  ['NOTE_TOO_LONG', 'store.noteTooLong'],
  ['REASON_INVALID', 'store.drawer.reason'],
  ['CATEGORY_INVALID', 'store.drawer.category'],
  ['BANK_INVALID', 'store.bankInvalid'],
  ['SHIFT_REQUIRED', 'store.shiftRequired'],
  ['SHIFT_NOT_FOUND', 'store.drawer.shiftNotFound'],
  ['SHIFT_ALREADY_CLOSED', 'store.drawer.shiftClosed'],
  ['MOVEMENT_NOT_FOUND', 'store.drawer.gone'],
  ['NOT_YOURS', 'store.drawer.notYours'],
]

/** 119 — close_shift's own refusals about what stays in the drawer. */
const CLOSE_REFUSALS: [string, Key][] = [
  ['FLOAT_INVALID', 'close.err.floatOver'],
  ['REASON_INVALID', 'store.closeReason'],
  ['NOTE_REQUIRED', 'close.err.noteRequired'],
  ['NOTE_TOO_LONG', 'store.noteTooLong'],
  ['BANK_INVALID', 'store.bankInvalid'],
  ['SALARY_INVALID', 'close.err.salaryInvalid'],   // 137
]

/** 121 — the monthly costs' refusals an owner can reach, in words. */
const RECURRING_REFUSALS: [string, Key][] = [
  ['AMOUNT_INVALID', 'store.rec.amount'],
  ['CATEGORY_INVALID', 'store.rec.category'],
  ['DAY_INVALID', 'store.rec.day'],
  ['MODE_INVALID', 'store.rec.mode'],
  ['NOTE_TOO_LONG', 'store.noteTooLong'],
  ['TIN_INVALID', 'store.rec.tin'],
  ['BRANCH_INVALID', 'store.rec.branch'],
  ['NOT_FOUND', 'store.rec.notFound'],
  ['NOT_DUE', 'store.rec.notDue'],
  ['NOT_OWNER', 'store.rec.notOwner'],
]

async function refreshRecurring(): Promise<void> {
  const gen = bootGen   // 1.47: lands only in the shop it was read for (refreshLive)
  const { data, error } = await allRows((a, b) =>
    supabase.from('recurring_expenses').select('*').order('created_at', { ascending: true }).order('id').range(a, b))
  if (!error && data && gen === bootGen) useStore.setState({ recurringExpenses: data.map(mapRecurringExpense) })
}

/** A monthly-cost refusal: re-read the list when ours was stale, say it in words. */
async function recurringRefused(error: unknown, fallback: Key): Promise<void> {
  const m = (error as { message?: string } | null)?.message ?? ''
  if (m.includes('NOT_FOUND') || m.includes('NOT_DUE')) await refreshRecurring()
  const said = RECURRING_REFUSALS.find(([code]) => m.includes(code))?.[1]
  if (said) {
    console.error(translate('ka', fallback), error)
    toast.error(t(said))
    return
  }
  failedT(error, fallback)
}

/** A drawer refusal: re-read the shifts when ours was stale, say it in words. */
async function drawerRefused(error: unknown, fallback: Key): Promise<void> {
  const m = (error as { message?: string } | null)?.message ?? ''
  if (m.includes('SHIFT_ALREADY_CLOSED') || m.includes('SHIFT_NOT_FOUND')) await refreshShifts()
  const said = CASH_MOVEMENT_REFUSALS.find(([code]) => m.includes(code))?.[1]
  if (said) {
    console.error(translate('ka', fallback), error)
    toast.error(t(said))
    return
  }
  failedT(error, fallback)
}

/** 1.65 (191) — folds an order RPC's answer ({orders, items}) into the slice, when this device holds it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// ── 1.66 (192) — the café's bills ─────────────────────────────────────────────
type TableOutcome<T> = { ok: true; data: T } | { ok: false; doubt: boolean; code: string | null }

type TableRpcOpts = { quiet?: boolean; quietCodes?: readonly string[] }

/** One table function call. A lost connection is tried once more with the SAME arguments (an op id makes that
 *  safe); a refusal is said in the till's words; a refusal that means „your copy is old" re-reads the bills. */
async function tableRpc<T>(fn: string, args: Record<string, unknown>, opts?: TableRpcOpts): Promise<TableOutcome<T>> {
  let r = await supabase.rpc(fn, args)
  if (r.error && isNetworkError(r.error)) {
    await new Promise((res) => setTimeout(res, 800))
    r = await supabase.rpc(fn, args)
  }
  if (!r.error) return { ok: true, data: r.data as T }
  const m = (r.error as { message?: string }).message ?? ''
  const code = tableErrorCode(m)
  // 1.68 — a call nobody pressed (the automatic check printer) says nothing; the reload below still runs.
  // 1.70 — nor a refusal the caller handles itself (`quietCodes`: the confirm's add-on retry)
  if (opts?.quiet || (code !== null && opts?.quietCodes?.includes(code))) console.error(fn, r.error)
  else if (isNetworkError(r.error)) toast.error(t('tables.offline'))
  else if (m.includes('PIN_LOCKED')) toast.error(t('store.ownerPinLocked'), { duration: 8000 })
  else {
    const k = tableErrorKey(m)
    if (k) toast.error(t(k), { duration: 6000 })
    else failedT(r.error, 'tables.saveFailed')
  }
  if (tablesNeedReload(m)) void useStore.getState().loadTables()
  return { ok: false, doubt: isNetworkError(r.error), code }
}

/** A call carrying an op: the same thing pressed again after a lost answer sends the same op (pendingOp). */
async function tableOpRpc<T>(key: string, fn: string, args: Record<string, unknown>, opts?: TableRpcOpts): Promise<TableOutcome<T>> {
  const op = pendingOp(tableOps, key, Date.now(), () => crypto.randomUUID())
  const r = await tableRpc<T>(fn, { ...args, p_op: op }, opts)
  settleOp(tableOps, key, !r.ok && r.doubt ? 'doubt' : 'done', op, Date.now())
  return r
}

/** A bill the server answered, in place of the device's copy (never an older one); a closed bill leaves the list.
 *  `startSeq` = tableSeq when the call began: a bill that left the list after that is not brought back (Codex final #5). */
function applyBill(raw: unknown, gen: number, startSeq: number): Bill | null {
  if (gen !== bootGen || raw == null) return null
  const b = mapBill(raw)
  if (!acceptBillReply(tableGone, b.id, startSeq)) return null
  if (b.status !== 'open') tableGone.set(b.id, ++tableSeq)
  tableReads.next()
  useStore.setState((state) => {
    if (!state.bills) return {}
    const next = { ...state.bills }
    if (b.status !== 'open') delete next[b.id]
    else next[b.id] = applyBillReply(next[b.id], b)
    return { bills: next }
  })
  return b
}

/** The owner's layout saved: a duplicate name and the guard's refusals in the till's words. */
function layoutRefused(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false
  if (error.code === '23505') toast.error(t('tables.settings.nameTaken'))
  else {
    const k = tableErrorKey(error.message ?? '')
    if (k) toast.error(t(k))
    else failedT(error, 'tables.saveFailed')
  }
  return true
}

function mergeOrders(data: { orders?: any[]; items?: any[] | null } | null, gen: number) {
  // Codex's final review #1: an answer for the shop this device has since left never lands in the next one
  if (gen !== bootGen) return
  const held = useStore.getState().supplierOrders
  if (!held || !data) return
  orderReads.next()   // a read already in flight predates this change
  const orders = (data.orders ?? []).map(mapSupplierOrder)
  const ids = new Set(orders.map((o) => o.id))
  const items = data.items == null ? null : data.items.map(mapSupplierOrderItem)
  useStore.setState({ supplierOrders: {
    orders: mergeById(held.orders, orders),
    items: items === null ? held.items : [...held.items.filter((i) => !ids.has(i.order_id)), ...items],
  } })
}

/**
 * `failed` for a message the cashier reads in the till's language. The
 * console line and the error report keep the Georgian — the morning digest
 * is read in Georgian, and one refusal must not count as two languages.
 */
function failedT(error: unknown, key: Key): boolean {
  return failed(error, t(key), translate('ka', key))
}

function failed(error: unknown, msg: string, reportAs: string = msg): boolean {
  if (error) {
    console.error(reportAs, error)
    // P0010 (migration 055) is raised by assert_shift_open with a message
    // written FOR the cashier — showing our generic one instead would hide the
    // only instruction that fixes it ("reload and open a shift").
    const e = error as { code?: string; message?: string }
    const knownKey = serverRefusalKey(e?.message)
    const known = knownKey ? t(knownKey) : undefined
    // 094 — an accountant pressing something that writes: the database said
    // no (READ_ONLY_ROLE from an RPC, 42501 from a policy). Say why, once.
    if (useStore.getState().userRole === 'accountant' &&
        (e?.message?.includes('READ_ONLY_ROLE') || e?.code === '42501' || /row-level security/i.test(e?.message ?? ''))) {
      toast.error(t('store.readOnly'))
      return true
    }
    // 098 — a support session hitting a money table's policy (42501, not the RPC's word)
    if (!known && useStore.getState().supportSession &&
        (e?.code === '42501' || /row-level security/i.test(e?.message ?? ''))) {
      toast.error(t('store.supportOnlyOwner'))
      return true
    }
    toast.error(e?.code === 'P0010' && e.message ? e.message : known ?? msg)
    // 111 — a refusal the app has words for is the rule working; one it has
    // no words for is a bug we want to hear about before the shop calls.
    if (!known && e?.code !== 'P0010') reportError('rpc', error, `${reportAs}${e?.code ? ` | code=${e.code}` : ''}`)
    return true
  }
  return false
}

/**
 * Raised when a payment names a shift the server does not consider open —
 * almost always this tab's cached `shifts` being stale after another device
 * closed it. See migration 055.
 */
function isShiftClosed(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'P0010'
}

/**
 * Re-read the shift list after the server has told us ours is out of date.
 * Without this the cashier reads "reload the page", presses the button again,
 * and gets the same refusal from the same stale row — so the message would be
 * an instruction we could have carried out ourselves.
 * Best-effort: a failure here leaves the cache exactly as it already was.
 */
async function refreshShifts(): Promise<void> {
  const gen = bootGen   // 1.47: lands only in the shop it was read for (refreshLive)
  const { data, error } = await allRows((a, b) => supabase.from('shifts').select('*').order('opened_at', { ascending: false }).order('id').range(a, b))
  if (error || !data || gen !== bootGen) return
  useStore.setState({ shifts: data.map(mapShift) })
}

/** Postgres unique_violation on one of migration 050's category indexes. */
function isDuplicateName(error: unknown): boolean {
  const e = error as any
  if (!e) return false
  return e.code === '23505' && String(e.message ?? '').includes('uq_categories')
}

/** unique_violation on a code: 054's primary-barcode index or 158's registry of every code. */
function isDuplicateBarcode(error: unknown): boolean {
  return isDuplicateCode(error)
}

// The product-limit trigger raises PRODUCT_LIMIT_REACHED on trial plans.
function isProductLimit(error: unknown): boolean {
  return String((error as any)?.message ?? '').includes('PRODUCT_LIMIT_REACHED')
}

/**
 * 114 — the server's time of the last products read on this device. In
 * memory only: a reload reads everything once (hydrate), which is also what
 * resets it. Null = the next refresh reads everything.
 */
let productsWatermark: string | null = null
// 1.25 — saved WITH the list (lib/warm-products.ts): the branch whose stock
// `products[].quantity` holds (null = the org-wide figure, also when the
// branch's stock could not be read), and whether the list carries a change
// the server never saw (an offline sale) — then the next online boot reads
// everything.
let productsLens: string | null = null
let productsLocal = false

/** R08 — the last sale saved neither on the server nor in the queue (lib/offline.ts). */
let unsavedAttempt: UnsavedAttempt | null = null
/** 1.57 — an issue or a refund told „not saved": the same press keeps its ref (Codex #1). */
let unsavedPrepaid: UnsavedAttempt | null = null
/** 1.47 — the last return whose answer never came (lib/offline.ts, returnAttemptKey). */
let unsavedReturn: UnsavedAttempt | null = null

/**
 * localStorage that never throws. zustand's persist writes on EVERY set(), and
 * a write that hits the ~5 MB quota throws QuotaExceededError out of whatever
 * set() triggered it — any button in the app. The products moved to
 * IndexedDB (lib/product-cache.ts) so the quota is far away now; this keeps a
 * full or blocked storage (private mode) from ever breaking a click again. The
 * first failure of a page load is reported (111) so it reaches the digest.
 */
let storageFailReported = false
const safeLocalStorage: StateStorage = {
  getItem: (name) => { try { return localStorage.getItem(name) } catch { return null } },
  setItem: (name, value) => {
    try { localStorage.setItem(name, value) } catch (e) {
      if (!storageFailReported) {
        storageFailReported = true
        console.error('persist: localStorage write failed', e)
        reportError('unhandled', e, `persist ${name}: ${value.length} chars`)
      }
    }
  },
  removeItem: (name) => { try { localStorage.removeItem(name) } catch { /* nothing to remove */ } },
}

/**
 * Many new products, their stock placed on the active branch and one
 * bookkeeping purchase for the batch. The Excel importer's path, shared with
 * a model's sizes (112) so the two cannot drift. No toast: each caller words
 * its own refusal.
 *
 * ⚠️ 133 (measured 2026-10-02, 30 000 rows): ONE insert of the whole file ran
 * past the 8 s a request may take — 1.2 ms a row in triggers — and the
 * database wrote nothing. Rows go 500 at a time now (~0.6 s each). A refusal
 * mid-way keeps the chunks before it: they are real products, they get their
 * purchase, and `created` says how many so the caller can say so.
 */
const PRODUCT_CHUNK = 500

/**
 * 133 — while an import writes, the background product refreshes wait.
 *
 * Every chunk the import commits sends the shop's live signal (106), and the
 * open page answered each one with a refresh: the import's own new rows are
 * „changed since the watermark", more than MAX_CHANGED of them, so every
 * refresh became the FULL catalogue read — 30 pages and the branch's whole
 * stock, every 1.2 s. Measured on the scratch org: the database committed a
 * chunk in ~1–2 s and the page then sat 10–20 s re-reading what it had just
 * written. Quiet refreshes are skipped while `bulkWrites` > 0 and ONE runs
 * when the last bulk write ends, so another device's sale is still caught.
 */
let bulkWrites = 0
let productsBehind = false
/** 1.34 (Codex): bumped every time a read of the server REPLACES the products,
 *  purchases or suppliers on screen. A write that saw it move while it was in
 *  flight must not add its own delta on top: that state may already hold it. */
let liveApplied = 0
/** Lines written since the first of the running bulk writes began. */
let bulkLines = 0
/**
 * 135 — after this many lines the planner's statistics are refreshed before
 * anything reads the catalogue again: right after 30 000 new products the
 * next read timed out three times (the owner switching to the next branch
 * saw „მონაცემების ჩატვირთვა ვერ მოხერხდა") until autovacuum analysed the
 * table a minute later.
 */
const BIG_WRITE = 1000
async function endBulkWrite(get: () => StoreState) {
  bulkWrites = Math.max(0, bulkWrites - 1)
  if (bulkWrites > 0) return
  const big = bulkLines >= BIG_WRITE
  bulkLines = 0
  if (big) {
    const { error } = await supabase.rpc('refresh_stock_stats')
    if (error) console.error('refresh_stock_stats', error)
  }
  if (big || productsBehind) {
    productsBehind = false
    void get().refreshProducts({ quiet: true })
  }
}
/** 133: ~7 ms a line on this database — 250 lines is ~2 s, a quarter of the limit. */
const RESTOCK_CHUNK = 250
/** 134: ~2–3.5 ms a line (SECURITY DEFINER, indexed barcode) — 500 lines is ~1–2 s. */
const OPENING_CHUNK = 500

async function insertProductBatch(
  get: () => StoreState,
  set: (fn: (s: StoreState) => Partial<StoreState>) => void,
  products: Omit<Product, 'id' | 'created_at'>[],
  note: string,
  onProgress?: (done: number) => void,
  paidSource: PaidSource | null = null,
): Promise<{ ok: true; products: Product[] } | { ok: false; error: unknown; created: Product[] }> {
  const { branches, activeBranchId } = get()
  const multi = branches.length > 1 && !!activeBranchId
  const created: Product[] = []
  let failure: unknown = null
  // The money follows the goods chunk by chunk: a tab closed half-way leaves
  // the products written so far and their lines in one purchase — short by
  // at most the chunk in flight (three requests, not one transaction). The
  // first run of 133 stopped at 5 000 products with NO purchase at all.
  let purchase: Purchase | null = null
  let purchased = 0
  let purchaseBroken = false
  for (let start = 0; start < products.length; start += PRODUCT_CHUNK) {
    const part = products.slice(start, start + PRODUCT_CHUNK)
    const bodies = part.map(sanitizeProductWrite)
    const { data, error } = await supabase
      .from('products')
      .insert(multi ? bodies.map((b) => ({ ...b, quantity: 0 })) : bodies)
      .select()
    if (error) { failure = error; break }
    let mapped: Product[] = (data ?? []).map(mapProduct)
    if (multi && data) {
      // rows return in insert order — pair the quantities back by index
      // and land them on the active branch in one bulk upsert
      const stockRows: { branch_id: string; product_id: string; quantity: number }[] = data
        .map((row: any, i: number) => ({ branch_id: activeBranchId!, product_id: row.id as string, quantity: part[i].quantity ?? 0 }))
        .filter((r: { quantity: number }) => r.quantity > 0)
      if (stockRows.length > 0) {
        const { error: se } = await supabase.from('product_stock').upsert(stockRows, { onConflict: 'branch_id,product_id' })
        if (se) {
          // Was a console line and the import went on: 480 products of one
          // chunk sat with no stock while the toast counted them (2026-10-02,
          // the upsert timed out under load). They exist, with 0 — shown as
          // such, NOT counted as imported: a second upload restocks them.
          console.error('insertProductBatch stock', se)
          // A live refresh may have brought these rows already (1.34): never twice.
          const ids = new Set(mapped.map((p) => p.id))
          set((state) => ({ products: [...mapped, ...state.products.filter((p) => !ids.has(p.id))] }))
          failure = se
          break
        }
        mapped = mapped.map((p, i) => ({ ...p, quantity: part[i].quantity ?? 0 }))
      }
    }
    created.push(...mapped)
    bulkLines += mapped.length
    // A live refresh may have brought these rows already (1.34): never twice.
    const ids = new Set(mapped.map((p) => p.id))
    set((state) => ({ products: [...mapped, ...state.products.filter((p) => !ids.has(p.id))] }))
    const lines = mapped
      .filter((pr) => pr.quantity > 0)
      .map((pr) => ({ product_id: pr.id, product_name: pr.name, barcode: pr.barcode, quantity: pr.quantity, unit_cost: pr.purchase_price }))
    if (lines.length > 0 && !purchaseBroken) {
      const next: Purchase | null = purchase
        ? await appendStockPurchase(purchase.id, lines, paidSource)
        : await recordStockPurchase(lines, note, get().activeBranchId, paidSource)
      if (next) {
        purchase = next
        purchased += lines.length
        const latest = next
        set((state) => ({ purchases: [latest, ...state.purchases.filter((x) => x.id !== latest.id)] }))
      } else {
        // The goods are written; say how much of their money is booked.
        purchaseBroken = true
        toast.error(t('store.x.purchasePartial', { done: purchased, total: products.filter((x) => (x.quantity ?? 0) > 0).length }))
      }
    }
    onProgress?.(created.length)
  }
  return failure ? { ok: false, error: failure, created } : { ok: true, products: created }
}

/** 133: more lines on a supplier-less purchase this session opened. */
async function appendStockPurchase(
  purchaseId: string,
  items: StockLine[],
  paidSource: PaidSource | null = null,
): Promise<Purchase | null> {
  const { data, error } = await supabase.rpc('append_stock_purchase', { p_purchase_id: purchaseId, p_items: items, ...chunkSourceArg(items, paidSource) })
  if (error || !data) { console.error('appendStockPurchase', error); return null }
  return mapPurchase(data)
}

/** What booking the opening stock did (1.37): what it added now, and whether it finished. */
export type OpeningBooking = {
  purchase: Purchase | null
  addedLines: number
  addedValue: number
  /** lines still not in the books — the next upload of the file books them */
  missing: number
  failed: boolean
}

/**
 * 1.37 — ONE „საწყისი ნაშთი" purchase per branch, finished across uploads.
 * The branch's opening purchase is read from the SERVER, never the cache — a
 * stale cache, or a purchases read that failed at boot, would book it twice
 * (Codex's review #1); unread = not booked (fail closed). An existing one gets
 * only the lines it does not hold, so a partial booking completes on the next
 * upload and a full one books nothing twice (#2). Appends retry twice.
 */
async function bookOpeningPurchase(
  lines: OpeningPurchaseLine[],
  branchId: string | null,
  defaultBranchId: string | null,
): Promise<OpeningBooking> {
  const none = (failed: boolean, missing = lines.length): OpeningBooking =>
    ({ purchase: null, addedLines: 0, addedValue: 0, missing, failed })
  const [byNote, byName] = await Promise.all([
    supabase.from('purchases').select('*').is('supplier_id', null).like('note', `${OPENING_PURCHASE_NOTE}%`),
    supabase.from('purchases').select('*').is('supplier_id', null).eq('supplier_name', OPENING_PURCHASE_NOTE),
  ])
  if (byNote.error || byName.error) { console.error('bookOpeningPurchase read', byNote.error ?? byName.error); return none(true) }
  let purchase = findOpeningPurchase<Purchase>([...(byNote.data ?? []), ...(byName.data ?? [])].map(mapPurchase), branchId, defaultBranchId)
  let todo = lines
  if (purchase) {
    const held = await allRows<{ product_id: string | null }>((a, b) =>
      supabase.from('purchase_items').select('product_id').eq('purchase_id', purchase!.id).order('id').range(a, b))
    if (held.error || !held.data) { console.error('bookOpeningPurchase lines', held.error); return none(true) }
    todo = missingOpeningLines(lines, new Set(held.data.map((r) => r.product_id).filter((id): id is string => !!id)))
  }
  let addedLines = 0
  let addedValue = 0
  const value = (part: OpeningPurchaseLine[]) => part.reduce((s, l) => s + l.quantity * l.unit_cost, 0)
  for (let start = 0; start < todo.length; start += PURCHASE_CHUNK) {
    const part = todo.slice(start, start + PURCHASE_CHUNK)
    let next: Purchase | null = null
    for (let attempt = 0; attempt < 3 && !next; attempt++) {
      if (purchase) {
        next = await appendStockPurchase(purchase.id, part)
      } else {
        const { data, error } = await supabase.rpc('record_stock_purchase', { p_items: part, p_note: OPENING_PURCHASE_NOTE, p_branch_id: branchId })
        if (error) console.error('bookOpeningPurchase', error)
        else next = data ? mapPurchase(data) : null
        // a write whose answer was lost may still have landed: never open a second purchase
        if (!next && attempt < 2) {
          const again = await supabase.from('purchases').select('*').is('supplier_id', null).like('note', `${OPENING_PURCHASE_NOTE}%`)
          const landed = again.error ? null : findOpeningPurchase<Purchase>((again.data ?? []).map(mapPurchase), branchId, defaultBranchId)
          if (landed) return { purchase: landed, addedLines, addedValue, missing: todo.length - addedLines, failed: true }
        }
      }
    }
    if (!next) return { purchase, addedLines, addedValue: Math.round(addedValue * 100) / 100, missing: todo.length - addedLines, failed: true }
    purchase = next
    addedLines += part.length
    addedValue += value(part)
  }
  return { purchase, addedLines, addedValue: Math.round(addedValue * 100) / 100, missing: 0, failed: false }
}

/** The code a duplicate-code 23505 names (054 or 158), if the details say. */
function duplicateBarcodeOf(error: unknown): string | null {
  return duplicateCodeOf(error)
}

/**
 * 158 (the 1.40 final review, M3) — the card's duplicate-code refusal in words
 * that name the holder. The list is read again quietly first: the holder is
 * usually a product another device just made or gave the code, which this
 * device's list does not have yet. Unknown after the read → the plain words.
 * `refresh` false = the caller has just read the list itself.
 */
async function toastDuplicateCode(
  get: StoreGet, error: unknown, typed: string, exceptId: string | null, refresh = true,
): Promise<void> {
  const dup = duplicateBarcodeOf(error) ?? typed
  if (refresh) await get().refreshProducts({ quiet: true })
  const holder = dup ? codeOwner(get().products, dup, exceptId) : undefined
  toast.error(holder ? t('store.codes.taken', { code: dup, name: holder.name }) : t('store.x.44', { p1: dup }))
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      categories: [],
      products: [],
      sales: [],
      saleItems: [],
      // Nothing loaded yet = loaded from „now"; hydrate sets the real edge.
      salesLoadedFrom: new Date().toISOString(),
      salesFirstAt: undefined,
      salesLoading: false,
      historyReady: false,
      historyFailed: false,
      salesGeneration: 0,
      heldCarts: [],
      priceGroups: [],
      priceGroupsRead: false,
      clubStatuses: {},
      stockLots: [],
      stockLotsLoaded: false,
      suppliers: [],
      purchases: [],
      purchaseItems: [],
      supplierPayments: [],
      supplierReturns: [],
      supplierReturnItems: [],
      customers: [],
      customerPayments: [],
      cashMovements: [],
      prepaidMoves: [],
      cashiers: [],
      shifts: [],
      expenses: [],
      recurringExpenses: [],
      cartRemovals: [],
      demands: null,
      halls: null,
      diningTables: null,
      bills: null,
      waiterReady: null,
      kitchenCards: null,
      serviceProductId: null,
      serviceRows: NO_SERVICE_ROWS,
      seated: null,
      visits: null,
      guestRequests: null,
      tillDuties: null,
      supplierOrders: null,
      priceChanges: [],
      branches: [],
      activeBranchId: null,
      stockTransfers: [],
      stockTransferItems: [],
      productAddons: [],
      productIngredients: [],
      productConversions: [],
      productBreakdowns: [],
      supplierCodes: [],
      promotions: [],
      settings: DEFAULT_SETTINGS,
      pin: DEFAULT_LOCK_PIN,
      pinIsDefault: false,
      pinSetup: null,
      openPinSetup: (mode) => set({ pinSetup: mode }),
      closePinSetup: () => set({ pinSetup: null }),
      closeShiftAsked: 0,
      askCloseShift: () => set((s) => ({ closeShiftAsked: s.closeShiftAsked + 1 })),
      currentOrg: null,
      storeSite: null,
      isPlatformAdmin: false,
      platformGate: 'none',
      userRole: null,
      isHydrated: false,
      loadFailed: false,
      cachedUserId: null,

      /**
       * Drop everything that belongs to one shop, keeping only device-level
       * preferences (sidebar width, kiosk lock). `isHydrated: false` is the
       * important part: it re-arms the guard in StoreHydrator so the next
       * user's data is actually fetched.
       */
      clearTenantCache: () => {
        bootGen += 1          // 1.24 — a phase 2 in flight must not land in the next tenant
        bootPending = null
        void clearProductCache()
        productsWatermark = null
        productsLens = null
        productsLocal = false
        set({
          products: [], categories: [], sales: [], saleItems: [], heldCarts: [], priceGroups: [], priceGroupsRead: false, stockLots: [], stockLotsLoaded: false,
          salesLoadedFrom: new Date().toISOString(), salesFirstAt: undefined, salesLoading: false,
          historyReady: false, historyFailed: false, salesGeneration: bootGen,
          customers: [], customerPayments: [], suppliers: [], supplierPayments: [], clubStatuses: {},
          purchases: [], purchaseItems: [], expenses: [], recurringExpenses: [], cartRemovals: [], priceChanges: [], demands: null, supplierOrders: null, halls: null, diningTables: null, bills: null, waiterReady: null, kitchenCards: null, kitchenMode: null, seated: null, visits: null, guestRequests: null, serviceProductId: null, serviceRows: NO_SERVICE_ROWS, shifts: [], cashiers: [], cashMovements: [], prepaidMoves: [], supplierReturns: [], supplierReturnItems: [],
          branches: [], activeBranchId: null, productAddons: [], productIngredients: [],
          stockTransfers: [], stockTransferItems: [], productConversions: [], productBreakdowns: [], supplierCodes: [], promotions: [],
          // The shop's name and lock PIN are the shop's too: a hydrate that
          // fails for the next user must not show the previous shop's name or
          // accept its PIN (audit, 2026-09-11).
          settings: DEFAULT_SETTINGS, pin: DEFAULT_LOCK_PIN, pinIsDefault: false, pinSetup: null, isLocked: false,
          currentOrg: null, userRole: null, isPlatformAdmin: false, platformGate: 'none', supportSession: null,
          cachedUserId: null, isHydrated: false, storeSite: null,
        } as Partial<StoreState> as StoreState)
        // 153 — the old device-wide feature answers (before they were kept per
        // user and shop) belong to nobody in particular: gone.
        try { clearFeatureCache(localStorage) } catch { /* private window */ }   // strict audit 2 (L2): every shop's answers
      },
      isLocked: false,
      isMobileSidebarOpen: false,
      isDesktopSidebarCollapsed: false,
      sidebarPeek: false,
      globalSearch: '',
      labelQueue: null,
      setLabelQueue: (ids) => set({ labelQueue: ids }),

      kioskMode: false,
      setKioskMode: (on) => set({ kioskMode: on }),
      kitchenMode: null,
      setKitchenMode: (mode) => set({ kitchenMode: mode }),

      // ---- QR payment (Keepz) ----
      qrPayReady: false,
      refreshQrPay: async () => {
        // Members may read the row (082) — only the fields that decide
        // readiness; the keys stay where they are.
        const { data } = await supabase.from('payment_credentials')
          .select('identifier, integrator_id, receiver_id, keepz_public_key, our_public_key').maybeSingle()
        const r = data as { identifier: string; integrator_id: string | null; receiver_id: string | null; keepz_public_key: string; our_public_key: string } | null
        set({ qrPayReady: !!(r && r.identifier && r.integrator_id && r.receiver_id && r.keepz_public_key && r.our_public_key) })
      },

      // ---- Offline mode ----
      isOnline: true,
      setOnline: (v) => set({ isOnline: v }),
      offlinePending: 0,
      isSyncingOffline: false,
      salaryCeilingHint: null,

      // Replay queued offline sales sequentially. Entries from a DIFFERENT org
      // (someone logged into another shop on this browser) are left untouched.
      // hydrate() runs only after a FULL drain so temp rows are swapped for
      // server rows without losing anything still queued.
      syncOfflineSales: async () => {
        if (get().isSyncingOffline || !isBrowserOnline()) return
        const orgId = get().currentOrg?.id
        if (!orgId) { set({ offlinePending: loadOfflineQueue().length }); return }
        const mine = loadOfflineQueue().filter((e) => e.org_id === orgId)
        if (mine.length === 0) { set({ offlinePending: loadOfflineQueue().length }); return }

        set({ isSyncingOffline: true })
        let synced = 0
        let failedCount = 0
        try {
          for (const entry of mine) {
            // 115 — a queued sale refused for a wrong money PIN can never go
            // through as it is (the PIN rides in the payload), and every retry
            // would count as another wrong guess and lock this login's
            // override. It stays queued and marked, for the owner; it used to
            // fail on every sync too, only without the count.
            if (entry.failed?.includes('DISCOUNT_LIMIT') && entry.payload.p_override_pin) continue
            const { data, error } = await supabase.rpc('create_sale', entry.payload)
            // 115: a wrong money PIN comes back as `{ refused }`, not as an
            // error — the server counts it, and a raise would undo the count.
            // It is NOT a sale: taking it for one dropped the sale from the queue.
            const refused = !error ? (data as { refused?: string } | null)?.refused : undefined
            if (!error && !refused) {
              removeFromOfflineQueue(entry.qid)
              synced += 1
            } else if (refused) {
              markOfflineSaleFailed(entry.qid, `${refused}: მფლობელის PIN არასწორია`)
              failedCount += 1
            } else if (isNetworkError(error)) {
              break // connection dropped again — stop, keep the rest queued
            } else {
              // Server rejected (should be rare — oversell is allowed on replay).
              // Keep it in the queue with the error so retry stays possible.
              markOfflineSaleFailed(entry.qid, (error as { message?: string }).message ?? 'unknown')
              failedCount += 1
              console.error('offline sale replay failed', entry.qid, error)
            }
          }
        } finally {
          const remaining = loadOfflineQueue()
          set({ isSyncingOffline: false, offlinePending: remaining.length })
          if (synced > 0 && remaining.filter((e) => e.org_id === orgId).length === 0) {
            toast.success(t('store.synced', { n: synced }))
            await get().hydrate() // swap temp rows for the real server rows
          } else if (synced > 0) {
            toast.success(t('store.syncedPartial', { n: synced, left: remaining.length }))
          }
          if (failedCount > 0) {
            toast.error(t('store.syncFailed', { n: failedCount }), { duration: 8000 })
          }
        }
      },

      // ---- Load everything from Supabase ----
      hydrate: async (opts) => {
        set({ loadFailed: false })
        // Offline: the till runs from what this device kept — the persisted
        // store, and the products' copy in IndexedDB, for THIS user only (a
        // device that updated from the old build still has them from
        // rehydration).
        const goOffline = async () => {
          if (get().products.length === 0) {
            // 1.25 — v1 and v2 copies; a v2 copy of another branch is not
            // this till's stock. Its meta comes back with it, so the copy is
            // re-saved unchanged and a later online boot may still be warm.
            // The branch the last online boot COMMITTED (persisted with the
            // store) — the one the copy was saved under. The device's saved
            // key can name another shop's branch on a shared device; online
            // it then falls back to this shop's default and the copy follows
            // that (Codex's review: offline the till came up empty).
            const off = offlineCopy(await readProductCacheEntry(), {
              userId: get().cachedUserId, orgId: get().currentOrg?.id, activeBranchId: get().activeBranchId,
            })
            if (off && off.products.length > 0 && get().products.length === 0) {
              productsWatermark = off.meta.watermark
              productsLens = off.meta.lens
              productsLocal = off.meta.local
              set(productsPatch(off.products))   // 196 (1.68)
            }
          }
          toast.warning(t('store.offlineMode'), { duration: 6000 })
          // 1.24 — offline nothing is loading: the screens show what is here.
          bootGen += 1
          bootPending = null
          set({ isHydrated: true, isOnline: false, offlinePending: loadOfflineQueue().length, historyReady: true, historyFailed: false, salesGeneration: bootGen })
        }
        // When the browser KNOWS it is offline, do not ask: postgrest-js
        // retries every failed GET three times (1 + 2 + 4 s), so an offline
        // reload sat on the loader for 7.6 s before the till appeared
        // (measured 2026-09-26). A connection that looks up but reaches
        // nothing still takes the long way, through the error branch below.
        if (opts?.offline || !isBrowserOnline()) { await goOffline(); return }
        // 1.24 (Codex's review) — every online hydrate starts a fresh history
        // lifecycle: what is on screen is being replaced, so the screens say
        // „loading" instead of showing the old rows as complete.
        set({ historyReady: false, historyFailed: false })
        // 089: the boot window. The same string is the left edge every later
        // „older" fetch continues from (`lt(created_at, edge)`), so the two
        // reads meet exactly — no row twice, none missed.
        const salesFrom = windowStart()
        customerSalesLoaded.clear()
        // 126 — two reads that used to wait for every other one (the 90-day
        // history included: 30 s at the 6-store scale test) start now. The
        // active branch's stock needs only the branch list; who the user is
        // needs nothing at all.
        const branchesRead = allRows((a, b) => supabase.from('branches').select('*').order('created_at', { ascending: true }).order('id').range(a, b))
        // 1.25 — the active branch from the branch list alone. Its stock is
        // read only on the full path (readAllProducts): the warm path gets
        // every branch quantity it needs from the delta, and the stock read
        // cost 1.9–2.6 s competing with the catalogue pages (Codex).
        const branchChoice = branchesRead.then((res) => {
          const list: Branch[] = res.error ? [] : (res.data ?? []).map(mapBranch)
          const saved = loadActiveBranch()
          const active: string | null =
            (saved && list.some((b) => b.id === saved) ? saved : null) ??
            list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null
          // ONLY the active branch, and only in a shop with more than one.
          return { active, lens: list.length > 1 && active ? active : null }
        })
        // 1.25 — the device's copy, read beside the first reads (~0.1 s
        // desktop, ~0.4 s phone at 30 000 products). Never rejects.
        const entryRead = readProductCacheEntry()
        const whoRead = (async () => {
          // IMPORTANT: filter memberships by THIS user's id explicitly. The
          // memberships RLS is `user_id = auth.uid() OR is_platform_admin()`, so
          // for a platform admin an unfiltered read returns EVERY org's rows and
          // .maybeSingle() then errors (>1 row) → currentOrg becomes null → the
          // admin-owner gets bounced to /platform and loses their own shop.
          const { data: { user: authUser } } = await supabase.auth.getUser()
          const [orgRow, adminRow] = await Promise.all([
            // 098: a support session adds a SECOND, temporary row — read them all
            // and pick the one in force the way auth_org() does (pickMembership).
            supabase.from('memberships')
              .select('org_id, role, created_at, expires_at, support_grant_id, organizations(id,name,plan,status,trial_ends_at,upgrade_requested,billing_no,paid_until,custom_price_month,pay_claim_at,pay_claim_months,pay_claim_amount)')
              .eq('user_id', authUser?.id ?? ''),
            // 1.48 — 'none' | 'enroll' | 'verify' | 'ok' (166). An RPC, not a
            // table read: robust with several admins, and since 167
            // is_platform_admin() is false until the phone's code is in.
            supabase.rpc('platform_admin_gate'),
          ])
          return { authUser, orgRow, adminRow }
        })()
        // A hydrate that returns early (offline, a failed read) never awaits
        // these two: a rejection there must not reach the error digest (111).
        branchChoice.catch(() => {})
        whoRead.catch(() => {})
        // The history (127). A cashier's lines are none (125): the membership
        // read above says who this is before the first page of sales is back.
        // Unknown — the read failed — asks anyway, and the server decides.
        const readsBooks = whoRead.then(
          ({ orgRow }) => (pickMembership((orgRow.data ?? []) as any[]) as any)?.role !== 'cashier',
          () => true)
        // 1.25 — the copy plus ONE delta when it is this boot's identity,
        // otherwise the full read. Either way one result in the products slot;
        // the till appears only after it (lib/warm-products.ts).
        const productRead: Promise<ProductLoad> = Promise.all([entryRead, whoRead.catch(() => null), branchChoice])
          .then(async ([entry, who, { lens }]) => {
            // Codex's review: a support (temporary) membership is judged by
            // the server's clock — then no copy, the full read.
            const fresh = who && !who.orgRow.error ? freshIdentity(who.authUser?.id, (who.orgRow.data ?? []) as any[], lens) : null
            const copy = fresh ? warmCopy(entry, fresh) : null
            if (copy) {
              try {
                const { data, error } = await supabase.rpc('list_products_changed', { p_since: sinceFor(copy.watermark), p_branch_id: lens })
                const ans = data as { now?: unknown; total?: unknown; rows?: any[] } | null
                const warm = !error && ans && Array.isArray(ans.rows)
                  ? acceptDelta(copy, { now: ans.now, total: ans.total, rows: ans.rows.map((r) => mapChangedRow(r, lens)) })
                  : null
                if (warm) return { error: null, rows: warm.products, watermark: warm.watermark, lens }
                if (error) console.error('warm products: delta failed, reading everything', error)
              } catch (e) {
                // Whatever went wrong with the copy, the full read still can.
                console.error('warm products', e)
              }
            }
            return readAllProducts(lens)
          })
        // 1.24 — one boundary for this hydrate: the till's recent range (today's
        // business day, back to the earliest open shift, never past the
        // window). Two tiny reads; only the sales read waits for them — every
        // other read below starts at once.
        const gen = ++bootGen
        bootPending = null
        const recentRead: Promise<string> = Promise.all([
          supabase.from('shifts').select('opened_at').eq('status', 'open').order('opened_at', { ascending: true }).limit(1).maybeSingle(),
          supabase.from('settings').select('value').eq('key', 'dayStartHour').maybeSingle(),
        ]).then(([openRow, dsRow]) => bootRecentFrom({
          now: Date.now(),
          dayStartHour: parseDayStartHour(dsRow.data?.value),
          // a failed read falls back to the whole window — slower, never wrong
          earliestOpenShift: openRow.error ? salesFrom : ((openRow.data?.opened_at as string | undefined) ?? null),
          windowStartIso: salesFrom,
        }), () => salesFrom)
        const recentSplit = recentRead.then((rf) => readSales(rf, null, readsBooks))
        const salesRead = recentSplit.then((r) => r.salesRead)
        const itemsRead = recentSplit.then((r) => r.itemsRead)
        const [cats, prods, sales, items, settingsRows, held, sups, purch, purchItems, supPays, custs, custPays, cashMoves, cashRows, shiftRows, expenseRows, recurringRows, branchRows, transferRows, transferItemRows, addonRows, ingredientRows, convRows, breakdownRows, codeRows, firstSale, groupRows] = await Promise.all([
          // Every table here is read in pages of 1000 with a deterministic
          // order — see lib/supabase/all-rows.ts for the silent cap that made
          // this necessary (2026-09-14).
          allRows((a, b) => supabase.from('categories').select('*').order('created_at', { ascending: true }).order('id').range(a, b)),
          // 068: never `from('products')` for a read — a cashier's RLS returns
          // zero rows there. `list_products` returns the same rows to everyone
          // and drops `purchase_price` unless the caller is an owner; the
          // server decides the role, so a stale cached role cannot widen it.
          // 081 gave list_products its tiebreaker (created_at DESC, id DESC).
          // 126: the same rows a page at a time — list_products() built all
          // 30 000 rows' JSON for every page (1 s each at the scale test).
          // 1.25: through productRead — the copy plus one delta, or these pages.
          productRead,
          salesRead,
          // Lines have no date of their own: they are read BY the sales just
          // read (125 `sale_items_of` — the caller's org only, and none for a
          // cashier, exactly as RLS decided for the old INVOKER function),
          // page by page as the sales arrive (127).
          itemsRead,
          allRows<{ key: string; value: string }>((a, b) => supabase.from('settings').select('*').order('key').range(a, b)),
          allRows((a, b) => supabase.from('held_carts').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('suppliers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('purchases').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          // 134 — NOT every purchase line any more. They were read whole at every
          // start and on every purchase signal, paged by OFFSET under RLS: a
          // chain's opening stock alone is ~180 000 lines, and at 25 000 those
          // pages already ran past the 8 s limit and starved the import's own
          // writes (2026-10-02). A purchase's lines are fetched when it is
          // opened (`fetchPurchaseItems`); a search by product asks the server
          // (`searchPurchaseLines`).
          Promise.resolve({ data: [] as any[], error: null }),
          allRows((a, b) => supabase.from('supplier_payments').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('customers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('customer_payments').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          // 107 — the drawer's movements. Not fatal: a failed read leaves the
          // list empty and the server still counts them at close.
          allRows((a, b) => supabase.from('cash_movements').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('cashiers').select(CASHIER_COLUMNS).order('created_at', { ascending: true }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('shifts').select('*').order('opened_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('expenses').select('*').order('expense_date', { ascending: false }).order('id').range(a, b)),
          // 121 — monthly costs. A cashier's RLS returns none; not fatal.
          allRows((a, b) => supabase.from('recurring_expenses').select('*').order('created_at', { ascending: true }).order('id').range(a, b)),
          branchesRead,
          allRows((a, b) => supabase.from('stock_transfers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('stock_transfer_items').select('*').order('id').range(a, b)),
          allRows((a, b) => supabase.from('product_addons').select('*').order('sort_order', { ascending: true }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('product_ingredients').select('*').order('sort_order', { ascending: true }).order('id').range(a, b)),   // 182
          allRows((a, b) => supabase.from('product_conversions').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          // 1.55 — a breakdown and its parts in ONE read, so the two can never be from different moments.
          allRows((a, b) => supabase.from('product_breakdowns').select('*, product_breakdown_parts(*)').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('supplier_product_codes').select('*').order('id').range(a, b)),
          // The shop's first sale — one row, off the same index — so the
          // history can say „ისტორია 6 ივლისიდან" and stop offering „older"
          // when there is none.
          supabase.from('sales').select('created_at').order('created_at', { ascending: true }).order('id').limit(1).maybeSingle(),
          // 181 (Codex's final review of 1.58, I4) — the price groups land WITH the customers; not
          // fatal: a failed read leaves them unread, and a grouped customer's sale waits for them
          allRows((a, b) => supabase.from('price_groups').select('*').order('created_at').order('id').range(a, b)),
        ])
        // 1.24 — resolved long ago (the sales read waited for it).
        const recentFrom = await recentRead
        // 1.25 (Codex) — a hydrate overtaken by another (a branch switch, a
        // tenant clear, a drained queue, offline) commits nothing and does
        // not take the till offline either: its rows are another lens's.
        if (gen !== bootGen) return

        // Shifts, branches and cashiers are fatal too: a failed read of any of
        // them used to become `[]` and be PERSISTED — no branches meant the
        // org-wide stock shown as this branch's and sales filed at the default
        // branch; no shifts meant „ცვლა დახურულია" until a reload (audit,
        // 2026-09-11). Better the offline path with yesterday's copy.
        if (cats.error || prods.error || sales.error || items.error || settingsRows.error
            || shiftRows.error || branchRows.error || cashRows.error) {
          console.error('Hydrate error', { cats, prods, sales, items, settingsRows, shiftRows, branchRows, cashRows })
          // Offline: the persisted POS survival kit (products/shift/settings)
          // is still in state from rehydration — keep it and let the till run.
          if (!isBrowserOnline() || isNetworkError(cats.error ?? prods.error ?? sales.error ?? items.error ?? settingsRows.error ?? shiftRows.error ?? branchRows.error ?? cashRows.error)) {
            await goOffline()
            return
          }
          toast.error(t('store.loadFailed'))
          set({ isHydrated: true, loadFailed: true, historyFailed: true })
          return
        }
        // Whether „QR" belongs on the till. Non-critical: a failed read means
        // no chip until the next open, never a broken till.
        void get().refreshQrPay().catch(() => {})

        // held_carts + supplier tables are non-critical (newer tables); don't fail hydrate if they error
        if (held.error) console.error('held_carts load error', held.error)
        if (sups.error || purch.error || purchItems.error || supPays.error)
          console.error('suppliers load error', { sups, purch, purchItems, supPays })
        if (custs.error || custPays.error)
          console.error('customers load error', { custs, custPays })
        if (expenseRows.error) console.error('expenses load error', expenseRows.error)
        if (recurringRows.error) console.error('recurring_expenses load error', recurringRows.error)
        if (addonRows.error) console.error('product_addons load error', addonRows.error)
        if (ingredientRows.error) console.error('product_ingredients load error', ingredientRows.error)
        if (convRows.error) console.error('product_conversions load error', convRows.error)
        if (breakdownRows.error) console.error('product_breakdowns load error', breakdownRows.error)
        if (codeRows.error) console.error('supplier_product_codes load error', codeRows.error)

        // Multi-branch: the active branch, resolved the moment the branch list
        // arrived (branchChoice above — the same list as `branchRows`). The
        // products are already through its lens (readAllProducts) or merged
        // from the copy (1.25).
        const branches: Branch[] = (branchRows.data ?? []).map(mapBranch)
        const { active: activeBranchId } = await branchChoice
        const productSplit = productsPatch(prods.rows)   // 196 (1.68)

        // settings key-value rows -> Settings object (+ pin)
        const kv: Record<string, string> = {}
        for (const row of settingsRows.data ?? []) kv[row.key] = row.value
        const settings: Settings = {
          companyName: kv.companyName ?? DEFAULT_SETTINGS.companyName,
          companyId: kv.companyId ?? DEFAULT_SETTINGS.companyId,
          address: kv.address ?? DEFAULT_SETTINGS.address,
          phone: kv.phone ?? DEFAULT_SETTINGS.phone,
          taxStatus: (['none', 'small', 'vat'].includes(kv.taxStatus) ? kv.taxStatus : 'none') as TaxStatus,
          bankDetails: kv.bankDetails ?? DEFAULT_SETTINGS.bankDetails,
          ownerReport: kv.ownerReport === 'true',
          tgMorning: tgEnabled(kv, 'morning'),
          tgShift: tgEnabled(kv, 'shift'),
          tgEvening: tgEnabled(kv, 'evening'),
          tgSale: tgEnabled(kv, 'sale'),
          tgLoss: tgEnabled(kv, 'loss'),
          tgWeek: tgEnabled(kv, 'week'),
          tgWaybill: tgEnabled(kv, 'waybill'),
          tgOrder: tgEnabled(kv, 'order'),
          posDiscount: kv.posDiscount === 'true' ? true : kv.posDiscount === 'false' ? false : null,
          features: featuresFromKv(kv),
          firstDayDone: kv.firstDayDone === 'true',
          shopType: kv.shopType ?? '',
          floatKeep: kv.floatKeep === 'true',
          floatAmount: Number.isFinite(parseFloat(kv.floatAmount)) && parseFloat(kv.floatAmount) >= 0
            ? Math.round(parseFloat(kv.floatAmount) * 100) / 100
            : DEFAULT_SETTINGS.floatAmount,
          dayStartHour: parseDayStartHour(kv.dayStartHour),
          salaryKeep: kv.salaryKeep === 'true',
          salaryAmount: parseSalaryAmount(kv.salaryAmount),
          // settings KV stores strings — parse the typed scale/fiscal keys
          scaleEnabled: kv.scaleEnabled === 'true',
          scalePrefixes: kv.scalePrefixes || DEFAULT_SETTINGS.scalePrefixes,
          scalePluDigits: kv.scalePluDigits === '4' ? 4 : 5,
          scaleValueType: kv.scaleValueType === 'price_tetri' ? 'price_tetri' : 'weight_g',
          fiscalKasaEnabled: kv.fiscalKasaEnabled === 'true',
          fiscalDevice: kv.fiscalDevice === 'true',
          exchangeDays: parseExchangeDays(kv.exchangeDays),
          cameraUrlTemplate: kv.cameraUrlTemplate ?? '',
          kioskCardProvider: (['tbc', 'bog', 'liberty'].includes(kv.kioskCardProvider) ? kv.kioskCardProvider : 'tbc') as CardProvider,
          kioskAllowCash: kv.kioskAllowCash === 'true',
          shareCatalog: kv.shareCatalog === 'true',
          // Tri-state on purpose: absent means the owner was never asked.
          cashierStocktake: kv.cashierStocktake === undefined ? null : kv.cashierStocktake === 'true',
          lastBackupAt: kv.lastBackupAt ?? '',
          lastDriverName: kv.lastDriverName ?? '',
          lastDriverTin: kv.lastDriverTin ?? '',
          lastDriverCar: kv.lastDriverCar ?? '',
          // 1.63 — the bonus and the club, one reader for the start and the live refresh
          ...clubSettingsFromKv(kv),
          ...tableSettingsFromKv(kv),   // 196 (1.68)
        }

        // Tenant (organization) + platform-admin role — read since the start
        // of hydrate (whoRead above).
        const { authUser, orgRow, adminRow } = await whoRead
        const membership: any = pickMembership((orgRow.data ?? []) as any[])
        const orgData: any = membership?.organizations ?? null
        const currentOrg: CurrentOrg | null = orgData ? {
          id: orgData.id,
          name: orgData.name,
          plan: orgData.plan,
          status: orgData.status,
          trial_ends_at: orgData.trial_ends_at ?? null,
          upgrade_requested: !!orgData.upgrade_requested,
          billing_no: orgData.billing_no ?? null,
          paid_until: orgData.paid_until ?? null,
          custom_price_month: orgData.custom_price_month == null ? null : Number(orgData.custom_price_month),
          pay_claim_at: orgData.pay_claim_at ?? null,
          pay_claim_months: orgData.pay_claim_months ?? null,
          pay_claim_amount: orgData.pay_claim_amount == null ? null : Number(orgData.pay_claim_amount),
        } : null
        const platformGate = parseGate(adminRow.data)
        const isPlatformAdmin = platformGate !== 'none'
        // Read the role, but never GUESS it. An unreadable membership means we
        // do not know, and "we do not know" must resolve to the smaller set of
        // screens — the same direction the RLS policies lean.
        const rawRole = membership?.role
        const userRole: 'owner' | 'cashier' | 'accountant' | null =
          rawRole === 'owner' || rawRole === 'cashier' || rawRole === 'accountant' ? rawRole : null

        // 1.25 — the last await is behind us; the meta lands with the list.
        if (gen !== bootGen) return
        productsWatermark = prods.watermark
        productsLens = prods.lens
        productsLocal = false
        // The products, purchases and suppliers are replaced from the server
        // here: a purchase still in flight must not add its delta on top (1.34).
        liveApplied++
        set({
          categories: (cats.data ?? []) as Category[],
          ...productSplit,
          sales: (sales.data ?? []).map(mapSale),
          saleItems: (items.data ?? []).map(mapSaleItem),
          // 1.24 — the RECENT range only. A shop whose first sale is inside it
          // has everything already (edge null, no phase 2); otherwise phase 2
          // brings [salesFrom, recentFrom) behind the till.
          salesLoadedFrom: bootEdge(recentFrom, firstSale.error ? undefined : (firstSale.data?.created_at ?? null)),
          salesGeneration: gen,
          salesFirstAt: firstSale.error ? undefined : (firstSale.data?.created_at ?? null),
          salesLoading: false,
          historyReady: bootEdge(recentFrom, firstSale.error ? undefined : (firstSale.data?.created_at ?? null)) === null,
          historyFailed: false,
          heldCarts: (held.data ?? []).map(mapHeldCart),
          priceGroups: groupRows.error ? [] : (groupRows.data ?? []).map(mapPriceGroup),
          priceGroupsRead: !groupRows.error,
          suppliers: (sups.data ?? []).map(mapSupplier),
          purchases: (purch.data ?? []).map(mapPurchase),
          purchaseItems: (purchItems.data ?? []).map(mapPurchaseItem),
          supplierPayments: (supPays.data ?? []).map(mapSupplierPayment),
          customers: (custs.data ?? []).map(mapCustomer),
          customerPayments: (custPays.data ?? []).map(mapCustomerPayment),
          cashMovements: (cashMoves.data ?? []).map(mapCashMovement),
          cashiers: (cashRows.data ?? []).map(mapCashier),
          shifts: (shiftRows.data ?? []).map(mapShift),
          expenses: (expenseRows.data ?? []).map(mapExpense),
          recurringExpenses: (recurringRows.data ?? []).map(mapRecurringExpense),
          branches,
          activeBranchId,
          stockTransfers: (transferRows.data ?? []).map(mapStockTransfer),
          stockTransferItems: (transferItemRows.data ?? []).map(mapStockTransferItem),
          productAddons: (addonRows.data ?? []).map(mapProductAddon),
          productIngredients: (ingredientRows.data ?? []).map(mapProductIngredient),
          productConversions: (convRows.data ?? []).map(mapProductConversion),
          productBreakdowns: (breakdownRows.data ?? []).map((r: any) => mapProductBreakdown(r)),
          supplierCodes: (codeRows.data ?? []).map(mapSupplierCode),
          settings,
          // 101: `pin` here is the SCREEN lock (settings key `lock_pin`).
          // The money PIN — the one that lifts a cashier's discount ceiling —
          // is owner-only in the database and never reaches this store at
          // all. `kv.pin` is the fallback for an owner whose shop predates
          // the split, and for him the two are the same value anyway.
          pin: kv.lock_pin ?? kv.pin ?? DEFAULT_LOCK_PIN,
          pinIsDefault: !kv.lock_pin && !kv.pin,
          currentOrg,
          isPlatformAdmin,
          platformGate,
          userRole,
          supportSession: membership?.support_grant_id
            ? { grantId: membership.support_grant_id, canWrite: membership.role === 'owner', endsAt: membership.expires_at }
            : null,
          cachedUserId: authUser?.id ?? null,
          isHydrated: true,
          isOnline: true,
          offlinePending: loadOfflineQueue().length,
        })
        // 1.24 — phase 2: the rest of the 90 days, behind the till.
        const edge = get().salesLoadedFrom
        if (edge !== null && gen === bootGen) {
          bootPending = { gen, from: salesFrom, edge, firstAt: get().salesFirstAt }
          void loadBootHistory(get, set)
        }
        // 086: promotions load after the till is usable — a failed read
        // leaves the list as it was (persisted), never blocks a sale.
        void get().refreshPromotions()
        // 174: the gift card / deposit moves (the close's preview reads their cash)
        void get().refreshPrepaidMoves()
        // 181: the price groups came with the batch; a failed read is tried once more here
        if (groupRows.error) void get().refreshPriceGroups()
        // 1.61: the open batches — after the till, never blocking it
        void get().refreshStockLots()
        // 153: the shop's website, if it has one — after the till, never blocking it
        void get().refreshStoreSite()
        // 122: the loss-control logs, for whoever reads the books, after the
        // boot — they never hold up a till.
        if (userRole === 'owner' || userRole === 'accountant') {
          void get().refreshSupplierReturns()   // 170
          void readLossLogs(salesFrom).then(({ removals, prices }) => {
            if (removals) set({ cartRemovals: removals })
            if (prices) set({ priceChanges: prices })
          })
        }
      },

      // ---- 089: older history, on demand ----
      ensureSalesSince: (fromISO) => {
        const run = async () => {
          const { salesLoadedFrom, salesFirstAt, isHydrated } = get()
          if (!isHydrated) return
          // Nothing older exists (first sale inside the range): close the
          // range so the notices stop, and fetch nothing.
          if (!hasOlder(salesLoadedFrom, salesFirstAt)) {
            if (salesLoadedFrom !== null) set({ salesLoadedFrom: null })
            return
          }
          const wanted = clampToFirst(fromISO, salesFirstAt)
          if (!needsOlder(salesLoadedFrom, wanted)) return
          const edge = salesLoadedFrom as string
          set({ salesLoading: true })
          try {
            // [wanted, edge) on the index, lines page by page (127).
            const { salesRead, itemsRead } = readSales(wanted, edge, Promise.resolve(get().userRole !== 'cashier'))
            const [older, olderItems] = await Promise.all([salesRead, itemsRead])
            if (older.error || olderItems.error) {
              console.error('older history load error', { older, olderItems })
              toast.error(t('store.x.1'))
              return
            }
            set((state) => {
              // A hydrate (branch switch, offline sync) may have reset the
              // window while this was in flight. Its rows fit the OLD edge;
              // gluing them to the new one could leave a gap of seconds at
              // the seam. Drop them — the screens' effects ask again.
              if (state.salesLoadedFrom !== edge) return {}
              return {
                sales: sortSalesDesc(mergeById(state.sales, (older.data ?? []).map(mapSale))),
                saleItems: mergeById(state.saleItems, (olderItems.data ?? []).map(mapSaleItem)),
                salesLoadedFrom: wanted,
              }
            })
          } finally {
            set({ salesLoading: false })
          }
        }
        const safe = () => run().catch((e) => console.error('older history', e))
        historyChain = historyChain.then(safe, safe)
        return historyChain
      },

      ensureCustomerSales: (customerId) => {
        const run = async () => {
          const { salesLoadedFrom, isHydrated } = get()
          if (!isHydrated || salesLoadedFrom === null || customerSalesLoaded.has(customerId)) return
          const edge = salesLoadedFrom
          set({ salesLoading: true })
          try {
            const older = await allRows((a, b) => supabase.from('sales').select('*').eq('customer_id', customerId).lt('created_at', edge)
              .order('created_at', { ascending: false }).order('id').range(a, b))
            const olderItems = await linesOf(older)
            if (older.error || olderItems.error) {
              console.error('customer history load error', { older, olderItems })
              toast.error(t('store.x.2'))
              return
            }
            // Same seam rule as ensureSalesSince: rows fetched against an
            // edge that moved are not glued to the new one.
            if (get().salesLoadedFrom !== edge) return
            customerSalesLoaded.add(customerId)
            if ((older.data ?? []).length === 0) return
            set((state) => ({
              sales: sortSalesDesc(mergeById(state.sales, (older.data ?? []).map(mapSale))),
              saleItems: mergeById(state.saleItems, (olderItems.data ?? []).map(mapSaleItem)),
            }))
          } finally {
            set({ salesLoading: false })
          }
        }
        const safe = () => run().catch((e) => console.error('customer history', e))
        historyChain = historyChain.then(safe, safe)
        return historyChain
      },

      // 1.24 — ask again for the boot's history after it failed.
      retryHistory: () => {
        const action = historyRetryAction({ historyReady: get().historyReady, historyFailed: get().historyFailed, phase2Pending: bootPending !== null })
        if (action === 'none') return
        // The boot itself failed (no phase 2 to retry): the whole hydrate again.
        if (action === 'hydrate') { set({ historyFailed: false }); void get().hydrate(); return }
        if (!bootPending) return
        // Continue from where the loaded range ends NOW: after a failure a
        // screen's ensureSalesSince may have loaded part of it already.
        const edge = get().salesLoadedFrom
        if (edge === null) { bootPending = null; set({ historyReady: true, historyFailed: false }); return }
        bootPending = { ...bootPending, gen: bootGen, edge }
        set({ historyFailed: false })
        void loadBootHistory(get, set)
      },

      // ---- UI-only state ----
      setMobileSidebarOpen: (open) => set({ isMobileSidebarOpen: open }),
      setDesktopSidebarCollapsed: (collapsed) => set({ isDesktopSidebarCollapsed: collapsed }),
      setSidebarPeek: (open) => set({ sidebarPeek: open }),
      setGlobalSearch: (q) => set({ globalSearch: q }),

      /**
       * A shop that never chose a screen PIN locks behind DEFAULT_LOCK_PIN,
       * which anyone can guess — and nothing on screen said so. Found
       * 2026-09-23: SAMGORI2026 (715 sales) and ANNASHOP had no PIN at all.
       * The owner is asked to choose one first and the screen locks right
       * after. Anyone else locks as before: a cashier cannot write settings
       * (070), and a support session must not set the owner's PIN (098).
       */
      lock: () => {
        const s = get()
        if (s.pinIsDefault && s.userRole === 'owner' && !s.supportSession) {
          set({ pinSetup: 'lock' })
          return
        }
        set({ isLocked: true })
      },
      unlock: (inputPin) => {
        if (inputPin === get().pin) {
          set({ isLocked: false })
          return true
        }
        return false
      },

      // ---- Settings (key-value upsert) ----
      updateSettings: async (s) => {
        const prev = get().settings
        const next = { ...prev, ...s }
        set({ settings: next }) // optimistic
        const orgId = get().currentOrg?.id
        const rows = Object.entries(s).map(([key, value]) => ({
          ...(orgId ? { org_id: orgId } : {}), key, value: String(value ?? ''),
        }))
        const { error } = await supabase.from('settings').upsert(rows, { onConflict: 'org_id,key' })
        if (failedT(error, 'store.x.3')) { set({ settings: prev }); return false }
        return true
      },

      setFeature: async (key, on) => {
        if (key === 'discount') return get().updateSettings({ posDiscount: on })
        const prev = get().settings
        const features = { ...prev.features }
        if (on === null) delete features[key]
        else features[key] = on
        set({ settings: { ...prev, features } }) // optimistic
        const orgId = get().currentOrg?.id
        // An empty value is „automatic" — the row stays, the choice goes.
        const { error } = await supabase.from('settings').upsert(
          { ...(orgId ? { org_id: orgId } : {}), key: `feature.${key}`, value: on === null ? '' : String(on) },
          { onConflict: 'org_id,key' },
        )
        if (failedT(error, 'store.x.4')) { set({ settings: prev }); return false }
        return true
      },

      moneyPinShared: false,

      /**
       * 101 — the money PIN. Written and never read: the row is owner-only in
       * the database, so this is the only way it can change and nothing in
       * the app ever holds it. `create_sale` is what compares it.
       */
      setMoneyPin: async (pin) => {
        const orgId = get().currentOrg?.id
        const { error } = await supabase.from('settings').upsert(
          { ...(orgId ? { org_id: orgId } : {}), key: 'pin', value: pin },
          { onConflict: 'org_id,key' },
        )
        if (failedT(error, 'store.x.5')) return false
        await get().checkMoneyPinShared()
        return true
      },

      checkMoneyPinShared: async () => {
        const { data, error } = await supabase.rpc('money_pin_is_shared')
        if (!error) set({ moneyPinShared: data === true })
      },

      setPin: async (pin) => {
        const prev = { pin: get().pin, pinIsDefault: get().pinIsDefault }
        set({ pin, pinIsDefault: false }) // optimistic
        const orgId = get().currentOrg?.id
        // 101: this is the SCREEN lock. The money PIN has its own action.
        const { error } = await supabase.from('settings').upsert(
          { ...(orgId ? { org_id: orgId } : {}), key: 'lock_pin', value: pin },
          { onConflict: 'org_id,key' },
        )
        if (failedT(error, 'store.x.5')) { set(prev); return false }
        return true
      },

      // Owner asks the platform to upgrade their plan (manual billing).
      requestUpgrade: async () => {
        const { error } = await supabase.rpc('request_upgrade')
        if (failedT(error, 'store.x.6')) return
        set((s) => ({ currentOrg: s.currentOrg ? { ...s.currentOrg, upgrade_requested: true } : s.currentOrg }))
        toast.success(t('store.x.7'))
      },

      platformBilling: null,
      loadPlatformBilling: async () => {
        const { data, error } = await supabase
          .from('platform_billing')
          .select('recipient, recipient_id, accounts, price_month, price_year, note')
          .maybeSingle()
        if (error || !data) { if (error) console.error('platform billing', error); return get().platformBilling }
        const b: PlatformBilling = {
          recipient: data.recipient ?? '',
          recipient_id: data.recipient_id ?? '',
          accounts: Array.isArray(data.accounts) ? data.accounts : [],
          price_month: Number(data.price_month),
          price_year: Number(data.price_year),
          note: data.note ?? '',
        }
        set({ platformBilling: b })
        return b
      },

      // The route calls claim_payment with the caller's own session (the
      // database decides owner-only and the amount) and then mails the
      // platform admin, so „I transferred" is seen the same hour.
      claimPayment: async (months, bank) => {
        try {
          const res = await fetch('/api/billing-claim', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ months, bank }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok) { toast.error(json.error ?? t('store.x.8')); return false }
          const c = json.claim ?? {}
          set((s) => ({
            currentOrg: s.currentOrg ? {
              ...s.currentOrg,
              pay_claim_at: c.claimed_at ?? new Date().toISOString(),
              pay_claim_months: c.months ?? months,
              pay_claim_amount: c.amount == null ? null : Number(c.amount),
            } : s.currentOrg,
          }))
          return true
        } catch {
          toast.error(t('store.x.9'))
          return false
        }
      },

      supportSession: null,

      createSupportGrant: async (canWrite, minutes, note) => {
        const { data, error } = await supabase.rpc('create_support_grant', { p_can_write: canWrite, p_minutes: minutes, p_note: note || null })
        if (failedT(error, 'store.x.10')) return null
        return data as { id: string; code: string; valid_until: string }
      },

      // Through the API Worker: it calls send_support_request with the owner's
      // own session and mails the platform admin (no service role).
      sendSupportRequest: async (grantId) => {
        try {
          const res = await fetch('/api/support-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ grantId }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok) { toast.error(json.error ?? t('store.x.6')); return false }
          return true
        } catch {
          toast.error(t('store.x.11'))
          return false
        }
      },

      endSupportSession: async () => {
        const { error } = await supabase.rpc('end_support_session')
        return !failedT(error, 'store.x.12')
      },

      fetchSupportStatus: async () => {
        const { data, error } = await supabase.rpc('support_status')
        if (error) { console.error('support status', error); return null }
        return data as SupportStatus
      },

      // ---- 100: outgoing waybills (RS.GE) ----
      waybillRefs: null,
      waybillRefsError: null,
      waybillSaveError: null,

      loadWaybillRefs: async () => {
        const cached = get().waybillRefs
        if (cached) return cached
        set({ waybillRefsError: null })
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'refs' }),
          })
          const json = await res.json().catch(() => ({}))
          // 129 — a shop that never connected RS.GE is not a refusal: the
          // dialog says where to connect it, and no raw „no-creds" toast.
          if (json.error === 'no-creds') {
            set({ waybillRefsError: { message: 'no-creds', noCreds: true } })
            return null
          }
          if (!res.ok) {
            const message = json.detail ?? json.error ?? 'RS.GE-ს სიები ვერ წამოვიდა'
            set({ waybillRefsError: { message } })
            toast.error(message)
            return null
          }
          const refs = { types: json.types ?? [], units: json.units ?? [], transTypes: json.transTypes ?? [] }
          if (refs.types.length === 0) {
            // RS.GE answered, and the answer had no types in it. Keep what it
            // said: nine times out of ten the service user simply lacks the
            // „შექმნა/რედაქტირება" permission on ზედნადები.
            const raw = typeof json.rawSample?.types === 'string' ? json.rawSample.types : undefined
            set({ waybillRefsError: { message: 'RS.GE-მ ზედნადების ტიპები არ დააბრუნა', raw } })
            return null
          }
          set({ waybillRefs: refs })
          return refs
        } catch {
          const message = 'ინტერნეტი არ არის — RS.GE მიუწვდომელია'
          set({ waybillRefsError: { message } })
          toast.error(message)
          return null
        }
      },

      rsgeNameFromTin: async (tin) => {
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'tin_name', tin }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok) { toast.error(json.error ?? 'კოდი ვერ შემოწმდა'); return null }
          return (json.name as string | null) ?? null
        } catch {
          toast.error('ინტერნეტი არ არის — RS.GE მიუწვდომელია')
          return null
        }
      },

      // RS.GE first, our own row second: a waybill that exists at the tax
      // office and not here is a note we can add later, while a row here for a
      // waybill that was never saved would be a lie on the sale.
      rsgeSample: null,

      fetchRsgeSample: async (id) => {
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'sample', id: id ?? undefined }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok) { toast.error(json.detail ?? json.error ?? 'ნიმუში ვერ წამოვიდა'); return }
          set({ rsgeSample: {
            waybillId: json.waybillId ?? null, waybill: json.waybill ?? null,
            templates: json.templates ?? null, via: json.via ?? null,
          } })
        } catch {
          toast.error('ინტერნეტი არ არის')
        }
      },

      saveOutgoingWaybill: async (draft, saleId, transferId) => {
        set({ waybillSaveError: null })
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'save_waybill', draft, saleId }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok || !json.ok) {
            const message = json.error ?? json.detail ?? 'ზედნადები ვერ შეინახა'
            set({ waybillSaveError: { message, code: json.code ?? null, codeText: json.codeText ?? null, raw: json.rawSample, sent: json.sentSample } })
            toast.error(message)
            return null
          }
          const total = draft.lines.reduce((sum, l) => sum + Math.round(l.quantity * l.unitPrice * 100) / 100, 0)
          const { data, error } = await supabase.from('outgoing_waybills').insert({
            sale_id: saleId,
            // 129 — a transfer's internal-transport waybill
            ...(transferId ? { transfer_id: transferId } : {}),
            rs_id: String(json.id),
            rs_number: json.number ?? null,
            waybill_type: draft.type,
            buyer_tin: draft.buyerTin,
            buyer_name: draft.buyerName,
            full_amount: Math.round(total * 100) / 100,
            fields: json.fields ?? null,
          }).select('*').single()
          if (error) {
            // Saved at RS.GE, not remembered here: say the number out loud so
            // it is not lost, rather than pretend the save failed.
            console.error('outgoing waybill row', error)
            toast.error(`ზედნადები RS.GE-ზე შეინახა (№ ${json.number ?? json.id}), ჩვენთან ჩაწერა ვერ მოხერხდა`)
            return null
          }
          toast.success(`ზედნადები შეინახა — № ${json.number ?? json.id}`)
          return { ...(data as OutgoingWaybill), full_amount: Number((data as { full_amount: number }).full_amount) }
        } catch {
          toast.error('ინტერნეტი არ არის — ზედნადები ვერ შეინახა')
          return null
        }
      },

      listOutgoingWaybills: async () => {
        const { data, error } = await supabase.from('outgoing_waybills')
          .select('*').order('created_at', { ascending: false }).limit(200)
        if (error) { console.error('outgoing waybills list', error); return [] }
        return (data ?? []).map((r: any) => ({ ...(r as OutgoingWaybill), full_amount: Number(r.full_amount) }))
      },

      activateOutgoingWaybill: async (row) => {
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'activate_waybill', id: row.rs_id }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok || !json.ok) {
            toast.error(json.error ?? json.detail ?? 'ზედნადები ვერ გააქტიურდა', { duration: 8000 })
            return null
          }
          // RS.GE's own answer about what it now is — not our assumption that
          // a successful call means „active".
          const mapped: OutgoingWaybill['status'] = rsStatus(Number(json.status)) ?? 'sent'
          const next: OutgoingWaybill = { ...row, status: mapped, rs_number: json.number ?? row.rs_number }
          const { error } = await supabase.from('outgoing_waybills')
            .update({ status: next.status, rs_number: next.rs_number }).eq('id', row.id)
          if (error) console.error('activate write-back', error.message)
          toast.success(next.rs_number ? `ზედნადები გააქტიურდა — № ${next.rs_number}` : 'ზედნადები გააქტიურდა')
          return next
        } catch {
          toast.error('ინტერნეტი არ არის — ზედნადები ვერ გააქტიურდა')
          return null
        }
      },

      refreshOutgoingStatuses: async (rows) => {
        // Only the ones we still believe are drafts: an activated waybill does
        // not go back, so asking again would be noise on a tax service.
        const ids = rows.filter((r) => r.status === 'draft').map((r) => r.rs_id).filter(Boolean)
        if (ids.length === 0) return { rows, failed: [] }
        try {
          const res = await fetch('/api/rsge', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'out_status', ids: ids.slice(0, 25) }),
          })
          const json = await res.json().catch(() => ({}))
          if (!res.ok) {
            const m = json.detail ?? json.error ?? 'სტატუსი ვერ განახლდა'
            toast.error(m)
            return { rows, failed: ids.map((id) => ({ id, error: m })) }
          }
          const answers = (json.statuses ?? []) as { id: string; status: string | null; number: string | null; error?: string }[]
          const byId = new Map(answers.map((s) => [s.id, s]))
          // RS.GE answered for these, but with nothing usable.
          const failed = answers
            .filter((s) => s.status == null)
            .map((s) => ({ id: s.id, error: s.error ?? 'RS.GE-მ სტატუსი არ დააბრუნა' }))
          const next = rows.map((r) => {
            const fresh = byId.get(r.rs_id)
            if (!fresh || fresh.status == null) return r
            // RS.GE's codes: 0 საved, 1 active, 2 closed, negative = void.
            const n = Number(fresh.status)
            const mapped: OutgoingWaybill['status'] = rsStatus(n) ?? r.status
            if (mapped === r.status && (fresh.number ?? null) === r.rs_number) return r
            return { ...r, status: mapped, rs_number: fresh.number ?? r.rs_number }
          })
          // Remember what changed, so the next visit does not ask again.
          const changed = next.filter((r, i) => r !== rows[i])
          for (const c of changed) {
            const { error } = await supabase.from('outgoing_waybills')
              .update({ status: c.status, rs_number: c.rs_number }).eq('id', c.id)
            if (error) console.error('outgoing status write', error.message)
          }
          return { rows: next, failed }
        } catch {
          const m = 'ინტერნეტი არ არის — სტატუსი ვერ განახლდა'
          toast.error(m)
          return { rows, failed: ids.map((id) => ({ id, error: m })) }
        }
      },

      getOutgoingWaybill: async (saleId) => {
        const { data, error } = await supabase.from('outgoing_waybills')
          .select('*').eq('sale_id', saleId).order('created_at', { ascending: false }).limit(1).maybeSingle()
        if (error) { console.error('outgoing waybill read', error); return null }
        if (!data) return null
        return { ...(data as OutgoingWaybill), full_amount: Number((data as { full_amount: number }).full_amount) }
      },

      getTransferWaybill: async (transferId) => {
        const { data, error } = await supabase.from('outgoing_waybills')
          .select('*').eq('transfer_id', transferId).order('created_at', { ascending: false }).limit(1).maybeSingle()
        if (error) { console.error('transfer waybill read', error); return null }
        if (!data) return null
        return { ...(data as OutgoingWaybill), full_amount: Number((data as { full_amount: number }).full_amount) }
      },

      // ---- Categories ----
      addCategory: async (cat) => {
        const { data, error } = await supabase
          .from('categories')
          .insert({ name: cat.name, parent_id: cat.parent_id ?? null, icon: cat.icon ?? null,
                    // 1.66 — sent only when the owner chose one (a database before 192 has no such column)
                    ...(cat.kitchen_station !== undefined ? { kitchen_station: cat.kitchen_station } : {}) })
          .select()
          .single()
        // 050 guarantees uniqueness per (org, parent, name). Say which name
        // collided — "duplicate key" is not something a shopkeeper can act on.
        if (isDuplicateName(error)) {
          toast.error(t('store.x.13', { name: cat.name.trim() }))
          return false
        }
        if (failedT(error, 'store.x.14') || !data) return false
        set((state) => ({ categories: [...state.categories, data as Category] }))
        return true
      },

      addCategories: async (names) => {
        if (names.length === 0) return []
        const { data, error } = await supabase
          .from('categories')
          .insert(names.map((name) => ({ name, parent_id: null, icon: null })))
          .select()
        if (failedT(error, 'store.x.14') || !data) return null
        const made = data as Category[]
        set((state) => ({ categories: [...state.categories, ...made] }))
        return made
      },

      applyCategoryTemplate: async (templateKey) => {
        const base = CATEGORY_TEMPLATES.find((x) => x.key === templateKey)
        if (!base) return 0
        // In the device's language: the categories are created in the words
        // the picker showed (lib/category-templates.ts, `templateIn`).
        const tpl = templateIn(base, t)
        // What is already there, keyed the way 050 makes names unique: roots
        // by name, children by (parent, name). A global name set used to stand
        // in for both, and it was wrong in the one direction that matters for
        // the parts template (2026-09-14): „კარი" under ძარა and „კარი" under
        // მეორადი are two shelves, and a re-run to fill gaps must see them so.
        const cats = get().categories
        const existingRoots = new Set(cats.filter((c) => !c.parent_id).map((c) => c.name.trim().toLowerCase()))
        const existingUnder = new Set(cats.filter((c) => c.parent_id).map((c) => `${c.parent_id}:${c.name.trim().toLowerCase()}`))

        // Two batched inserts, not one per row: a grocery template is ~50
        // categories and 50 round trips would take longer than typing them.
        // Parents first, because children need their ids.
        const newParents = tpl.groups
          .map((g) => g.name)
          .filter((n) => !existingRoots.has(n.trim().toLowerCase()))
        let parentRows: Category[] = []
        if (newParents.length) {
          const { data, error } = await supabase
            .from('categories')
            .insert(newParents.map((name) => ({ name, parent_id: null })))
            .select()
          if (failedT(error, 'store.x.15')) return 0
          parentRows = (data ?? []) as Category[]
        }

        // Parent name -> id, counting categories that were ALREADY there, so
        // re-running only fills the gaps instead of duplicating a tree.
        const idByName = new Map<string, string>()
        for (const c of [...get().categories, ...parentRows]) {
          if (!c.parent_id) idByName.set(c.name.trim().toLowerCase(), c.id)
        }

        const childInserts: { name: string; parent_id: string }[] = []
        for (const g of tpl.groups) {
          const pid = idByName.get(g.name.trim().toLowerCase())
          if (!pid) continue
          for (const child of g.children ?? []) {
            if (existingUnder.has(`${pid}:${child.trim().toLowerCase()}`)) continue
            childInserts.push({ name: child, parent_id: pid })
          }
        }

        let childRows: Category[] = []
        if (childInserts.length) {
          const { data, error } = await supabase.from('categories').insert(childInserts).select()
          // Parents already exist at this point — report it, but keep them
          // rather than leaving the shop with a half-created tree and no idea.
          if (error) console.error('child categories failed', error)
          else childRows = (data ?? []) as Category[]
        }

        set((state) => ({ categories: [...state.categories, ...parentRows, ...childRows] }))

        // 3b — the template is a PROFILE: remember which shop this is, and turn
        // on what its shelves cannot sell without (the scale for weighed
        // goods). Only ever switches ON — a shop that turned the scale off
        // itself and re-applies a template to fill gaps keeps its choice.
        const profile = templateProfile(tpl.key)
        const firstTime = get().settings.shopType !== tpl.key
        const patch: Partial<Settings> = {}
        if (firstTime) patch.shopType = tpl.key
        if (profile.scale && firstTime && !get().settings.scaleEnabled) patch.scaleEnabled = true
        if (Object.keys(patch).length) await get().updateSettings(patch)
        return parentRows.length + childRows.length
      },

      updateCategory: async (id, cat) => {
        const { error } = await supabase.from('categories').update(cat).eq('id', id)
        // Renaming into an existing name is the same collision from the other
        // direction, and was equally unguarded.
        if (isDuplicateName(error)) {
          toast.error(t('store.x.16', { p1: String(cat.name ?? '').trim() }))
          return false
        }
        if (failedT(error, 'store.x.17')) return false
        set((state) => ({
          categories: state.categories.map((c) => (c.id === id ? { ...c, ...cat } : c)),
        }))
        return true
      },

      deleteCategory: async (id) => {
        const { error } = await supabase.from('categories').delete().eq('id', id)
        if (failedT(error, 'store.x.18')) return
        set((state) => {
          // mirror DB cascade locally: remove the category + all descendants
          const toRemove = new Set<string>([id])
          let grew = true
          while (grew) {
            grew = false
            for (const c of state.categories) {
              if (c.parent_id && toRemove.has(c.parent_id) && !toRemove.has(c.id)) {
                toRemove.add(c.id)
                grew = true
              }
            }
          }
          return {
            categories: state.categories.filter((c) => !toRemove.has(c.id)),
            // products kept their rows in DB (ON DELETE SET NULL); reflect that locally
            products: state.products.map((p) =>
              p.category_id && toRemove.has(p.category_id) ? { ...p, category_id: '' } : p
            ),
          }
        })
      },

      // ---- Products ----
      // Re-pull products from the DB (e.g. after a stocktake correction).
      // Applies the same active-branch quantity lens as hydrate.
      fetchPurchaseItems: async (purchaseId) => {
        const { data, error } = await supabase
          .from('purchase_items').select('*').eq('purchase_id', purchaseId).order('id')
        if (error) { console.error('fetchPurchaseItems', error.message); return [] }
        const rows: PurchaseItem[] = (data ?? []).map(mapPurchaseItem)
        // Merged by id, so a second open does not duplicate the list and the
        // purchases tab's product search finds them too.
        set((state) => {
          const known = new Set(state.purchaseItems.map((i) => i.id))
          const fresh = rows.filter((i) => !known.has(i.id))
          return fresh.length > 0 ? { purchaseItems: [...state.purchaseItems, ...fresh] } : {}
        })
        return rows
      },

      refreshProducts: async (opts) => {
        if (opts?.quiet && bulkWrites > 0) { productsBehind = true; return }
        const { branches: br, activeBranchId: active } = get()
        const multiBranch = br.length > 1 && !!active
        const lens = multiBranch ? active : null
        // 1.25 (Codex's review) — a hydrate that starts while this read is
        // out replaces the list (another branch, another shop); this answer
        // is then older than what is on screen and is dropped.
        const g = bootGen
        // 114 — only what changed since the last read. Every sale anywhere
        // brings a live-sync signal, and the whole catalogue on every one was
        // 490 KB at 10 000 products. A count that does not match means a
        // product was deleted somewhere (a delete leaves no row to find) —
        // then, and when too much changed, the full read below. 1.25: only
        // into a list of the same lens (refreshPath).
        if (refreshPath({ full: opts?.full, watermark: productsWatermark, count: get().products.length, listLens: productsLens, lens }) === 'delta') {
          const { data: d, error: e } = await supabase.rpc('list_products_changed', {
            p_since: sinceFor(productsWatermark!),
            p_branch_id: lens,
          })
          if (g !== bootGen) return
          const ans = d as { now?: string; total?: number; rows?: any[] } | null
          if (!e && ans && Array.isArray(ans.rows) && ans.rows.length <= MAX_CHANGED) {
            const changed: Product[] = ans.rows.map((r) => mapChangedRow(r, lens))
            // 196 (1.68) — the service product's rows are in the server's count: merge into the list with them
            const hidden = get().serviceRows
            const current = hidden.length > 0 ? [...get().products, ...hidden] : get().products
            const merged = mergeChanged(current, changed)
            if (merged.length === Number(ans.total)) {
              // The watermark first: the list being set is current to THIS
              // answer, and the subscriber saves the two together. (An answer
              // that lands after a newer one moves it back — the safe way.)
              if (ans.now) productsWatermark = ans.now
              // The changed rows are the server's absolute numbers (1.34: liveApplied).
              if (merged !== current) { liveApplied++; set(productsPatch(merged)) }
              return
            }
          } else if (e && opts?.quiet && isNetworkError(e)) {
            return   // offline: keep what we have; the full read would only fail slower
          }
        }
        // Same helper as hydrate(). This path used to carry its own un-paged
        // copy, so the truncation fix reached only one of the two places that
        // build the branch lens — and this is the one that runs right after a
        // stocktake, transfer, conversion or purchase. Read alongside the
        // catalogue, not after it (126).
        const { branches, activeBranchId } = get()
        const stockRead = branches.length > 1 && activeBranchId ? loadBranchStock(activeBranchId) : Promise.resolve(null)
        // Same read path as hydrate() — 068/126. Newest first, ordered in SQL.
        const { data, error } = await allRows<any>((a, b) => supabase.rpc('list_products_page', { p_offset: a, p_limit: b - a + 1 }))
        const byId = await stockRead
        if (g !== bootGen) return
        if (error && opts?.quiet) return
        if (failedT(error, 'store.x.19') || !data) return
        let rows: Product[] = data.map(mapProduct)
        if (byId) rows = rows.map((p) => ({ ...p, org_quantity: p.quantity, quantity: byId.get(p.id) ?? 0 }))
        // The meta first: the subscriber saves the list with it inside set().
        productsWatermark = newestStamp(rows)
        productsLens = byId ? activeBranchId : null
        productsLocal = false
        liveApplied++   // 1.34: the whole list, replaced from the server
        set(productsPatch(rows))   // 196 (1.68)
      },

      // ---- Branches (multi-branch) ----
      setActiveBranch: async (id) => {
        if (!get().branches.some((b) => b.id === id)) return
        saveActiveBranch(id)
        // 1.68 — the seated tables are the old branch's (Codex final #6): dropped now, read again after the hydrate
        const tables = branchSwitchTables(get().bills)
        set({ activeBranchId: id, ...tables.patch })
        await get().hydrate() // reload everything through the new branch lens
        if (tables.reload) void get().loadTables()
        const name = get().branches.find((b) => b.id === id)?.name
        if (name) toast.success(t('store.x.20', { name: name }))
      },

      addBranch: async (b) => {
        const { data, error } = await supabase
          .from('branches')
          .insert({ name: b.name.trim(), address: b.address?.trim() || null })
          .select()
          .single()
        if (failedT(error, 'store.x.21') || !data) return null
        const branch = mapBranch(data)
        set((state) => ({ branches: [...state.branches, branch] }))
        // Going 1 → 2 branches flips the whole app into branch mode; the
        // current device keeps working on the default branch's numbers.
        if (get().branches.length === 2) await get().hydrate()
        return branch
      },

      updateBranch: async (id, b) => {
        const patch: Record<string, unknown> = {}
        if (b.name !== undefined) patch.name = b.name?.trim()
        if (b.address !== undefined) patch.address = b.address?.trim() || null
        const { error } = await supabase.from('branches').update(patch).eq('id', id)
        if (failedT(error, 'store.x.22')) return
        set((state) => ({ branches: state.branches.map((x) => (x.id === id ? { ...x, ...b } : x)) }))
      },

      deleteBranch: async (id) => {
        const branch = get().branches.find((b) => b.id === id)
        if (!branch || branch.is_default) {
          toast.error(t('store.x.23'))
          return
        }
        // Refuse while the branch still holds stock — deleting would CASCADE
        // its product_stock rows and silently shrink org-wide quantities.
        const { data: stockLeft } = await supabase
          .from('product_stock').select('product_id').eq('branch_id', id).gt('quantity', 0).limit(1)
        if (stockLeft && stockLeft.length > 0) {
          toast.error(t('store.x.24'))
          return
        }
        // Transfer history is a HARDER stop than stock, and it is checked here
        // rather than left to Postgres because the database's answer is a raw
        // foreign-key violation behind the generic „ვერ მოხერხდა" toast — an
        // owner reading that has no idea what is wrong or what to do.
        //
        // `stock_transfers` is ON DELETE RESTRICT on both branch columns, on
        // purpose: a transfer names two real places, and deleting one of them
        // would leave a movement of goods from or to nowhere. The money tables
        // are SET NULL and survive; this one refuses.
        //
        // The error is CHECKED, not discarded. `stock_transfers` carries a
        // RESTRICTIVE owner-only policy (057), so for a cashier this select
        // returns an empty list rather than the truth — and an unchecked
        // `length > 0` would read that as "no transfers" and fall through to
        // the raw foreign-key error this block exists to replace. A guard that
        // cannot see is a guard that says yes.
        const { data: transfersLeft, error: transferErr } = await supabase
          .from('stock_transfers').select('id').or(`from_branch.eq.${id},to_branch.eq.${id}`).limit(1)
        if (transferErr || !transfersLeft) {
          toast.error(t('store.x.25'))
          return
        }
        if (transfersLeft.length > 0) {
          toast.error(t('store.x.26'))
          return
        }
        const { error } = await supabase.from('branches').delete().eq('id', id)
        if (failedT(error, 'store.x.27')) return
        set((state) => ({ branches: state.branches.filter((x) => x.id !== id) }))
        // If the deleted branch was this device's active one, fall back to default.
        if (get().activeBranchId === id) {
          const def = get().branches.find((b) => b.is_default) ?? get().branches[0]
          if (def) await get().setActiveBranch(def.id)
        } else if (get().branches.length === 1) {
          await get().hydrate() // back to single-branch mode (org-wide numbers)
        }
      },

      // Move stock between two branches (atomic RPC — org-wide stock-neutral).
      createStockTransfer: async (fromBranch, toBranch, items, note) => {
        const { data, error } = await supabase.rpc('create_stock_transfer', {
          p_from_branch: fromBranch,
          p_to_branch: toBranch,
          p_items: items,
          p_note: note ?? null,
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('SAME_BRANCH')) toast.error(t('store.x.28'))
          else if (m.includes('INSUFFICIENT_STOCK')) {
            const [name, avail] = m.split('INSUFFICIENT_STOCK:')[1]?.split(':') ?? []
            toast.error(t('store.x.sourceOnly', { name: name || t('common.product'), n: avail || 0 }))
          } else failedT(error, 'store.x.29')
          return null
        }
        if (!data) return null
        const transfer = mapStockTransfer((data as any).transfer)
        const savedItems = ((data as any).items ?? []) as any[]
        // The active branch's product_stock changed — re-fetch products through
        // the branch lens so the grid reflects the move immediately.
        set((state) => ({
          stockTransfers: [transfer, ...state.stockTransfers],
          stockTransferItems: [...state.stockTransferItems, ...savedItems.map(mapStockTransferItem)],
        }))
        await get().refreshProducts()
        toast.success(t('store.x.30'))
        return transfer
      },

      lookupCatalog: async (barcode) => {
        const code = barcode.trim()
        // 112 — a shop's own 2… code means nothing in another shop.
        if (!code || isStoreCode(code)) return null
        const { data, error } = await supabase
          .from('catalog_products')
          .select('barcode,name,unit,photo_url,category_hint,parent_hint')
          .eq('barcode', code)
          .maybeSingle()
        // Silent on failure: this is an optional convenience while typing a
        // product, and an error toast here would interrupt real work.
        if (error) { console.error('catalog lookup failed', error); return null }
        return (data as CatalogProduct) ?? null
      },

      lookupCatalogMany: async (barcodes) => {
        const codes = [...new Set(barcodes.map((b) => b?.trim()).filter((b): b is string => !!b && !isStoreCode(b)))]
        const out = new Map<string, CatalogProduct>()
        if (codes.length === 0) return out
        const { data, error } = await supabase
          .from('catalog_products')
          .select('barcode,name,unit,photo_url,category_hint,parent_hint')
          .in('barcode', codes)
        // Silent: this only ENRICHES a flow that works without it. An error
        // toast here would interrupt a delivery over a missing photo.
        if (error) { console.error('catalog bulk lookup failed', error); return out }
        for (const r of (data ?? []) as CatalogProduct[]) out.set(r.barcode.trim(), r)
        return out
      },

      browseCatalog: async (search, limit = 300) => {
        // Codes this shop already has — primary or extra (158). Offering them again would either
        // hit migration 054's unique index or create a twin nobody wants — and
        // the owner has no way to tell from the row which is which, so filter
        // here rather than explaining it in the UI.
        const owned = allCodes(get().products)

        let q = supabase
          .from('catalog_products')
          .select('barcode,name,unit,photo_url,category_hint,parent_hint')
          .order('name')

        const term = search.trim()
        if (term) {
          // Commas would be read as PostgREST's own OR separator.
          const safe = term.replace(/[,()]/g, ' ')
          q = q.or(`name.ilike.%${safe}%,barcode.ilike.%${safe}%`)
        }
        // Over-fetch, because `owned` is filtered client-side and would
        // otherwise eat into the page the owner actually sees.
        const { data, error } = await q.limit(limit + owned.size)
        if (failedT(error, 'store.x.31') || !data) return []

        return (data as CatalogProduct[])
          .filter((c) => !owned.has(c.barcode.trim()))
          .slice(0, limit)
      },

      importFromCatalog: async (rows) => {
        if (rows.length === 0) return 0
        const { branches, activeBranchId } = get()
        const multi = branches.length > 1 && !!activeBranchId

        // quantity 0 in BOTH shapes. The catalog describes what a thing is, not
        // how many you have, so there is no stock to place and — unlike
        // importProducts — nothing to log as a purchase. A ₾0 purchase row for
        // "goods" that were never bought would land in the expense totals and
        // be impossible to explain later.
        const bodies = rows.map((r) =>
          sanitizeProductWrite({
            name: r.name,
            barcode: r.barcode,
            unit: r.unit,
            photo_url: r.photo_url,
            category_id: r.category_id,
            purchase_price: r.purchase_price,
            sale_price: r.sale_price,
            wholesale_price: null,
            quantity: 0,
            plu_code: null,
            expiry_date: null,
          }),
        )

        const { data, error } = await supabase.from('products').insert(bodies).select()
        if (error) {
          if (isProductLimit(error)) toast.error(t('store.x.32'))
          else if ((error as { code?: string }).code === '23505')
            // 054's unique(org, barcode). browseCatalog filters owned barcodes,
            // so reaching here means the catalog moved under us — another tab
            // added the product while this dialog was open.
            toast.error(t('store.x.33'))
          else toast.error(t('store.x.34'))
          console.error('importFromCatalog', error)
          return 0
        }
        if (!data) return 0

        const mapped: Product[] = data.map(mapProduct)
        set((state) => ({ products: [...mapped, ...state.products] }))
        // multi-branch: no product_stock row is needed for a zero quantity —
        // trigger A already reads a missing row as 0.
        void multi
        return mapped.length
      },

      fillPhotosFromCatalog: async () => {
        const missing = get().products.filter(
          (p) => (!p.photo_url || p.photo_url === '') && p.barcode && p.barcode.trim()
        )
        if (!missing.length) return 0

        // One query for every barcode rather than one per product: a shop with
        // 200 unphotographed items would otherwise fire 200 requests.
        const codes = missing.map((p) => p.barcode!.trim())
        const { data, error } = await supabase
          .from('catalog_products')
          .select('barcode,photo_url')
          .in('barcode', codes)
          .not('photo_url', 'is', null)
        if (failedT(error, 'store.x.31')) return 0

        const photoByCode = new Map<string, string>(
          (data ?? []).map((r: any) => [r.barcode, r.photo_url as string])
        )
        const updates = missing
          .map((p) => ({ id: p.id, photo_url: photoByCode.get(p.barcode!.trim()) }))
          .filter((u): u is { id: string; photo_url: string } => !!u.photo_url)
        if (!updates.length) return 0

        // Written one at a time on purpose: an upsert would need every column
        // of products and a single bad row would take the whole batch with it.
        // A photo is cosmetic — a partial fill is fine, a lost product is not.
        let done = 0
        for (const u of updates) {
          const { error: e } = await supabase
            .from('products')
            .update({ photo_url: u.photo_url })
            .eq('id', u.id)
          if (e) { console.error('photo fill failed', u.id, e); continue }
          done++
        }
        set((state) => ({
          products: state.products.map((p) => {
            const hit = updates.find((u) => u.id === p.id)
            return hit ? { ...p, photo_url: hit.photo_url } : p
          }),
        }))
        return done
      },

      contributeCatalog: async () => {
        const { data, error } = await supabase.rpc('contribute_catalog')
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('NO_CONSENT')) toast.error(t('store.x.35'))
          else failedT(error, 'store.x.36')
          return 0
        }
        return Number(data) || 0
      },

      withdrawCatalog: async () => {
        const { data, error } = await supabase.rpc('withdraw_catalog')
        if (failedT(error, 'store.x.37')) return 0
        return Number(data) || 0
      },

      // Combo links and recipes (182): the dialog hands over the FULL desired set of one scope — a
      // product or a category — and set_product_links replaces both tables' rows of it at once.
      setProductLinks: async (scope, addons, ingredients) => {
        const isProduct = 'productId' in scope
        const val = isProduct ? scope.productId : scope.categoryId
        const { data, error } = await supabase.rpc('set_product_links', {
          p_product_id: isProduct ? val : null,
          p_category_id: isProduct ? null : val,
          p_addons: addons,
          p_ingredients: ingredients,
        })
        if (failedT(error, 'store.x.38') || !data) return false
        const inScope = (r: { product_id: string | null; category_id: string | null }) =>
          isProduct ? r.product_id === val : r.category_id === val
        addonReads.next()   // 1.71: a re-read already in flight predates this save
        set((state) => ({
          productAddons: [...state.productAddons.filter((a) => !inScope(a)), ...((data as any).addons ?? []).map(mapProductAddon)],
          productIngredients: [...state.productIngredients.filter((r) => !inScope(r)), ...((data as any).ingredients ?? []).map(mapProductIngredient)],
        }))
        return true
      },

      linkSupplierCode: async (supplierTin, supplierCode, productId, packSize) => {
        const tin = supplierTin.trim()
        const code = supplierCode.trim()
        if (!tin || !code || !productId) return
        // A pack size is only written when the caller has one to say: an
        // upsert that always sent 1 would erase „a block of ten" the next
        // time a line was merely re-pointed at its product.
        const pack = packSize !== undefined && Number.isFinite(packSize) && packSize > 0 ? { pack_size: packSize } : {}
        const { data, error } = await supabase
          .from('supplier_product_codes')
          .upsert(
            { supplier_tin: tin, supplier_code: code, product_id: productId, ...pack },
            { onConflict: 'org_id,supplier_tin,supplier_code' },
          )
          .select()
          .single()
        // Non-fatal: the stock is already in, the mapping is only a shortcut
        // for NEXT time. Never let it undo a completed receive.
        if (error) { console.error('supplier code link failed', error); return }
        if (!data) return
        const row = mapSupplierCode(data)
        set((state) => ({
          supplierCodes: [
            ...state.supplierCodes.filter(
              (c) => !(c.supplier_tin === row.supplier_tin && c.supplier_code === row.supplier_code)
            ),
            row,
          ],
        }))
      },

      // Open a jar: 1 unit of source becomes N units of target, and the
      // source's cost moves with it (see migration 046).
      convertProduct: async ({ sourceId, sourceQty, targetId, targetQty, note }) => {
        const { data, error } = await supabase.rpc('convert_product_lots', {   // 185: the target takes the source's batch date
          p_source_id: sourceId,
          p_source_qty: sourceQty,
          p_target_id: targetId,
          p_target_qty: targetQty,
          p_branch_id: get().activeBranchId,
          p_note: note ?? null,
          p_created_by: selectOpenShift(get())?.cashier_name ?? null,
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('SAME_PRODUCT')) toast.error(t('store.x.39'))
          else if (m.includes('BAD_QUANTITY')) toast.error(t('store.x.40'))
          else if (m.includes('PRODUCT_NOT_FOUND')) toast.error(t('store.x.41'))
          else if (m.includes('INSUFFICIENT_STOCK')) {
            const [name, avail] = m.split('INSUFFICIENT_STOCK:')[1]?.split(':') ?? []
            toast.error(t('store.x.onlyInStock', { name: name || t('common.product'), n: avail || 0 }))
          } else failedT(error, 'store.x.42')
          return null
        }
        if (!data) return null
        const conversion = mapProductConversion((data as any).conversion)
        set((state) => ({ productConversions: [conversion, ...state.productConversions] }))
        // Both products' stock (and the target's cost) changed — re-read through
        // the branch lens rather than patching two rows by hand.
        await get().refreshProducts()
        toast.success(t('store.x.43', { source_name: conversion.source_name, target_qty: conversion.target_qty, target_name: conversion.target_name, unit_cost: conversion.unit_cost.toFixed(2) }))
        return conversion
      },

      // 1.55 — one source → many parts (migration 172): a carcass into its cuts, a set into its
      // pieces. The server splits the cost by sale value; the dialog previews the same rule
      // (lib/breakdown.ts). The cache takes the server's stock and costs through refreshProducts().
      breakDownProduct: async ({ sourceId, sourceQty, parts, note, clientRef }) => {
        const gen = bootGen
        const orgId = get().currentOrg?.id
        const branchId = get().activeBranchId
        const { data, error } = await supabase.rpc('break_down_product_lots', {   // 185: the parts take the source's batch date
          p_source_id: sourceId,
          p_source_qty: sourceQty,
          p_parts: parts.map((p) => ({ product_id: p.productId, quantity: p.qty })),
          p_branch_id: branchId,
          p_note: note ?? null,
          p_created_by: selectOpenShift(get())?.cashier_name ?? null,
          p_client_ref: clientRef,
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('INSUFFICIENT_STOCK')) {
            const [name, avail] = m.split('INSUFFICIENT_STOCK:')[1]?.split(':') ?? []
            toast.error(t('store.x.onlyInStock', { name: name || t('common.product'), n: avail || 0 }))
          } else if (m.includes('PARTS_EXCEED_SOURCE')) {
            const [partsQty, sourceQ] = m.split('PARTS_EXCEED_SOURCE:')[1]?.split(':') ?? []
            toast.error(t('store.bd.exceeds', { parts: Number(partsQty) || 0, source: Number(sourceQ) || 0 }))
          } else if (m.includes('SAME_PRODUCT')) toast.error(t('store.x.39'))
          else if (m.includes('BAD_QUANTITY')) toast.error(t('store.x.40'))
          else if (m.includes('PRODUCT_NOT_FOUND')) toast.error(t('store.x.41'))
          else if (m.includes('PART_DUPLICATE')) toast.error(t('store.bd.duplicate'))
          else if (m.includes('SERVICE_NO_STOCK')) toast.error(t('store.bd.service'))
          else if (m.includes('PRICE_INVALID')) toast.error(t('store.bd.price'))
          else if (m.includes('TOO_MANY_PARTS')) toast.error(t('store.bd.tooMany'))
          else if (m.includes('NO_PARTS')) toast.error(t('store.bd.noParts'))
          else if (m.includes('OWNER_ONLY')) toast.error(t('store.bd.ownerOnly'))
          else if (m.includes('CLIENT_REF_REUSED')) toast.error(t('store.bd.refReused'))
          else failedT(error, 'store.x.42')
          return null
        }
        if (!data) return null
        const reply = data as any
        const breakdown = mapProductBreakdown(reply.breakdown, reply.parts)
        // Another shop, branch or session took the screen while this was in flight: not our cache
        // (Codex, final review — the createExchange rule).
        if (gen !== bootGen || get().currentOrg?.id !== orgId || get().activeBranchId !== branchId) return breakdown
        // a replay answers a breakdown the cache may already hold: never list it twice
        set((state) => ({ productBreakdowns: [breakdown, ...state.productBreakdowns.filter((b) => b.id !== breakdown.id)] }))
        await get().refreshProducts()
        toast.success(t('store.bd.done', { source_name: breakdown.source_name, n: breakdown.parts.length }))
        return breakdown
      },

      addProduct: async (p, opts) => {
        // 054 makes a duplicate barcode impossible; this turns the database's
        // answer into one a shopkeeper can act on. Two products answering to
        // one code meant the twin's stock never moved and read as dead stock.
        const { branches, activeBranchId } = get()
        const multi = branches.length > 1 && !!activeBranchId
        // Multi-branch: insert with qty 0, then place the stock on the ACTIVE
        // branch (a direct products.quantity insert would land on default).
        const bodyOf = (row: typeof p) => (multi ? { ...sanitizeProductWrite(row), quantity: 0 } : sanitizeProductWrite(row))
        let { data, error } = await supabase.from('products').insert(bodyOf(p)).select().single()
        // Another device numbered the same in-store code between our read and
        // our write — the same once-only renumbering as addModelVariants. A
        // barcode the owner typed or scanned is never ours to change.
        let reread = false
        if (error && opts?.renumber && isDuplicateBarcode(error) && isStoreCode(p.barcode)) {
          await get().refreshProducts({ quiet: true })
          reread = true
          p = { ...p, barcode: nextStoreCodes([...allCodes(get().products)], 1)[0] }
          ;({ data, error } = await supabase.from('products').insert(bodyOf(p)).select().single())
        }
        if (error) {
          if (isProductLimit(error)) toast.error(t('store.x.32'))
          else if (isDuplicateBarcode(error)) await toastDuplicateCode(get, error, String(p.barcode ?? '').trim(), null, !reread)
          else toast.error(t('store.x.45'))
          console.error('addProduct', error)
          return null
        }
        if (!data) return null
        let product = mapProduct(data)
        if (multi && p.quantity > 0) {
          const ok = await writeBranchQty(product.id, p.quantity, activeBranchId!)
          if (ok) product = { ...product, quantity: p.quantity }
          else toast.error(t('store.x.46'))
        }
        set((state) => ({ products: [product, ...state.products] }))
        // Log the initial stock as a purchase so it shows in expense totals.
        if (product.quantity > 0) {
          const purchase = await recordStockPurchase(
            [{ product_id: product.id, product_name: product.name, barcode: product.barcode, quantity: product.quantity, unit_cost: product.purchase_price }],
            'ხელით დამატება',
            get().activeBranchId,
            opts?.paidSource ?? null,
          )
          if (purchase) set((state) => ({ purchases: [purchase, ...state.purchases] }))
        }
        return product
      },

      assignStoreCodes: async (ids) => {
        const written: Record<string, string> = {}
        const pass = async (todo: string[]): Promise<string[]> => {
          const all = get().products
          const want = todo.filter((id) => {
            const p = all.find((x) => x.id === id)
            return !!p && !p.barcode?.trim() && !written[id]
          })
          // Numbered past every code the shop has AND every one this call
          // already wrote, so a second pass cannot reuse a first-pass code.
          const codes = nextStoreCodes([...allCodes(all), ...Object.values(written)], want.length)
          const clashed: string[] = []
          // A few at a time: a shop can have hundreds without a barcode, and
          // one request per product in a single burst is a wall of sockets.
          for (let i = 0; i < want.length; i += 8) {
            await Promise.all(want.slice(i, i + 8).map(async (id, k) => {
              const code = codes[i + k]
              // `barcode IS NULL`: another device may have given it one since
              // this list was read — that one wins, nothing is overwritten.
              const { data, error } = await supabase
                .from('products').update({ barcode: code }).eq('id', id).is('barcode', null).select('id')
              if (isDuplicateBarcode(error)) { clashed.push(id); return }
              if (error) { console.error('assignStoreCodes', error); return }
              if (data && data.length > 0) written[id] = code
            }))
          }
          return clashed
        }
        const clashed = await pass(ids)
        // Another device numbered codes at the same moment: read the shop
        // again and number the leftovers once more — once, as addModelVariants.
        if (clashed.length > 0) {
          await get().refreshProducts({ quiet: true })
          await pass(clashed)
        }
        if (Object.keys(written).length > 0) {
          set((state) => ({
            products: state.products.map((p) => (written[p.id] ? { ...p, barcode: written[p.id] } : p)),
          }))
        }
        return written
      },

      importProducts: async (products, onProgress, paidSource) => {
        if (products.length === 0) return { created: 0, failed: false }
        bulkWrites++
        try {
          const res = await insertProductBatch(get, set, products, 'Excel იმპორტი', onProgress, paidSource ?? null)
          if (!res.ok) {
            console.error('importProducts', res.error)
            // 1.44 (Codex #5): a code taken meanwhile (another device, a stale
            // copy) — the importer names the code and its Excel row instead
            const code = isDuplicateCode(res.error) ? duplicateCodeOf(res.error) : null
            if (code) return { created: res.created.length, failed: true, code }
            if (isProductLimit(res.error)) toast.error(t('store.x.32'))
            else toast.error(t('store.x.47'))
            return { created: res.created.length, failed: true }
          }
          return { created: res.products.length, failed: false }
        } finally {
          await endBulkWrite(get)
        }
      },

      restockProducts: async (items, note, onProgress, paidSource) => {
        const src = stockSourceArg(items.reduce((s, i) => s + i.quantity * i.unit_cost, 0), paidSource ?? null)   // 183
        const { activeBranchId, branches } = get()
        const multi = branches.length > 1 && !!activeBranchId
        let purchase: Purchase | null = null
        let done = 0
        bulkWrites++
        try {
        for (let start = 0; start < items.length; start += RESTOCK_CHUNK) {
          const part = items.slice(start, start + RESTOCK_CHUNK)
          const { data, error } = await supabase.rpc('restock_products_dated', {   // 185: each line may carry its pack date
            p_items: part,
            p_branch_id: activeBranchId,
            p_note: note,
            p_purchase_id: purchase?.id ?? null,
            ...(src ? { p_paid_source: src } : {}),
          })
          if (error || !data) {
            console.error('restockProducts', error)
            const m = String((error as { message?: string } | null)?.message ?? '')
            toast.error(m.includes('OWNER_ONLY') ? t('store.x.restockOwner') : t('store.x.57'))
            productsBehind = true   // the screen must not keep a number the database refused
            return { done, failed: true }
          }
          const d = data as { purchase?: unknown; items?: Record<string, unknown>[] }
          purchase = d.purchase ? mapPurchase(d.purchase) : purchase
          const byId = new Map((d.items ?? []).map((r) => [String(r.product_id), r]))
          const latest = purchase
          set((state) => ({
            products: state.products.map((prod) => {
              const r = byId.get(prod.id)
              if (!r) return prod
              return {
                ...prod,
                purchase_price: num(r.purchase_price),
                quantity: multi ? num(r.branch_quantity) : num(r.org_quantity),
                ...(prod.org_quantity !== undefined ? { org_quantity: num(r.org_quantity) } : {}),
              }
            }),
            purchases: latest ? [latest, ...state.purchases.filter((x) => x.id !== latest.id)] : state.purchases,
          }))
          done += part.length
          bulkLines += part.length
          onProgress?.(done)
        }
        return { done, failed: false }
        } finally {
          await endBulkWrite(get)
        }
      },

      preflightOpeningStock: async () => {
        const { activeBranchId } = get()
        const { error } = await supabase.rpc('import_opening_stock_dated', {
          p_items: [],
          p_branch_id: activeBranchId,
          p_after_sales_ok: false,
        })
        if (!error) return { ok: true }
        const sales = openingAfterSalesCount(error.message)
        if (sales !== null) return { ok: false, sales }
        throw error
      },

      importOpeningStock: async (items, afterSalesOk, onProgress, opts) => {
        const { activeBranchId, branches } = get()
        const multi = branches.length > 1 && !!activeBranchId
        let done = 0
        let created = 0
        const answers: OpeningAnswer[] = []
        bulkWrites++
        try {
          for (let start = 0; start < items.length; start += OPENING_CHUNK) {
            const part = items.slice(start, start + OPENING_CHUNK)
            const { data, error } = await supabase.rpc('import_opening_stock_dated', {
              p_items: part,
              p_branch_id: activeBranchId,
              p_after_sales_ok: afterSalesOk,
            })
            if (error || !data) {
              console.error('importOpeningStock', error)
              const m = String((error as { message?: string } | null)?.message ?? '')
              const refusal = parseCodeRefusal(m)
              if (refusal) {
                productsBehind = true
                return { done, created, failed: true, refusal: { ...refusal, index: start + refusal.ordinal - 1 }, ids: answers.map((a) => a.product.id) }
              }
              toast.error(m.includes('OWNER_ONLY') ? t('store.x.restockOwner') : t('store.x.openingFailed'))
              productsBehind = true   // the chunk that failed may hold rows this screen has not seen
              return { done, created, failed: true, ids: answers.map((a) => a.product.id) }
            }
            const rows = ((data as { items?: unknown[] }).items ?? []) as { product: unknown; created: boolean; branch_quantity: unknown }[]
            const byId = new Map<string, Product>()
            for (const r of rows) {
              const pr = mapProduct(r.product)
              byId.set(pr.id, multi ? { ...pr, org_quantity: pr.quantity, quantity: num(r.branch_quantity) } : pr)
              answers.push({ product: { id: pr.id, name: pr.name, barcode: pr.barcode, purchase_price: pr.purchase_price }, branch_quantity: num(r.branch_quantity) })
              if (r.created) created++
            }
            set((state) => {
              const seen = new Set<string>()
              const products = state.products.map((p) => {
                const n = byId.get(p.id)
                if (!n) return p
                seen.add(p.id)
                return { ...p, ...n }
              })
              const added = [...byId.values()].filter((p) => !seen.has(p.id))
              return { products: [...added, ...products] }
            })
            done += part.length
            bulkLines += part.length
            onProgress?.(done)
          }
          // 1.37 — the goods the shop started with, in the books once per branch
          // (the stock above is already idempotent; the money must be too).
          // 1.62 — the RPC answers every item in input order (158), so answers[i] is items[i]'s product
          const ids = answers.map((a) => a.product.id)
          if (!opts?.bookPurchase) return { done, created, failed: false, ids }
          const lines = openingPurchaseLines(items, answers)
          if (lines.length === 0) return { done, created, failed: false, ids }
          const defaultBranchId = get().branches.find((b) => b.is_default)?.id ?? null
          const booking = await bookOpeningPurchase(lines, activeBranchId, defaultBranchId)
          const booked = booking.purchase
          if (booked) set((state) => ({ purchases: [booked, ...state.purchases.filter((x) => x.id !== booked.id)] }))
          return { done, created, failed: false, booking, ids }
        } finally {
          await endBulkWrite(get)
        }
      },

      searchPurchaseLines: async (query) => {
        const q = query.trim()
        if (!q) return new Set()
        // ilike's own wildcards in the words are taken literally.
        const like = `%${q.replace(/[\\%_]/g, (c) => `\\${c}`)}%`
        const [byName, byCode] = await Promise.all([
          supabase.from('purchase_items').select('purchase_id').ilike('product_name', like).limit(5000),
          supabase.from('purchase_items').select('purchase_id').ilike('barcode', like).limit(5000),
        ])
        if (byName.error || byCode.error) { console.error('searchPurchaseLines', byName.error ?? byCode.error); return null }
        return new Set([...(byName.data ?? []), ...(byCode.data ?? [])].map((r) => String(r.purchase_id)))
      },

      addModelVariants: async (m) => {
        const modelId = m.model_id ?? crypto.randomUUID()
        const modelName = m.model_name.trim().replace(/\s+/g, ' ')
        const auto = m.variants.filter((v) => !v.barcode?.trim()).length
        const rows = (codes: string[]) => {
          let k = 0
          return m.variants.map((v) => ({
            name: variantName(modelName, v.size, v.color),
            barcode: v.barcode?.trim() || codes[k++],
            category_id: m.category_id,
            purchase_price: m.purchase_price,
            sale_price: m.sale_price,
            wholesale_price: m.wholesale_price,
            quantity: v.quantity,
            unit: 'piece' as const,
            photo_url: v.photo_url !== undefined ? v.photo_url : m.photo_url,
            plu_code: null,
            expiry_date: null,
            model_id: modelId,
            model_name: modelName,
            size: v.size?.trim() || null,
            color: v.color?.trim() || null,
            ...(m.site ?? {}),
          }))
        }
        const note = m.model_id ? 'მოდელი — ახალი ზომები' : 'ახალი მოდელი'
        const codesNow = () => nextStoreCodes([...allCodes(get().products)], auto)
        let res = await insertProductBatch(get, set, rows(codesNow()), note, undefined, m.paid_source ?? null)
        // Another device numbered the same in-store code between our read and
        // our write (054's index refused it): read the shop again, number
        // again, once. A typed supplier barcode is not ours to renumber.
        if (!res.ok && auto > 0 && isDuplicateBarcode(res.error)) {
          const dup = duplicateBarcodeOf(res.error)
          if (!dup || isStoreCode(dup)) {
            await get().refreshProducts({ quiet: true })
            res = await insertProductBatch(get, set, rows(codesNow()), note, undefined, m.paid_source ?? null)
          }
        }
        if (!res.ok) {
          const e = res.error
          if (isProductLimit(e)) toast.error(t('store.x.32'))
          else if (isDuplicateBarcode(e)) toast.error(t('store.x.44', { p1: duplicateBarcodeOf(e) ?? '' }))
          else if (String((e as { message?: string } | null)?.message ?? '').includes('uq_products_model_variant'))
            toast.error(t('store.x.48'))
          else failedT(e, 'store.x.49')
          console.error('addModelVariants', e)
          return null
        }
        return res.products
      },

      updateModel: async (modelId, patch) => {
        const sizes = get().products.filter((p) => p.model_id === modelId)
        if (sizes.length === 0) return false
        const newName = patch.model_name?.trim().replace(/\s+/g, ' ') || undefined
        const common: Record<string, unknown> = {}
        if (patch.category_id !== undefined) common.category_id = patch.category_id || null
        if (patch.sale_price !== undefined) common.sale_price = patch.sale_price
        if (patch.wholesale_price !== undefined) common.wholesale_price = patch.wholesale_price
        if (patch.photo_url !== undefined) common.photo_url = patch.photo_url
        if (patch.online !== undefined) common.online = patch.online
        if (patch.online_hidden !== undefined) common.online_hidden = patch.online_hidden
        if (newName) common.model_name = newName
        if (Object.keys(common).length > 0) {
          const { error } = await withShift(supabase.from('products').update(common).eq('model_id', modelId))
          if (failedT(error, 'store.x.50')) return false
        }
        // A photo per colour: one write per distinct photo, over the sizes
        // that take it — never the whole model, so a size's own stays.
        const photos = patch.photos ?? {}
        const byPhoto = new Map<string | null, string[]>()
        for (const [id, url] of Object.entries(photos)) {
          if (!sizes.some((v) => v.id === id)) continue
          byPhoto.set(url, [...(byPhoto.get(url) ?? []), id])
        }
        for (const [url, ids] of byPhoto) {
          const { error } = await supabase.from('products').update({ photo_url: url }).in('id', ids)
          if (failedT(error, 'store.x.51')) return false
        }
        // Each size's own name carries the model's („Air · 42 · შავი"), and
        // the names differ — one write per size that actually changes.
        const renamed = new Map<string, string>()
        if (newName) {
          const results = await Promise.all(sizes.map(async (v) => {
            const name = variantName(newName, v.size, v.color)
            if (name === v.name) return true
            const { error } = await supabase.from('products').update({ name }).eq('id', v.id)
            if (error) { console.error('updateModel rename', error); return false }
            renamed.set(v.id, name)
            return true
          }))
          if (results.some((ok) => !ok)) toast.error(t('store.x.52'))
        }
        set((state) => ({
          products: state.products.map((p) => (p.model_id !== modelId ? p : {
            ...p,
            ...(patch.category_id !== undefined ? { category_id: patch.category_id } : {}),
            ...(patch.sale_price !== undefined ? { sale_price: patch.sale_price } : {}),
            ...(patch.wholesale_price !== undefined ? { wholesale_price: patch.wholesale_price } : {}),
            ...(patch.photo_url !== undefined ? { photo_url: patch.photo_url } : {}),
            ...(patch.online !== undefined ? { online: patch.online } : {}),
            ...(patch.online_hidden !== undefined ? { online_hidden: patch.online_hidden } : {}),
            ...(p.id in photos ? { photo_url: photos[p.id] } : {}),
            ...(newName ? { model_name: newName } : {}),
            ...(renamed.has(p.id) ? { name: renamed.get(p.id)! } : {}),
          })),
        }))
        return true
      },

      deleteModel: async (modelId) => {
        // One statement, so the model goes whole or not at all. Sale, purchase
        // and stocktake lines keep the size's name (their link is SET NULL),
        // like a single product's delete. `select` counts what RLS let go.
        const { data, error } = await supabase.from('products').delete().eq('model_id', modelId).select('id')
        if (failedT(error, 'store.x.53')) return false
        const gone = new Set((data ?? []).map((r: { id: string }) => r.id))
        if (gone.size === 0) { toast.error(t('store.x.54')); return false }
        set((state) => ({
          products: state.products.filter((p) => !gone.has(p.id)),
          productAddons: state.productAddons.filter((a) => !gone.has(a.product_id) && !gone.has(a.addon_id)),
          productIngredients: state.productIngredients.filter((r) => !gone.has(r.product_id) && !gone.has(r.ingredient_id)),
          supplierCodes: state.supplierCodes.filter((c) => !gone.has(c.product_id)),
        }))
        return true
      },

      addProductCodes: async (items) => {
        if (items.length === 0) return { ok: true, products: [] }
        if (typeof navigator !== 'undefined' && !navigator.onLine) return { ok: false, refusal: null, offline: true }
        const done: Product[] = []
        for (let start = 0; start < items.length; start += 1000) {
          const part = items.slice(start, start + 1000)
          const { data, error } = await supabase.rpc('add_product_codes', { p_items: part })
          if (error || !data) {
            console.error('addProductCodes', error)
            if (isNetworkError(error)) return { ok: false, refusal: null, offline: true }
            const r = parseCodeRefusal((error as { message?: string } | null)?.message)
            return { ok: false, refusal: r ? { ...r, ordinal: start + r.ordinal } : null, offline: false }
          }
          const rows = ((data as { products?: unknown[] }).products ?? []).map(mapProduct)
          const byId = new Map(rows.map((p) => [p.id, p]))
          set((state) => ({
            products: state.products.map((prod) => {
              const n = byId.get(prod.id)
              return n ? { ...prod, barcode: n.barcode, extra_barcodes: n.extra_barcodes, updated_at: n.updated_at } : prod
            }),
          }))
          done.push(...rows)
        }
        return { ok: true, products: done }
      },

      editProductFitment: async (rows, onProgress) => {
        if (rows.length === 0) return { ok: true, done: 0 }
        if (typeof navigator !== 'undefined' && !navigator.onLine) return { ok: false, done: 0, failedIndex: 0, refusal: null, offline: true }
        let done = 0
        for (let start = 0; start < rows.length; start += FIT_CHUNK) {
          const part = rows.slice(start, start + FIT_CHUNK)
          const { data, error } = await supabase.rpc('edit_product_fitment', { p_rows: part })
          if (error || !data) {
            console.error('editProductFitment', error)
            const offline = isNetworkError(error)
            const refusal = offline ? null : parseFitRefusal(String((error as { message?: string } | null)?.message ?? ''))
            return { ok: false, done, failedIndex: start, refusal, offline }
          }
          // only the written rows come back (a no-op row is not written); only these three fields are theirs
          const written = new Map(((data as { rows?: { id: string; fits: unknown; oem_codes: unknown; updated_at: string }[] }).rows ?? [])
            .map((r) => [r.id, r]))
          if (written.size > 0) {
            set((state) => ({
              products: state.products.map((prod) => {
                const n = written.get(prod.id)
                return n ? { ...prod, fits: fitsOf(n), oem_codes: oemOf(n), updated_at: n.updated_at } : prod
              }),
            }))
          }
          done += part.length
          onProgress?.(done)
        }
        return { ok: true, done }
      },

      updateProduct: async (id, p, stockIn) => {
        const { branches, activeBranchId } = get()
        const multi = branches.length > 1 && !!activeBranchId
        // A restock ('add' mode with units) is a DELTA the server applies, so
        // the quantity and the blended cost the dialog worked out are dropped
        // here: both were computed from a read that may be minutes old, and a
        // till selling in between would be undone by writing them (075).
        const isRestock = !!stockIn && stockIn.quantity > 0
        let patch: Record<string, unknown> = { ...sanitizeProductWrite(p) }
        // 1.61: the switch moves only through set_track_expiry (186 refuses a direct write), and
        // expiry_date is the batches' cache (184) — an edit never writes either
        delete patch.track_expiry
        delete patch.expiry_date
        // 1.62 (187): the card's lists go through editProductFitment as additions and removals — a whole list
        // sent here would erase what another device added meanwhile (Codex's design review #8)
        delete patch.fits
        delete patch.oem_codes
        if (isRestock) {
          delete patch.quantity
          delete patch.purchase_price
        } else if (multi && patch.quantity !== undefined) {
          // quantity is branch-local in multi mode — route it to product_stock
          const qty = Number(patch.quantity)
          delete patch.quantity
          const ok = await writeBranchQty(id, qty, activeBranchId!)
          if (!ok) { toast.error(t('store.x.55')); return false }
        }
        if (Object.keys(patch).length > 0) {
          const { error } = await withShift(supabase.from('products').update(patch).eq('id', id))
          // Renaming a barcode onto one that exists is the same collision from
          // the other direction (054).
          if (isDuplicateBarcode(error)) {
            await toastDuplicateCode(get, error, String(p.barcode ?? '').trim(), id)
            return false
          }
          if (failedT(error, 'store.x.56')) return false
        }
        // The edited fields land locally; on a restock the two the server owns
        // are left to its answer below.
        const localPatch = isRestock
          ? (({ quantity: _q, purchase_price: _c, expiry_date: _e, track_expiry: _t, fits: _f, oem_codes: _o, ...rest }) => rest)(p as Product)
          : (({ expiry_date: _e, track_expiry: _t, fits: _f, oem_codes: _o, ...rest }) => rest)(p as Product)   // 1.61: the database's
        set((state) => ({
          products: state.products.map((prod) => (prod.id === id ? { ...prod, ...localPatch } : prod)),
        }))
        // The units and their money, in one transaction at the active branch.
        if (isRestock) {
          const res = await restockProduct(id, stockIn!.quantity, stockIn!.unit_cost, activeBranchId, 'ნაშთის შევსება', stockIn!.paid_source ?? null, stockIn!.expiry_date ?? null)
          if (!res) {
            toast.error(t('store.x.57'))
            // The screen must not keep a number the database refused.
            await get().refreshProducts()
            return false
          }
          set((state) => ({
            products: state.products.map((prod) => (prod.id === id
              ? {
                  ...prod,
                  purchase_price: res.purchasePrice,
                  quantity: multi ? res.branchQty : res.orgQty,
                  ...(prod.org_quantity !== undefined ? { org_quantity: res.orgQty } : {}),
                }
              : prod)),
            purchases: res.purchase ? [res.purchase, ...state.purchases] : state.purchases,
          }))
        }
        return true
      },

      writeOffStock: async (productId, quantity, reason, note) => {
        // The stock movement and the expense are ONE transaction in the
        // database. Doing it here in two calls would let a dropped connection
        // leave the shelf short with nothing to show where the money went —
        // which is exactly the state a write-off exists to prevent.
        const { data, error } = await supabase.rpc('write_off_stock', {
          p_product_id: productId,
          p_quantity: quantity,
          p_reason: reason,
          p_branch_id: get().activeBranchId,
          p_note: note?.trim() || null,
        })
        if (error || !data) {
          const msg = String((error as { message?: string } | null)?.message ?? '')
          if (msg.includes('STOCK_INSUFFICIENT')) toast.error(t('store.x.58'))
          else if (msg.includes('QUANTITY_INVALID')) toast.error(t('store.x.59'))
          else if (msg.includes('OWNER_ONLY')) toast.error(t('store.x.60'))
          else failedT(error, 'store.x.61')
          return false
        }
        const d = data as Record<string, unknown>
        const multi = get().branches.length > 1 && !!get().activeBranchId
        const expense = d.expense ? mapExpense(d.expense) : null
        set((state) => ({
          products: state.products.map((p) => (p.id === productId
            ? {
                ...p,
                quantity: multi ? num(d.branch_quantity) : num(d.org_quantity),
                ...(p.org_quantity !== undefined ? { org_quantity: num(d.org_quantity) } : {}),
              }
            : p)),
          expenses: expense ? [expense, ...state.expenses] : state.expenses,
        }))
        return true
      },

      deleteProduct: async (id) => {
        const { error } = await supabase.from('products').delete().eq('id', id)
        if (error && (error.message ?? '').includes('PRODUCT_ON_OPEN_BILL')) { toast.error(t('tables.productOnBill')); return }
        if (failedT(error, 'store.x.62')) return
        set((state) => ({
          products: state.products.filter((p) => p.id !== id),
          // ON DELETE CASCADE already cleared these in the DB — drop them
          // locally too, in BOTH directions (the product could be a trigger
          // or somebody else's add-on).
          productAddons: state.productAddons.filter(
            (a) => a.product_id !== id && a.addon_id !== id
          ),
          productIngredients: state.productIngredients.filter((r) => r.product_id !== id && r.ingredient_id !== id),
          // ON DELETE CASCADE cleared these server-side; mirror it locally.
          supplierCodes: state.supplierCodes.filter((c) => c.product_id !== id),
        }))
      },

      // ---- Sales ----
      // Atomic: sale + line items + stock change happen in one DB transaction
      // (Postgres function create_sale). is_fiscal sales start as 'pending'.
      // When the network is down, non-fiscal sales are queued locally and
      // replayed on reconnect (see syncOfflineSales). ნისია too since
      // 2026-09-26: create_sale ADDS the debt (`balance + owed`) and checks no
      // limit, so a replay hours later lands exactly as it would have then.
      // Only bonus stays online-only — its balance IS checked at replay.
      addSale: async (sale, items, opts) => {
        const openShiftRow = selectOpenShift(get())
        // One id per attempt, minted BEFORE the call and sent with it. The
        // online path used to send none, so a request that committed while
        // its reply was lost (the case 053 was written for) came back as a
        // second sale when the same cart was queued or retried. Now the server
        // answers the same sale for the same id (audit, 2026-09-11).
        // A QR payment arrives with its Keepz order id and that IS the sale's
        // id (082): one payment cannot become two sales, and the accountant
        // can meet qr_payments and sales on one key.
        // R08: a press that was told „not saved" and is pressed again with
        // the same cart is the same attempt — it keeps its id, so a first
        // request that did reach the server is answered, not doubled.
        const prepaid = opts?.prepaid ?? null
        const attemptKey = saleAttemptKey(sale, items, prepaid ? { id: prepaid.id, amount: prepaid.amount } : null)
        const qid = sale.client_ref ?? reuseAttempt(unsavedAttempt, attemptKey, Date.now()) ?? crypto.randomUUID()
        unsavedAttempt = null
        const rpcPayload = {
          p_client_ref: qid,
          p_branch_id: get().activeBranchId,
          p_total: sale.total,
          p_payment_method: sale.payment_method,
          p_items_count: sale.items_count,
          p_is_fiscal: sale.is_fiscal,
          p_items: items,
          p_type: 'sale',
          p_discount: sale.discount ?? 0,
          p_customer_id: sale.customer_id ?? null,
          p_customer_name: sale.customer_name ?? null,
          p_paid: sale.paid ?? null,
          p_shift_id: openShiftRow?.id ?? null,
          p_cashier_id: openShiftRow?.cashier_id ?? null,
          p_cashier_name: openShiftRow?.cashier_name ?? null,
          p_card_provider: sale.card_provider ?? null,
          // 084: the owner's PIN when the discount is above the cashier's
          // ceiling — compared on the server, never trusted from here.
          p_override_pin: sale.override_pin ?? null,
          // 085: bonus spent on this sale — the server checks the balance and
          // the shop's loyalty switch; the percent earned is never sent.
          p_bonus_redeemed: sale.bonus_redeemed ?? 0,
          // 092: what the customer handed over — informational; the server
          // keeps it only for a cash sale it covers.
          p_cash_received: sale.cash_received ?? null,
          // 103: a split. Both NULL means „single rail", which is the branch
          // of create_sale that behaves exactly as it did before — so an
          // offline sale queued by an older build still replays correctly.
          p_paid_cash: sale.paid_cash ?? null,
          p_paid_card: sale.paid_card ?? null,
          // 168: the change rounding (lib/change-tetri) — sent only when a chip
          // was pressed, so a till that never offers it sends what 1.52 sent.
          ...(sale.change_kept ? { p_change_kept: sale.change_kept } : {}),
          // 175: the gift card (its whole code — the id alone never spends a bearer card) or the
          // deposit; sent only when there is one, so every other sale is what 1.56 sent
          ...(prepaid ? { p_prepaid: [{ id: prepaid.id, amount: prepaid.amount, code: prepaid.code ?? null }] } : {}),
        }

        // Queue an offline sale locally: optimistic Sale/items in state (temp
        // ids), full replay payload in localStorage. The cart UI already
        // blocks bonus + fiscal offline; this is the hard backstop.
        //
        // ნისია offline (owner, 2026-09-26): allowed, because the server's
        // rule is additive — create_sale does `balance + (total − paid)` and
        // has no credit limit to refuse on — so nothing forks. What IS owed is
        // what the SERVER will derive: the part of the total the cash and card
        // rails do not cover, whatever method the cart named.
        const owedOffline = (() => {
          if (sale.paid_cash != null || sale.paid_card != null) {
            return Math.max(0, sale.total - (sale.paid_cash ?? 0) - (sale.paid_card ?? 0))
          }
          return sale.payment_method === 'credit' ? Math.max(0, sale.total - (sale.paid ?? 0)) : 0
        })()
        const queueOffline = (): Sale | null => {
          // 1.57 — a prepaid's balance is the server's: never spent offline
          if (prepaid) {
            toast.error(t('pp.offline'))
            return null
          }
          // 1.58 — a group price is accepted online only (179): never queued, the cart is kept
          if (opts?.groupPriced) {
            toast.error(t('store.groupOffline'), { duration: 8000 })
            return null
          }
          if ((sale.bonus_redeemed ?? 0) > 0) {
            toast.error(t('store.bonusOffline'))
            return null
          }
          if (owedOffline > 0.005 && !sale.customer_id) {
            toast.error(t('store.creditNeedsCustomer'))
            return null
          }
          const nowIso = new Date().toISOString()
          const queued = enqueueOfflineSale({
            qid,
            org_id: get().currentOrg?.id ?? '',
            created_at: nowIso,
            total: sale.total,
            // Replayed sales physically happened — never bounce them on stock.
            // p_client_ref makes the replay idempotent: the server returns the
            // sale it already recorded instead of creating a second one (053).
            // 140: and it says WHEN it happened (p_sold_at).
            payload: offlinePayload(rpcPayload, nowIso),
          })
          // R08: the device would not keep it (storage full or blocked). It is
          // saved NOWHERE — say so, keep the cart, and let the next press of
          // this cart reuse the id. It used to announce „saved" regardless.
          if (!queued) {
            unsavedAttempt = { qid, key: attemptKey, at: Date.now() }
            toast.error(t('store.offlineSaveFailed'), { duration: 12000 })
            return null
          }
          const localSale: Sale = {
            id: qid,
            total: sale.total,
            discount: sale.discount ?? 0,
            cash_received: sale.payment_method === 'cash' ? sale.cash_received ?? null : null,
            // 168: what create_sale will store on replay (the till never offers what it would zero)
            change_kept: sale.payment_method === 'cash' ? sale.change_kept ?? 0 : 0,
            payment_method: sale.payment_method,
            // Mirrors what create_sale will derive on replay: a single-rail
            // ნისია's down payment is cash; a split names its own rails.
            paid_cash: sale.paid_cash ?? (sale.payment_method === 'cash' ? sale.total
              : sale.payment_method === 'credit' ? Math.min(Math.max(sale.paid ?? 0, 0), sale.total) : 0),
            paid_card: sale.paid_card ?? (sale.payment_method === 'card' ? sale.total : 0),
            // 085: the customer rides along so the replay earns their bonus;
            // the balance itself refreshes with the next load.
            customer_id: sale.customer_id ?? null,
            customer_name: sale.customer_name ?? null,
            paid: Math.round((sale.total - owedOffline) * 100) / 100,
            shift_id: openShiftRow?.id ?? null,
            cashier_id: openShiftRow?.cashier_id ?? null,
            cashier_name: openShiftRow?.cashier_name ?? null,
            items_count: sale.items_count,
            created_at: nowIso,
            type: 'sale',
            reversal_of: null,
            card_provider: sale.card_provider ?? null,
            is_fiscal: false,
            fiscal_status: 'none',
            fiscal_id: null,
          }
          // 1.25 — this decrement exists only on this device until the queue
          // drains; if the server rejects the sale no delta will ever bring
          // the truth back, so the next online boot reads everything.
          productsLocal = true
          set((state) => ({
            sales: [localSale, ...state.sales],
            saleItems: [
              ...state.saleItems,
              ...items.map((i, idx) => ({
                id: `${qid}-${idx}`,
                sale_id: qid,
                product_id: i.product_id,
                product_name: i.product_name,
                barcode: i.barcode,
                quantity: i.quantity,
                unit_price: i.unit_price,
                total_price: i.total_price,
                unit_cost: i.product_id
                  ? state.products.find(p => p.id === i.product_id)?.purchase_price ?? 0
                  : 0,
              })),
            ],
            // 182: and what the cart's recipes take (an estimate; the replay writes the true lines)
            products: offlineShelf(state, items),
            // The debt shows on the customer at once. The full sync ends in
            // hydrate(), which replaces this with the server's balance — so it
            // is never counted twice.
            customers: owedOffline > 0.005 && sale.customer_id
              ? state.customers.map((c) => (c.id === sale.customer_id
                ? { ...c, balance: Math.round((c.balance + owedOffline) * 100) / 100 } : c))
              : state.customers,
            offlinePending: loadOfflineQueue().length,
          }))
          toast.warning(t('store.savedOffline'), { duration: 5000 })
          return localSale
        }

        // B2 (152): an online order's sale is never queued. create_sale refuses
        // its replay (and a refused replay would block the shift's close); its
        // client_ref IS the order, so pressing again after a lost answer gets
        // the sale the server already made — never a second one.
        const onlineOrderSale = qid.startsWith('online:')
        // 1.66 (192) — a table's bill closes only with the server (its trigger checks the bill): never the queue
        const tableSale = qid.startsWith('table:')
        if (!isBrowserOnline()) {
          if (onlineOrderSale) { toast.error(t('oo.sell.offline')); return null }
          if (tableSale) { toast.error(t('tables.payOffline')); return null }
          return queueOffline()
        }

        // 182: what was on screen when this left — the reply moves THIS shelf only (Codex #6)
        const stamp: ShelfStamp = { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId, applied: liveApplied }
        const { data, error } = await supabase.rpc('create_sale', rpcPayload)
        if (error) {
          // Network died mid-request — treat exactly like starting offline.
          if (isNetworkError(error)) {
            if (onlineOrderSale) { toast.error(t('oo.sell.lost'), { duration: 8000 }); return null }
            // the ref IS the bill at its revision: the next press of the same bill replays, never doubles
            if (tableSale) { toast.error(t('tables.payLost'), { duration: 10000 }); return null }
            // 1.57 (Codex #6) — a prepaid or a bonus is online-only: the request may have
            // committed. Keep THIS ref for the next press of the same cart; the server then
            // answers the first sale instead of spending the card twice.
            if (onlineOnlySale(sale, prepaid, opts?.groupPriced === true)) {
              unsavedAttempt = { qid, key: attemptKey, at: Date.now() }
              toast.error(t('pp.netLost'), { duration: 10000 })
              return null
            }
            return queueOffline()
          }
          // Server rejected an oversell (stock changed under us) — show real stock.
          const m = (error as { message?: string }).message ?? ''
          // 1.57 — a gift card or a deposit refused (short, expired, its code, the rails…)
          const ppErr = prepaidErrorKey(m)
          if (ppErr) {
            toast.error(t(ppErr.key, ppErr.vars), { duration: 8000 })
            return null
          }
          if (m.includes('INSUFFICIENT_STOCK')) {
            const [name, avail] = m.split('INSUFFICIENT_STOCK:')[1]?.split(':') ?? []
            toast.error(t('store.insufficient', { name: name || t('common.product'), n: avail || 0 }))
            // Resync product stock (branch-lens aware) so the cart reflects reality.
            await get().refreshProducts()
            return null
          }
          // 090: a line's price is not the product's price any more — the
          // owner changed it under the till (or a held cart aged). Refresh
          // the products; the POS page re-prices its lines from them and
          // the cashier presses sell again with the true total in view.
          const pm = parsePriceMismatch(m)
          if (pm) {
            toast.error(t('store.priceChanged', { name: pm.name || t('common.product'), from: `₾${pm.sent.toFixed(2)}`, to: `₾${pm.list.toFixed(2)}` }), { duration: 7000 })
            // 1.58: the products, the price groups and the customers — any of them changed elsewhere
            await get().refreshLive([...PRICE_REFRESH_TABLES])
            return null
          }
          // 1.63 (190) — a line at the club price the server would not allow: the club switched off, or this
          // customer is no member right now. The cart drops the club price at once (the server said so), re-reads
          // what changed and re-prices; the cashier sees the new total before another press (Codex #I4).
          const club = afterClubRefusal(m)
          if (club) {
            toast.error(t(club.message), { duration: 8000 })
            const cid = sale.customer_id
            if (cid) set((st) => {
              const s0 = st.clubStatuses[cid]
              return s0 ? { clubStatuses: { ...st.clubStatuses, [cid]: { ...s0, member: false, until: null } } } : {}
            })
            await get().refreshLive(club.refresh)
            return null
          }
          if (m.includes('PIN_LOCKED')) {
            // 115 — five wrong money PINs in 15 minutes (or twenty in a day)
            // lock the override for this login, the right PIN included.
            toast.error(t('store.ownerPinLocked'), { duration: 8000 })
            return null
          }
          if (m.includes('DISCOUNT_LIMIT')) {
            const limit = m.split('DISCOUNT_LIMIT:')[1]?.split(/[^0-9.]/)[0]
            // Heal first. The till decides whether to ask for the owner's PIN
            // from ITS copy of the cashier's ceiling; if the owner lowered it
            // on another device, that copy still said „within limit", so the
            // PIN prompt never appeared and every retry sent the same refused
            // request until a reload (audit, 2026-09-23). The server has just
            // named the real ceiling — take it, and the next press asks for
            // the PIN by itself.
            const ceiling = limit ? Number(limit) : NaN
            const cid = openShiftRow?.cashier_id
            if (cid && Number.isFinite(ceiling)) {
              set((st) => ({ cashiers: st.cashiers.map((c) => (c.id === cid ? { ...c, max_discount_pct: ceiling } : c)) }))
            }
            toast.error(t('store.discountLimit', { limit: limit ?? '?' }))
            return null
          }
          if (m.includes('BONUS_INSUFFICIENT')) {
            const have = m.split('BONUS_INSUFFICIENT:')[1]?.split(/[^0-9.]/)[0]
            toast.error(t('store.bonusInsufficient', { sum: `₾${have ?? '0'}` }))
            await get().refreshCustomers()
            return null
          }
          if (m.includes('PROMO_NOT_ACTIVE') || m.includes('PROMO_PRICE_BELOW') || m.includes('PROMO_PRODUCT_MISMATCH')) {
            const name = m.split(/PROMO_[A-Z_]+:/)[1]?.split(':')[0]
            toast.error(t('store.promoChanged', { name: name ?? '?' }))
            await get().refreshPromotions()
            return null
          }
          if (m.includes('BONUS_DISABLED')) {
            toast.error(t('store.bonusDisabled'))
            return null
          }
          const onlineKey = onlineErrorKey(m)
          if (onlineKey) {
            toast.error(t(onlineKey), { duration: 7000 })
            return null
          }
          // 1.66 (192) — the bill changed, closed or does not match: say so and read the bills again. 196 (1.68): the
          // service check raises P0010 with Georgian words for an old till and its code in the hint for this one.
          const hint = (error as { hint?: string }).hint ?? ''
          const tableKey = tableSale ? (tableErrorKey(m) ?? tableErrorKey(hint)) : null
          if (tableKey) {
            toast.error(t(tableKey), { duration: 8000 })
            void get().loadTables()
            // the till may not know the service product yet (made on another device a moment ago)
            if (hint === 'TABLE_SERVICE_MISMATCH') void get().refreshProducts({ quiet: true })
            return null
          }
          failedT(error, 'store.saleFailed')
          return null
        }
        if (!data) return null
        // 115 — a wrong money PIN: refused, counted by the server, and NOT a
        // sale. (It is returned rather than raised because a raise would have
        // rolled the count back.)
        const refusal = data as { refused?: string; limit?: number }
        if (refusal.refused) {
          const ceiling = Number(refusal.limit)
          const cid = openShiftRow?.cashier_id
          if (cid && Number.isFinite(ceiling)) {
            set((st) => ({ cashiers: st.cashiers.map((c) => (c.id === cid ? { ...c, max_discount_pct: ceiling } : c)) }))
          }
          toast.error(t('store.ownerPinWrong'), { duration: 6000 })
          return null
        }

        const saleRow = (data as any).sale
        const savedItems = ((data as any).items ?? []) as any[]
        const newSale = mapSale(saleRow)
        // Unpaid remainder of a credit sale increases the customer's debt locally.
        // 087: only a credit sale owes — same rule as create_sale (F1).
        // Read off the SERVER's row, not the request: since 103 the method
        // sent is only a hint. ₾2 cash + ₾3 card on ₾6 goes up as 'mixed' and
        // comes back 'credit' with paid ₾5 — the server added ₾1 of debt, and
        // reading `sale` here added none, so the customer's balance on this
        // till stayed ₾1 short until the next reload (audit, 2026-09-23;
        // no such sale had happened yet).
        const debtAdded = newSale.customer_id && newSale.payment_method === 'credit' ? newSale.total - (newSale.paid ?? newSale.total) : 0
        // 085: the bonus balance comes back from the server — the one that
        // booked the earn and the redeem — never recomputed here.
        const bonusRaw = (data as any).bonus_balance
        const bonusBal = bonusRaw == null ? null : Number(bonusRaw)
        // B2 (152): a sale answered as a replay moved its stock and its debt when it was first
        // made — maybe by another till, maybe already on screen through a live refresh — so both
        // are read again, never moved twice. 1.57 (Codex's final review #3): for EVERY replay, not
        // only an online order's — a lost reply and a second press („pp.netLost") is a replay too.
        const replayed = (data as any).replayed === true
        // 182: the shelf moves by the lines the SERVER wrote — the drink's own and the ₾0 ones it took —
        // never by this till's copy of the recipe, which another device may have changed; a replay, or a
        // live read applied meanwhile, reads the shelf again instead (Codex's design review #6)
        const shelf = replyShelfPlan(stamp, { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId, applied: liveApplied }, replayed)
        if (shelf === 'refresh') void get().refreshProducts({ quiet: true })
        if (replayed && sale.customer_id) void get().refreshLive(['customers', 'customer_payments'])
        const replyLines = savedItems.map(mapSaleItem)
        set((state) => ({
          sales: prependUnique([newSale], state.sales),
          saleItems: appendUnique(state.saleItems, replyLines),
          products: shelf === 'delta' ? soldFromShelf(state.products, replyLines, false) : state.products,
          // 1.63 — his month changed with this sale: the next pick asks the server again (Codex final #3)
          clubStatuses: withoutStatus(state.clubStatuses, sale.customer_id),
          customers: sale.customer_id && ((debtAdded !== 0 && !replayed) || bonusBal != null)
            ? state.customers.map((c) => (c.id === sale.customer_id
                ? { ...c, balance: c.balance + (replayed ? 0 : debtAdded), ...(bonusBal != null ? { bonus_balance: bonusBal } : {}) }
                : c))
            : state.customers,
        }))
        // 1.66 (192) — the bill this sale closed leaves the device's list at once; the read confirms it
        const paidBill = tableSale ? parseTableRef(qid) : null
        if (paidBill) {
          tableGone.set(paidBill.billId, ++tableSeq)
          tableReads.next()
          set((state) => {
            if (!state.bills || !state.bills[paidBill.billId]) return {}
            const next = { ...state.bills }
            delete next[paidBill.billId]
            return { bills: next }
          })
          void get().loadTables()
        }
        // 175 — the card's balance after this sale, for the receipt (never stored by the server)
        const pp = (data as any).prepaid
        // 1.66 — a replay is said: a table's till that lost the answer must not print a fresh receipt for it
        const out = { ...newSale, replayed }
        return pp
          ? { ...out, prepaid_after: { balance: Number(pp.balance), code_tail: String(pp.code_tail ?? ''), expires_at: pp.expires_at ?? null } }
          : out
      },

      // Return / refund: restores stock (+qty) via the same atomic function.
      addReturn: async (original, items, session) => {
        const total = items.reduce((s, i) => s + i.total_price, 0)
        const itemsCount = itemsCountOf(items)
        const openShiftRow = selectOpenShift(get())
        // 1.47 (Codex's audit): one id per return attempt. Pressed again after
        // „not saved", the same return keeps its id, so one whose answer was
        // lost after the commit is answered by create_sale, not made twice.
        const attemptKey = returnAttemptKey(original.id, items, session ?? null)
        const qid = reuseAttempt(unsavedReturn, attemptKey, Date.now()) ?? `return:${crypto.randomUUID()}`
        unsavedReturn = null
        const stamp: ShelfStamp = { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId, applied: liveApplied }   // 182
        const { data, error } = await supabase.rpc('create_sale', {
          p_client_ref: qid,
          // returned goods physically land where the return happens — the
          // ACTIVE branch, not necessarily the original sale's branch
          p_branch_id: get().activeBranchId,
          p_total: total,
          p_payment_method: original.payment_method,
          p_items_count: itemsCount,
          p_is_fiscal: original.is_fiscal,
          p_items: items,
          p_type: 'return',
          p_reversal_of: original.id,
          // A return of a credit sale lowers that customer's debt.
          p_customer_id: original.customer_id ?? null,
          p_customer_name: original.customer_name ?? null,
          p_shift_id: openShiftRow?.id ?? null,
          p_cashier_id: openShiftRow?.cashier_id ?? null,
          p_cashier_name: openShiftRow?.cashier_name ?? null,
          p_card_provider: original.card_provider ?? null,
          // 1.36 — a MIXED sale's return names its rails (the cumulative rule of
          // lib/exchange.ts). Without them create_sale recorded neither, and the
          // drawer would not see the cash handed back. Never happened (no mixed
          // return exists); cash, card and credit returns send exactly what they did.
          // 175 — a sale paid (partly) from a prepaid: the server sets all three rails by
          // the cumulative rule and holds the till to them (Codex #7); the share goes back
          // to the card by itself, so only cash and card are sent.
          ...((original.paid_prepaid ?? 0) > 0 ? (() => {
            const prior = get().sales.filter((s) => s.type === 'return' && s.reversal_of === original.id)
              .map((s) => ({ total: s.total, paid_cash: s.paid_cash ?? 0, paid_prepaid: s.paid_prepaid ?? 0 }))
            const r = prepaidReturnRails(original, prior, total)
            return { p_paid_cash: r.cash, p_paid_card: r.card }
          })() : original.payment_method === 'mixed' ? (() => {
            const prior = get().sales.filter((s) => s.type === 'return' && s.reversal_of === original.id)
              .map((s) => ({ total: s.total, paid_cash: s.paid_cash ?? 0 }))
            const r = returnRails(original, prior, total)
            return { p_paid_cash: r.cash, p_paid_card: r.card }
          })() : {}),
        })
        if (error || !data) unsavedReturn = { qid, key: attemptKey, at: Date.now() }
        // 128: a unit named that this sale never sold, or one already back
        const serialMsg = String((error as { message?: string } | null)?.message ?? '')
        if (serialMsg.includes('SERIAL_NOT_IN_SALE')) { toast.error(t('acc.ret.err.serialNotInSale')); return null }
        if (serialMsg.includes('SERIAL_ALREADY_RETURNED')) { toast.error(t('acc.ret.err.serialReturned')); return null }
        // 175 — the rails this till sent differ from the server's (its returns list was behind):
        // the server's figures are shown and the returns read again; the next press agrees
        const ppErr = prepaidErrorKey(serialMsg)
        if (ppErr) { toast.error(t(ppErr.key, ppErr.vars), { duration: 9000 }); void get().refreshLive(['sales']); return null }
        if (failedT(error, 'store.x.63') || !data) return null

        const saleRow = (data as any).sale
        const savedItems = ((data as any).items ?? []) as any[]
        const newReturn = mapSale(saleRow)
        // 1.47: a return answered as a replay was booked by the first press —
        // its stock and the debt it lowered may already be on screen (a live
        // refresh came in meanwhile), so both are read again, never moved twice
        // (the 152 rule of sales). A replay's answer carries no bonus balance.
        const replayed = (data as any).replayed === true
        // 182: back on the shelf by the reply's lines — the drinks and the cups the server gave back with them
        const shelf = replyShelfPlan(stamp, { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId, applied: liveApplied }, replayed)
        if (shelf === 'refresh') void get().refreshProducts({ quiet: true })
        if (replayed && original.customer_id) void get().refreshLive(['customers', 'customer_payments'])
        const replyLines = savedItems.map(mapSaleItem)
        // 1.57 (Codex's final review #4) — the card the return went back to, its balance and its
        // expiry now (an expired card got the return day + 30): for the till to say, never stored
        const ppBack = (data as any).prepaid
        if (ppBack) newReturn.prepaid_after = { balance: Number(ppBack.balance), code_tail: String(ppBack.code_tail ?? ''), expires_at: ppBack.expires_at ?? null }
        set((state) => ({
          sales: prependUnique([newReturn], state.sales),
          saleItems: appendUnique(state.saleItems, replyLines),
          products: shelf === 'delta' ? returnedToShelf(state.products, replyLines) : state.products,   // a service stays at 0
          // Mirror what the server did: the debt falls only for a CREDIT
          // original (085 — a cash sale carries a customer too, now); the
          // bonus balance is whatever the server says it is.
          clubStatuses: withoutStatus(state.clubStatuses, original.customer_id),   // 1.63 — a return changes his month too
          customers: original.customer_id && !replayed
            ? state.customers.map((c) => {
                if (c.id !== original.customer_id) return c
                const bonusRaw = (data as any).bonus_balance
                return {
                  ...c,
                  balance: original.payment_method === 'credit' ? c.balance - total : c.balance,
                  ...(bonusRaw == null ? {} : { bonus_balance: Number(bonusRaw) }),
                }
              })
            : state.customers,
        }))
        return newReturn
      },

      // ---- Held (parked) carts ----
      holdCart: async (cart) => {
        const { data, error } = await supabase
          .from('held_carts')
          .insert({
            label: cart.label ?? null,
            items: cart.items,
            discount: cart.discount,
            discount_type: cart.discount_type,
            discount_value: cart.discount_value,
            total: cart.total,
            items_count: cart.items_count,
            // 178 (1.58): sent only when there is one, so every other held cart is what 1.57 sent
            ...(cart.customer_id ? { customer_id: cart.customer_id } : {}),
            ...(cart.quote ? { quote: cart.quote } : {}),
          })
          .select()
          .single()
        if (failedT(error, 'store.holdFailed') || !data) return false
        set((state) => ({ heldCarts: [mapHeldCart(data), ...state.heldCarts] }))
        return true
      },

      deleteHeldCart: async (id) => {
        const { error } = await supabase.from('held_carts').delete().eq('id', id)
        if (failedT(error, 'store.heldDeleteFailed')) return
        set((state) => ({ heldCarts: state.heldCarts.filter((c) => c.id !== id) }))
      },

      // ---- Suppliers ----
      addSupplier: async (s) => {
        const { data, error } = await supabase
          .from('suppliers')
          .insert({ name: s.name, tin: s.tin, phone: s.phone, contact: s.contact, address: s.address, note: s.note })
          .select()
          .single()
        if (failedT(error, 'store.x.64') || !data) return null
        const sup = mapSupplier(data)
        set((state) => ({ suppliers: [sup, ...state.suppliers] }))
        return sup
      },

      updateSupplier: async (id, s) => {
        const { error } = await supabase.from('suppliers').update(s).eq('id', id)
        if (failedT(error, 'store.x.65')) return
        set((state) => ({ suppliers: state.suppliers.map((x) => (x.id === id ? { ...x, ...s } : x)) }))
      },

      deleteSupplier: async (id) => {
        // Same rule as deleteCustomer: `supplier_payments` cascade, and an
        // open balance would vanish without a trace; 170 — returns keep it too.
        const s = get().suppliers.find((x) => x.id === id)
        const refusal = s ? supplierDeleteRefusal(s, get().supplierPayments, get().supplierReturns) : null
        if (refusal) {
          toast.error('params' in refusal ? t(refusal.key, refusal.params) : t(refusal.key))
          return
        }
        const { error } = await supabase.from('suppliers').delete().eq('id', id)
        if (failedT(error, 'store.x.68')) return
        set((state) => ({ suppliers: state.suppliers.filter((x) => x.id !== id) }))
      },

      // ---- Purchasing (atomic: stock up + cost refresh + supplier debt) ----
      createPurchase: async (purchase, items) => {
        // 1.34 — how many server reads the screen had applied when this left.
        const seen = liveApplied
        // 171 — the source of the paid money and the dialog's ref; the drawer needs this device's open shift
        const source = purchaseSourceArgs({ paid: purchase.paid, source: purchase.paid_source, shiftId: selectOpenShift(get())?.id ?? null, clientRef: purchase.client_ref })
        const { data, error } = await supabase.rpc('create_purchase_dated', {   // 185: each line may carry its pack date
          ...source,
          p_branch_id: get().activeBranchId, // goods received at the active branch
          p_supplier_id: purchase.supplier_id,
          p_supplier_name: purchase.supplier_name,
          p_total: purchase.total,
          p_paid: purchase.paid,
          p_items_count: purchase.items_count,
          p_note: purchase.note,
          p_items: items,
          p_rsge_waybill_id: purchase.rsge_waybill_id ?? null,
        })
        if (error && (error as { code?: string }).code === '23505') {
          // unique(org_id, rsge_waybill_id) — this waybill was already received
          toast.error(t('store.x.69'))
          return null
        }
        // 1.65 (191) — an order is received once: its ref already booked with other lines is not „a reused ref"
        if (error && purchase.client_ref.startsWith('sorder:') && /CLIENT_REF_REUSED/.test((error as { message?: string }).message ?? '')) {
          toast.error(t('sorder.err.done'))
          return null
        }
        if (failedT(error, 'store.x.70') || !data) return null

        const newPurchase = mapPurchase((data as any).purchase)
        const savedItems = ((data as any).items ?? []) as any[]
        // 171 — money paid from the drawer is a movement out; the Z counts it at once
        const movement = (data as any).movement ? mapCashMovement((data as any).movement) : null
        if (movement) set((state) => ({ cashMovements: [movement, ...state.cashMovements.filter((m) => m.id !== movement.id)] }))
        // a replay (the same ref): the server booked it before and says so (171, Codex final
        // review #2) — read the absolute state, add nothing twice, even when this screen has
        // not seen the first booking yet
        if ((data as any).replayed === true || get().purchases.some((p) => p.id === newPurchase.id)) {
          await get().refreshLive(['products', 'purchases', 'suppliers'])
          set((state) => ({ purchaseItems: mergeById(state.purchaseItems, savedItems.map(mapPurchaseItem)) }))
          return newPurchase
        }
        // 1.34 (Codex) — a live refresh replaced the products, purchases or
        // suppliers while this was in flight, and that read may already hold
        // this purchase: the delta below on top of it counted the box twice
        // (reproduced: a size went 5 → 7 on screen for one unit, the DB said 6).
        // Read the absolute state again instead; only the lines, which no
        // refresh reads (134), come from this answer.
        if (liveApplied !== seen) {
          await get().refreshLive(['products', 'purchases', 'suppliers'])
          set((state) => ({ purchaseItems: mergeById(state.purchaseItems, savedItems.map(mapPurchaseItem)) }))
          return newPurchase
        }
        set((state) => ({
          purchases: [newPurchase, ...state.purchases.filter((x) => x.id !== newPurchase.id)],
          purchaseItems: mergeById(state.purchaseItems, savedItems.map(mapPurchaseItem)),
          // Raise stock + blend cost locally (weighted average, mirrors create_purchase).
          //
          // The two numbers here are NOT the same base, and mixing them up is the
          // bug this comment exists to prevent:
          //   - stock rises at the ACTIVE BRANCH, so it moves `quantity`, which
          //     hydrate() has already lensed to that branch (see ~line 835);
          //   - cost is org-wide. `create_purchase` blends against
          //     `products.quantity` in the DB, which trigger A keeps equal to
          //     SUM(product_stock) — the org-wide total, kept here as
          //     `org_quantity`. Blending against the branch slice instead
          //     over-weights the incoming price: measured on MINIMAX, receiving
          //     20 × ₾2.00 of ნაბეღლავი into the branch holding 1 of 18 units
          //     showed ₾1.96 on screen while the DB held ₾1.62 — 21% off, and
          //     margin/profit/below-cost all read the screen value until the
          //     next hydrate.
          // In a single-branch org `org_quantity` is undefined and `quantity` IS
          // the org total; receiptOnProducts (lib/receive) reads both shapes and
          // counts every line of a product, blended line after line.
          products: receiptOnProducts(state.products, items),
          // add unpaid remainder to supplier balance locally
          suppliers: state.suppliers.map((s) =>
            s.id === purchase.supplier_id ? { ...s, balance: s.balance + (purchase.total - purchase.paid) } : s
          ),
        }))
        return newPurchase
      },

      refreshSupplierReturns: async () => {
        const gen = bootGen
        const [r, i] = await Promise.all([
          allRows((a, b) => supabase.from('supplier_returns').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows((a, b) => supabase.from('supplier_return_items').select('*').order('id').range(a, b)),
        ])
        if (gen !== bootGen || r.error || i.error) return   // a late answer never paints another shop
        set({ supplierReturns: (r.data ?? []).map(mapSupplierReturn), supplierReturnItems: (i.data ?? []).map(mapSupplierReturnItem) })
      },

      // ---- 1.65 (191) — needs and supplier orders: read when a screen opens, re-read on a broadcast ----
      loadDemands: async () => {
        const gen = bootGen
        const k = demandReads.next()
        const { data, error } = await allRows((a, b) => supabase.from('demand_records').select('*')
          .eq('status', 'open').order('created_at', { ascending: false }).order('id').range(a, b))
        if (gen !== bootGen) return false   // a late answer never paints another shop
        if (!demandReads.isLatest(k)) return true   // a newer read or a local change has the slice
        if (error) { console.error('demand_records load error', error); return false }
        set({ demands: (data ?? []).map(mapDemand) })
        return true
      },

      readOrderNeeds: async (orderId) => {
        const { data, error } = await supabase.from('demand_records').select('*').eq('order_id', orderId)
        if (error) { console.error('demand_records read error', error); return null }
        return (data ?? []).map(mapDemand)
      },

      closeDemands: async (ids) => {
        if (ids.length === 0) return true
        const gen = bootGen
        const { error } = await supabase.from('demand_records').update({ status: 'closed' }).in('id', ids)
        if (failedT(error, 'store.x.70')) return false
        if (gen !== bootGen) return true
        demandReads.next()
        const gone = new Set(ids)
        set((state) => ({ demands: state.demands ? state.demands.filter((d) => !gone.has(d.id)) : state.demands }))
        return true
      },

      loadSupplierOrders: async () => {
        const gen = bootGen
        const k = orderReads.next()
        const since = new Date(Date.now() - SALES_WINDOW_DAYS * 86_400_000).toISOString()
        const { data, error } = await allRows((a, b) => supabase.from('supplier_orders').select('*')
          .or(`status.eq.open,created_at.gte.${since}`).order('created_at', { ascending: false }).order('id').range(a, b))
        if (gen !== bootGen) return false
        if (error) { console.error('supplier_orders load error', error); return false }
        const orders = (data ?? []).map(mapSupplierOrder)
        const items: SupplierOrderItem[] = []
        for (let i = 0; i < orders.length; i += 150) {
          const ids = orders.slice(i, i + 150).map((o) => o.id)
          const r = await allRows((a, b) => supabase.from('supplier_order_items').select('*').in('order_id', ids).order('id').range(a, b))
          if (gen !== bootGen) return false
          if (r.error) { console.error('supplier_order_items load error', r.error); return false }
          items.push(...(r.data ?? []).map(mapSupplierOrderItem))
        }
        if (!orderReads.isLatest(k)) return true   // a newer read or a local change has the slice
        set({ supplierOrders: { orders, items } })
        return true
      },

      saveSupplierOrders: async (orders) => {
        const gen = bootGen
        const { data, error } = await supabase.rpc('save_supplier_orders', { p_orders: orders })
        if (error && /NEED_TAKEN/.test(error.message ?? '')) {
          toast.error(t('sorder.err.needTaken'))
          void get().loadDemands()
          return 'need_taken'
        }
        if (failedT(error, 'store.x.70') || !data) return 'error'
        mergeOrders(data, gen)
        if (gen !== bootGen) return 'ok'
        demandReads.next()
        const claimed = new Map(orders.flatMap((o) => o.need_ids.map((n) => [n, o.id] as const)))
        set((state) => ({ demands: state.demands ? state.demands.map((d) => (claimed.has(d.id) ? { ...d, order_id: claimed.get(d.id)! } : d)) : state.demands }))
        return 'ok'
      },

      editSupplierOrder: async (id, lines, note, needIds) => {
        const gen = bootGen
        const { data, error } = await supabase.rpc('edit_supplier_order', {
          p_order_id: id,
          p_lines: lines.map((l, i) => ({ id: crypto.randomUUID(), product_id: l.product_id, name: l.name, unit: l.unit, qty: l.qty, unit_cost: l.unit_cost, position: i })),
          p_note: note,
          p_need_ids: needIds,
        })
        if (failedT(error, 'store.x.70') || !data) return false
        mergeOrders(data, gen)
        if (gen === bootGen && get().demands !== null) void get().loadDemands()
        return true
      },

      setSupplierOrderStatus: async (id, status) => {
        const gen = bootGen
        const { data, error } = await supabase.rpc('set_supplier_order_status', { p_order_id: id, p_status: status })
        if (failedT(error, 'store.x.70') || !data) return false
        mergeOrders(data, gen)
        if (gen === bootGen && status === 'cancelled' && get().demands !== null) void get().loadDemands()   // its needs are open again
        return true
      },

      markOrderSent: async (id) => {
        const gen = bootGen
        const { data, error } = await supabase.from('supplier_orders').update({ sent_at: new Date().toISOString() })
          .eq('id', id).is('sent_at', null).select()
        if (error) { console.error('mark order sent', error); return }   // a missing stamp is cosmetic
        if (data?.length) mergeOrders({ orders: data, items: null }, gen)
      },

      // ---- 1.66 (192) — a café's tables and open bills: read when the tables screen opens, re-read on a broadcast ----
      loadTables: async () => {
        const gen = bootGen
        const k = tableReads.next()
        const branch = get().activeBranchId
        const ks = seatedReads.next()   // 1.68 — the seated read is this read's too
        // 1.69 — the parties at the tables, where the switch is on (no visit exists without it): read with the bills
        const qr = get().settings.features?.tableQr === true
        const kv = visitReads.next()
        const place = { gen, orgId: get().currentOrg?.id, branchId: branch }
        const visitsRead = qr && branch ? supabase.rpc('tables_visits', { p_branch: branch }) : Promise.resolve({ data: [], error: null })
        const [h, tb, ob, se] = await Promise.all([
          supabase.from('dining_halls').select('*').order('sort').order('name'),
          supabase.from('dining_tables').select('*').order('sort').order('name'),
          supabase.rpc('table_orders_open'),
          // 196 (1.68) — read with the bills, so a table's status comes from one moment (G22)
          branch ? supabase.rpc('tables_seated', { p_branch: branch }) : Promise.resolve({ data: [], error: null }),
        ])
        if (gen !== bootGen) return false   // a late answer never paints another shop
        if (!tableReads.isLatest(k)) return true   // a reply applied meanwhile is newer than this read
        const err = h.error ?? tb.error ?? ob.error
        if (err) { console.error('tables load error', err); return false }
        const bills: Record<string, Bill> = {}
        for (const r of (ob.data ?? []) as unknown[]) { const b = mapBill(r); bills[b.id] = b }
        tombstoneAfterLoad(Object.keys(get().bills ?? {}), Object.keys(bills), ++tableSeq, tableGone)
        // a seated read that failed, or one a newer read or a freed table overtook, keeps what is there
        if (se.error) console.error('tables seated', se.error)
        set({ halls: (h.data ?? []).map(mapHall), diningTables: (tb.data ?? []).map(mapTable), bills,
          ...(se.error || !seatedReads.isLatest(ks) ? {} : { seated: mapSeated(se.data) }) })
        // a visits read that failed (a database before 197) or that a newer read overtook keeps what is there
        const vr = await visitsRead
        if (vr.error) console.error('tables visits', vr.error)
        else if (acceptsRead({ ...place, k: kv }, { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId }, visitReads.isLatest)) {
          set({ visits: mapVisits(vr.data) })
        }
        return true
      },

      // ---- 1.69 (198) — the guests' requests ----
      loadGuestRequests: async () => {
        const branch = get().activeBranchId
        if (!branch) return false
        const place = { gen: bootGen, orgId: get().currentOrg?.id, branchId: branch }
        const k = requestReads.next()
        const { data, error } = await supabase.rpc('table_requests_open', { p_branch: branch })
        const now = { gen: bootGen, orgId: get().currentOrg?.id, branchId: get().activeBranchId }
        if (!acceptsRead({ ...place, k }, now, requestReads.isLatest)) return true
        if (error) { console.error('table requests', error); return false }
        set({ guestRequests: mapRequests(data) })
        return true
      },

      requestClaim: async (id) => {
        const r = await tableRpc<{ ok?: boolean; reason?: string; by?: string | null; requests?: unknown }>('table_request_claim', { p_request: id })
        void get().loadGuestRequests()
        if (!r.ok) return null
        if (r.data?.ok !== true) {
          const by = typeof r.data?.by === 'string' && r.data.by ? r.data.by : null
          toast.error(by ? t('tables.req.taken', { name: by }) : t('tables.req.takenAny'), { duration: 6000 })
          return { ok: false, by }
        }
        // the party is this login's now: its bill's waiter changed too (the database did it, 198)
        if (get().bills !== null) void get().loadTables()
        return { ok: true }
      },

      requestOpen: async (id) => {
        const r = await tableRpc<unknown>('table_request_open', { p_request: id })
        if (!r.ok) { void get().loadGuestRequests(); return null }
        const [req] = mapRequests([r.data])
        if (!req) return null
        requestReads.next()   // a read already in flight predates this change
        set((st) => (st.guestRequests ? { guestRequests: st.guestRequests.map((x) => (x.id === req.id ? req : x)) } : {}))
        return req
      },

      requestConfirm: async (id, version, lines, addons) => {
        const gen = bootGen
        const start = tableSeq
        // 1.70 (A11): the first send keeps a stale-rules refusal quiet — the rules are read again and it goes once more
        // (a new op: the refused one is settled); the second answer is said as usual
        let last: 0 | 1 = 0
        const r = await confirmWithFreshAddons((attempt) => {
          last = attempt
          return tableOpRpc<{ bill: unknown; batch: unknown }>(confirmOpKey(id, lines), 'table_request_confirm',
            { p_request: id, p_version: version, p_lines: lines, p_addons: addons() },
            attempt === 0 ? { quietCodes: ['REQUEST_ADDON_INVALID'] } : undefined)
        }, () => get().reloadAddonRules(), () => gen === bootGen)
        if (!r.ok) {
          if (gen === bootGen && last === 0 && r.code === 'REQUEST_ADDON_INVALID') toast.error(t('tables.err.REQUEST_ADDON_INVALID'), { duration: 6000 })
          void get().loadGuestRequests()
          return { ok: false, code: r.code }
        }
        // 1.71: signed out or another shop while it was sent — the confirm stands on the server; this session prints nothing
        if (gen !== bootGen) return { ok: true, bill: null, batch: null }
        requestReads.next()   // a read already in flight predates this change; the one below does not
        set((st) => (st.guestRequests ? { guestRequests: st.guestRequests.filter((x) => x.id !== id) } : {}))
        void get().loadGuestRequests()
        const bill = applyBill(r.data.bill, gen, start)
        return { ok: true, bill, batch: r.data.batch ? mapBatch(r.data.batch) : null }
      },

      reloadAddonRules: async () => {
        const gen = bootGen
        const k = addonReads.next()
        const r = await allRows((a, b) => supabase.from('product_addons').select('*').order('sort_order', { ascending: true }).order('id').range(a, b))
        if (gen !== bootGen) return false
        if (r.error) { console.error('product_addons reload error', r.error); return false }
        if (!addonReads.isLatest(k)) return true   // a newer read or the owner's own save has the rules
        set({ productAddons: (r.data ?? []).map(mapProductAddon) })
        return true
      },

      requestDismiss: async (id, reason) => {
        const r = await tableRpc<unknown>('table_request_dismiss', { p_request: id, p_reason: reason })
        if (r.ok) {
          requestReads.next()
          set((st) => (st.guestRequests ? { guestRequests: st.guestRequests.filter((x) => x.id !== id) } : {}))
        }
        void get().loadGuestRequests()
        return r.ok
      },

      requestDone: async (id) => {
        const r = await tableRpc<{ check?: boolean }>('table_request_done', { p_request: id })
        if (r.ok) {
          requestReads.next()
          set((st) => (st.guestRequests ? { guestRequests: st.guestRequests.filter((x) => x.id !== id) } : {}))
          if (r.data?.check && get().bills !== null) void get().loadTables()   // the bill's check request is set (1.68)
        }
        void get().loadGuestRequests()
        return r.ok ? { check: r.data?.check === true } : null
      },

      // ---- 1.69 (197) — the party at a table ----
      tableSeat: async (tableId) => {
        const r = await tableRpc<unknown>('table_seat', { p_table: tableId })
        if (r.ok) void get().loadTables()
        return r.ok
      },

      tableVisitEnd: async (tableId) => {
        const r = await tableRpc<{ ended?: boolean }>('table_visit_end', { p_table: tableId })
        if (!r.ok) return false
        visitReads.next()
        seatedReads.next()
        set((st) => ({
          ...(st.visits ? { visits: st.visits.filter((v) => v.tableId !== tableId) } : {}),
          ...(st.seated ? { seated: st.seated.filter((x) => x.tableId !== tableId) } : {}),
        }))
        void get().loadTables()
        void get().loadGuestRequests()
        return true
      },

      tableReserve: async (tableId, at, name) => {
        const r = await tableRpc<{ reserved_at?: string | null; reserved_name?: string | null }>('table_reserve',
          { p_table: tableId, p_at: at, p_name: name })
        if (!r.ok) return false
        set((st) => (st.diningTables ? {
          diningTables: st.diningTables.map((x) => (x.id === tableId
            ? { ...x, reserved_at: r.data?.reserved_at ?? null, reserved_name: r.data?.reserved_name ?? null } : x)),
        } : {}))
        return true
      },

      billOpen: async (tableId, guests) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_open', { p_table_id: tableId, p_guests: guests })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billAdd: async (billId, productId, qty, note) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableOpRpc<unknown>(`add:${billId}:${productId}:${qty}:${note ?? ''}`, 'table_order_add',
          { p_order_id: billId, p_product_id: productId, p_qty: qty, p_note: note ?? null })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billEditLine: async (lineId, qty, note) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_edit_line', { p_line_id: lineId, p_qty: qty, p_note: note })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billSend: async (billId) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableOpRpc<{ bill: unknown; batch: unknown }>(`send:${billId}`, 'table_order_send', { p_order_id: billId })
        if (!r.ok) return null
        const bill = applyBill(r.data.bill, gen, start)
        return bill ? { bill, batch: r.data.batch ? mapBatch(r.data.batch) : null } : null
      },

      billCancelPortion: async (lineId, qty, reason, pin) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableOpRpc<{ bill?: unknown; batch?: unknown; refused?: string }>(`cancel:${lineId}:${qty}:${reason}`,
          'table_order_cancel_portion', { p_line_id: lineId, p_qty: qty, p_reason: reason, p_override_pin: pin })
        if (!r.ok) return null
        if (r.data.refused) { toast.error(t('store.ownerPinWrong'), { duration: 6000 }); return 'PIN' }
        const bill = applyBill(r.data.bill, gen, start)
        return bill && r.data.batch ? { bill, batch: mapBatch(r.data.batch) } : null
      },

      billReprint: async (batchId) => {
        const r = await tableRpc<unknown>('table_order_reprint', { p_batch_id: batchId })
        return r.ok && r.data ? mapBatch(r.data) : null
      },

      findBatch: async (billId, n, kind) => {
        const { data, error } = await supabase.from('table_order_batches').select('*')
          .eq('order_id', billId).eq('n', n).eq('kind', kind).order('created_at', { ascending: false }).limit(1)
        if (error) { console.error('table batch read', error); return null }
        return data?.length ? mapBatch(data[0]) : null
      },

      tablesOfSales: async (saleIds) => {
        const out = new Map<string, { table_name: string; hall_name: string | null; guests: number | null }>()
        if (saleIds.length === 0) return out
        const { data, error } = await supabase.from('table_orders').select('sale_id, table_name, hall_name, guests')
          .in('sale_id', saleIds.slice(0, 200))
        if (error) return out   // no tables here (or not yet): the history shows none
        for (const r of (data ?? []) as any[]) {
          if (r.sale_id) out.set(r.sale_id, { table_name: r.table_name, hall_name: r.hall_name ?? null, guests: r.guests ?? null })
        }
        return out
      },

      billMarkPrinted: async (billId, total) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_bill', { p_order_id: billId, p_total: total })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billGuests: async (billId, guests) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_guests', { p_order_id: billId, p_guests: guests })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billMove: async (billId, toTableId) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_move', { p_order_id: billId, p_to_table_id: toTableId })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billCancel: async (billId, reason, pin) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<{ bill?: unknown; batch?: unknown; refused?: string }>('table_order_cancel',
          { p_order_id: billId, p_reason: reason, p_override_pin: pin })
        if (!r.ok) return null
        if (r.data.refused) { toast.error(t('store.ownerPinWrong'), { duration: 6000 }); return 'PIN' }
        const bill = applyBill(r.data.bill, gen, start)
        return bill ? { bill, batch: r.data.batch ? mapBatch(r.data.batch) : null } : null
      },

      // ---- 1.67 (193) — the kitchen and the waiter: ready / served move seq, never the money revision ----
      linesReady: async (lineIds, ready) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<{ bills?: unknown[] }>('table_lines_ready', { p_line_ids: lineIds, p_ready: ready })
        if (!r.ok) return false
        for (const raw of r.data?.bills ?? []) applyBill(raw, gen, start)
        return true
      },

      linesServed: async (lineIds, served) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<{ bills?: unknown[] }>('table_lines_served', { p_line_ids: lineIds, p_served: served })
        if (!r.ok) return false
        for (const raw of r.data?.bills ?? []) applyBill(raw, gen, start)
        return true
      },

      billTake: async (billId) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_take', { p_order_id: billId })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billCheckRequest: async (billId, on) => {
        const gen = bootGen
        const start = tableSeq
        const r = await tableRpc<unknown>('table_order_check_request', { p_order_id: billId, p_on: on })
        return r.ok ? applyBill(r.data, gen, start) : null
      },

      billCloseIfEmpty: async (billId) => {
        const gen = bootGen
        const start = tableSeq
        // quiet on purpose: leaving a bill never toasts a refusal (it may already be paid or moved elsewhere)
        const { data, error } = await supabase.rpc('table_order_close_if_empty', { p_order_id: billId })
        if (error || !data) return 'failed'
        const d = data as { closed?: boolean; bill?: unknown }
        if (d.bill) applyBill(d.bill, gen, start)
        return d.closed ? 'closed' : 'kept'
      },

      loadWaiterReady: async () => {
        const gen = bootGen
        const branch = get().activeBranchId
        if (!branch) return false
        const k = waiterReads.next()
        const { data, error } = await supabase.rpc('waiter_ready', { p_branch: branch })
        if (gen !== bootGen || !waiterReads.isLatest(k)) return true
        if (error) { console.error('waiter feed', error); return false }
        set({ waiterReady: mapReadyGroups(data) })
        return true
      },

      loadKitchenQueue: async (station) => {
        const gen = bootGen
        const branch = get().activeBranchId
        if (!branch) return false
        const k = kitchenReads.next()
        const { data, error } = await supabase.rpc('kitchen_queue', { p_station: station, p_branch: branch })
        if (gen !== bootGen || !kitchenReads.isLatest(k)) return true
        if (error) { console.error('kitchen queue', error); return false }
        set({ kitchenCards: mapKitchenCards(data) })
        return true
      },

      kitchenPing: async (stations, deviceId) => {
        const branch = get().activeBranchId
        if (!branch || stations.length === 0) return
        const { error } = await supabase.rpc('kitchen_screen_ping', { p_branch: branch, p_device: deviceId, p_stations: stations })
        if (error && !isNetworkError(error)) console.error('kitchen heartbeat', error)
      },

      stationsOnline: async () => {
        const branch = get().activeBranchId
        if (!branch) return null
        const { data, error } = await supabase.rpc('kitchen_stations_online', { p_branch: branch })
        if (error || !Array.isArray(data)) return null
        return (data as string[]).filter((x): x is PingStation => x === 'kitchen' || x === 'bar' || x === 'check')
      },

      // ---- 1.68 (196) — the service rate, the seated tables, the check printing, the waiters' numbers ----
      tableServiceSet: async (pct) => {
        const r = await tableRpc<{ pct?: unknown; product_id?: unknown }>('table_service_set', { p_pct: pct })
        if (!r.ok) return false
        const v = Number(r.data?.pct)
        set((st) => ({ settings: { ...st.settings, tableServicePct: Number.isFinite(v) ? v : pct } }))
        // the first rate makes the service product: the till needs its id before the next bill is paid
        await get().refreshProducts({ quiet: true })
        return true
      },

      setTableFreeOnPay: async (on) => {
        const was = get().settings.tableFreeOnPay
        set((st) => ({ settings: { ...st.settings, tableFreeOnPay: on } }))   // optimistic
        const orgId = get().currentOrg?.id
        const { error } = await supabase.from('settings').upsert(
          { ...(orgId ? { org_id: orgId } : {}), key: 'table.free_on_pay', value: on ? 'true' : 'false' },
          { onConflict: 'org_id,key' },
        )
        if (failedT(error, 'store.x.3')) { set((st) => ({ settings: { ...st.settings, tableFreeOnPay: was } })); return false }
        return true
      },

      tableFree: async (tableId) => {
        const r = await tableRpc<{ freed?: number }>('table_free', { p_table_id: tableId })
        if (!r.ok) return false
        seatedReads.next()   // a read already in flight predates this change
        set((st) => (st.seated ? { seated: st.seated.filter((s) => s.tableId !== tableId) } : {}))
        void get().loadSeated()
        return true
      },

      loadSeated: async () => {
        const gen = bootGen
        const branch = get().activeBranchId
        if (!branch) return false
        const k = seatedReads.next()
        const { data, error } = await supabase.rpc('tables_seated', { p_branch: branch })
        if (gen !== bootGen || !seatedReads.isLatest(k)) return true
        if (error) { console.error('tables seated', error); return false }
        set({ seated: mapSeated(data) })
        return true
      },

      checkClaim: async (a) => {
        const gen = bootGen
        const start = tableSeq
        // the automatic printer never toasts: a request another device answered is not this till's news
        const r = await tableRpc<{ claimed?: boolean; print?: unknown; token?: string | null; bill?: unknown }>('table_check_claim', {
          p_order_id: a.billId, p_mode: a.mode, p_request_at: a.requestAt, p_copy_of: a.copyOf, p_snapshot: a.snapshot,
          p_device: stationDeviceId(),
        }, { quiet: a.mode === 'auto' })
        if (!r.ok || !r.data) return null
        const print = mapCheckPrint(r.data.print)
        if (!print) return null
        applyBill(r.data.bill, gen, start)
        return { claimed: r.data.claimed === true, print, token: typeof r.data.token === 'string' ? r.data.token : null }
      },

      checkPrintDone: async (printId, token, state) => {
        // quiet: a lost answer leaves the printing claimed, and after a minute every phone reads it as failed
        const r = await tableRpc<unknown>('table_check_print_done', { p_print_id: printId, p_token: token, p_state: state }, { quiet: true })
        return r.ok
      },

      checkFind: async (code) => {
        const r = await tableRpc<{ print?: unknown; bill?: unknown } | null>('table_check_find', { p_code: code })
        if (!r.ok || !r.data || !r.data.bill) return null
        const print = mapCheckPrint(r.data.print)
        return print ? { print, bill: mapBill(r.data.bill) } : null
      },

      waiterStats: async (branchId, from, to) => {
        // quiet: a read — the screen says „ციფრები ვერ ჩაიტვირთა" in its own place, not a „save failed" toast
        const r = await tableRpc<unknown>('waiter_stats', { p_branch: branchId, p_from: from, p_to: to }, { quiet: true })
        return r.ok ? mapWaiterStats(r.data) : null
      },

      setTillDuties: (d) => {
        const orgId = get().currentOrg?.id
        const branchId = get().activeBranchId
        if (!orgId || !branchId) return
        set({ tillDuties: { orgId, branchId, bar: d.bar, checks: d.checks } })
      },

      saveHall: async (h) => {
        const { error } = await supabase.from('dining_halls').upsert({ ...h, name: h.name.trim() })
        if (layoutRefused(error)) return false
        await get().loadTables()
        return true
      },

      saveTables: async (rows) => {
        if (rows.length === 0) return true
        const { error } = await supabase.from('dining_tables').upsert(rows.map((r) => ({ ...r, name: r.name.trim() })))
        if (layoutRefused(error)) return false
        await get().loadTables()
        return true
      },

      lastSuppliers: async (productIds) => {
        const out = new Map<string, LastSupplier>()
        if (productIds.length === 0) return out
        const { data, error } = await supabase.rpc('product_last_suppliers', { p_product_ids: productIds.slice(0, 2000) })
        if (error) { console.error('product_last_suppliers', error); return out }   // the builder still works, ungrouped
        for (const r of (data ?? []) as any[]) {
          out.set(r.product_id, { product_id: r.product_id, supplier_id: r.supplier_id, supplier_name: r.supplier_name ?? null,
            unit_cost: Number(r.unit_cost) || 0, purchased_at: r.purchased_at })
        }
        return out
      },

      refreshPrepaidMoves: async () => {
        const gen = bootGen
        const { data, error } = await allRows((a, b) => supabase.from('prepaid_moves').select('*').order('created_at', { ascending: false }).order('id').range(a, b))
        if (gen !== bootGen || error) return   // a late answer never paints another shop
        set({ prepaidMoves: (data ?? []).map(mapPrepaidMove) })
      },

      issuePrepaid: async (p) => {
        if (get().isOnline === false || !isBrowserOnline()) { toast.error(t('pp.offline')); return null }
        const shift = selectOpenShift(get())
        if (!shift) { toast.error(t('store.shiftRequired')); return null }
        const key = JSON.stringify(['issue', shift.id, p])
        const qid = reuseAttempt(unsavedPrepaid, key, Date.now()) ?? `prepaid:${crypto.randomUUID()}`
        unsavedPrepaid = null
        const { data, error } = await supabase.rpc('issue_prepaid', {
          p_kind: p.kind, p_amount: p.amount, p_method: p.method, p_card_provider: p.method === 'cash' ? null : p.cardProvider,
          p_shift_id: shift.id, p_customer_id: p.customerId ?? null, p_customer_name: p.customerName ?? null,
          p_customer_phone: p.customerPhone ?? null, p_note: p.note ?? null, p_items: p.items ?? [],
          p_due_date: p.dueDate ?? null, p_expires_at: p.expiresAt ?? null, p_code: p.code ?? null, p_client_ref: qid,
        })
        if (error || !data) {
          // the same press again is the same card (Codex #1): the server answers the first
          unsavedPrepaid = { qid, key, at: Date.now() }
          const m = (error as { message?: string } | null)?.message ?? ''
          const ppErr = prepaidErrorKey(m)
          if (ppErr) toast.error(t(ppErr.key, ppErr.vars), { duration: 8000 })
          else if (error && isNetworkError(error)) toast.error(t('pp.netLost'), { duration: 10000 })
          else failedT(error, 'pp.err.issue')
          // 177 — the owner switched it off: this till learns it now, and the entry points go
          if (m.includes('PREPAID_OFF')) void get().refreshLive(['settings'])
          return null
        }
        void get().refreshPrepaidMoves()
        return mapPrepaid((data as any).prepaid)
      },

      refundPrepaid: async (id, amount, method, cardProvider, note) => {
        if (get().isOnline === false || !isBrowserOnline()) { toast.error(t('pp.offline')); return null }
        const shift = selectOpenShift(get())
        if (!shift && method !== 'transfer') { toast.error(t('store.shiftRequired')); return null }
        const key = JSON.stringify(['refund', id, amount, method, cardProvider, note, shift?.id ?? null])
        const qid = reuseAttempt(unsavedPrepaid, key, Date.now()) ?? `prepaid:${crypto.randomUUID()}`
        unsavedPrepaid = null
        const { data, error } = await supabase.rpc('refund_prepaid', {
          p_prepaid_id: id, p_amount: amount, p_method: method, p_card_provider: method === 'cash' ? null : cardProvider,
          p_shift_id: shift?.id ?? null, p_note: note, p_client_ref: qid,
        })
        if (error || !data) {
          unsavedPrepaid = { qid, key, at: Date.now() }
          const m = (error as { message?: string } | null)?.message ?? ''
          const ppErr = prepaidErrorKey(m)
          if (ppErr) toast.error(t(ppErr.key, ppErr.vars), { duration: 8000 })
          else if (error && isNetworkError(error)) toast.error(t('pp.netLost'), { duration: 10000 })
          else failedT(error, 'pp.err.refund')
          return null
        }
        void get().refreshPrepaidMoves()
        return mapPrepaid((data as any).prepaid)
      },

      listPrepaids: async (query, openOnly) => {
        const { data, error } = await supabase.rpc('list_prepaids', { p_query: query || null, p_open_only: openOnly })
        if (error) { failedT(error, 'pp.err.list'); return null }
        return ((data as any[]) ?? []).map(mapPrepaid)
      },

      findPrepaid: async (code) => {
        const { data, error } = await supabase.rpc('find_prepaid', { p_code: code })
        if (error) { failedT(error, 'pp.err.list'); return null }
        return data ? mapPrepaid(data) : null
      },

      setPrepaidDetails: async (id, d) => {
        const { data, error } = await supabase.rpc('set_prepaid_details', {
          p_prepaid_id: id, p_note: d.note, p_due_date: d.dueDate, p_expires_at: d.expiresAt,
        })
        if (error || !data) {
          const ppErr = prepaidErrorKey((error as { message?: string } | null)?.message ?? '')
          if (ppErr) toast.error(t(ppErr.key, ppErr.vars)); else failedT(error, 'pp.err.details')
          return null
        }
        return mapPrepaid(data)
      },

      prepaidHistory: async (id) => {
        const { data, error } = await supabase.from('prepaid_moves').select('*').eq('prepaid_id', id).order('created_at', { ascending: true })
        if (error) { failedT(error, 'pp.err.list'); return [] }
        return (data ?? []).map(mapPrepaidMove)
      },

      returnToSupplier: async (r) => {
        if (get().isOnline === false) { toast.error(t('sup.ret.offline')); return null }
        const args = returnRpcArgs({
          supplierId: r.supplier_id, settle: r.settle, purchaseId: r.purchase_id, note: r.note, lines: r.lines,
          shiftId: selectOpenShift(get())?.id ?? null, branchId: get().activeBranchId, clientRef: r.clientRef,
        })
        const { data, error } = await supabase.rpc('return_to_supplier', args)
        if (failedT(error, 'sup.ret.failed') || !data) return null
        const d = data as any
        const answer: ReturnAnswer = {
          return: mapSupplierReturn(d.return),
          items: (d.items ?? []).map(mapSupplierReturnItem),
          supplier: d.supplier ? mapSupplier(d.supplier) : null,
          movement: d.movement ? mapCashMovement(d.movement) : null,
          expenses: (d.expenses ?? []).map(mapExpense),
          products: (d.products ?? []).map((p: any) => ({ id: p.id, purchase_price: num(p.purchase_price), org_quantity: num(p.org_quantity), branch_quantity: p.branch_quantity == null ? null : num(p.branch_quantity) })),
        }
        set((state) => applyReturnAnswer(state, answer))
        liveApplied++   // the products and the supplier are the server's now (1.34)
        return answer.return
      },

      paySupplier: async (supplierId, amount, note, method = 'cash', cardProvider) => {
        const openShiftRow = selectOpenShift(get())
        const before = get().suppliers.find((s) => s.id === supplierId)?.balance   // 1.47: paymentLanded
        const { data, error } = await supabase.rpc('pay_supplier', {
          p_supplier_id: supplierId,
          p_amount: amount,
          p_note: note ?? null,
          p_shift_id: openShiftRow?.id ?? null,
          p_method: method,
          p_card_provider: cardProvider ?? null,
        })
        if (error || !data) {
          // 1.47 (Codex's review): an answer lost on the network may have been
          // lost AFTER the commit. What we owe is read again before anything is
          // said and before the dialog's button wakes up: moved by exactly this
          // amount, the payment was booked (lib/offline.ts paymentLanded).
          if (isNetworkError(error)) {
            await get().refreshLive(['suppliers', 'supplier_payments'])
            if (paymentLanded(before, get().suppliers.find((s) => s.id === supplierId)?.balance, amount)) return true
          }
          if (isShiftClosed(error)) await refreshShifts()
          failedT(error, 'store.x.71')
          return false
        }
        const payment = mapSupplierPayment((data as any).payment)
        const supplier = mapSupplier((data as any).supplier)
        set((state) => ({
          supplierPayments: [payment, ...state.supplierPayments],
          suppliers: state.suppliers.map((s) => (s.id === supplierId ? supplier : s)),
        }))
        return true
      },

      // ---- Customers (credit / ნისია) ----
      addCustomer: async (c) => {
        const { data, error } = await supabase
          .from('customers')
          .insert({ name: c.name, phone: c.phone, note: c.note, tin: c.tin ?? null, legal_name: c.legal_name ?? null, legal_address: c.legal_address ?? null,
            ...(c.price_group_id ? { price_group_id: c.price_group_id } : {}) })   // 178 (1.58): the owner's only
          .select()
          .single()
        if (failedT(error, 'store.customerAddFailed') || !data) return null
        const cust = mapCustomer(data)
        set((state) => ({ customers: [cust, ...state.customers] }))
        return cust
      },

      adjustBonus: async (customerId, amount, note) => {
        const { data, error } = await supabase.rpc('adjust_bonus', {
          p_customer_id: customerId, p_amount: amount, p_note: note ?? null,
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('OWNER_ONLY')) { toast.error(t('store.bonusOwnerOnly')); return null }
          failedT(error, 'store.bonusAdjustFailed')
          return null
        }
        const bal = Number(data)
        set((state) => ({ customers: state.customers.map((x) => (x.id === customerId ? { ...x, bonus_balance: bal } : x)) }))
        return bal
      },

      // ---- 1.63 — the loyalty club (188–190) ----
      resolveCard: async (token) => {
        if (get().isOnline === false) {
          // offline: only a customer this tab already loaded (customers are not kept across a reload)
          const c = get().customers.find((x) => x.card_token === token && !x.anonymized_at)
          return c ? { customerId: c.id, status: get().clubStatuses[c.id] ?? null } : { error: 'CLUB_OFFLINE' }
        }
        const gen = bootGen
        const { data, error } = await supabase.rpc('club_card_resolve', { p_token: token })
        if (error) return { error: clubRefusal(error.message) ?? (navigator.onLine ? 'generic' : 'CLUB_OFFLINE') }
        if (gen !== bootGen) return { error: 'generic' }
        const r = data as { customer_id: string; status: ClubStatus | null }
        set((state) => ({ clubStatuses: r.status ? { ...state.clubStatuses, [r.customer_id]: r.status } : state.clubStatuses }))
        await get().refreshCustomer(r.customer_id)
        return { customerId: r.customer_id, status: r.status }
      },

      clubLinkStart: async (input) => {
        try {
          const res = await fetch('/api/club', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'linkStart', ...input }),
          })
          const json = await res.json().catch(() => ({})) as { customerId?: string; error?: string; mailed?: boolean }
          if (!res.ok || !json.customerId) return { error: json.error ?? 'generic' }
          await get().refreshCustomer(json.customerId)
          return { customerId: json.customerId, mailed: json.mailed === true }
        } catch {
          return { error: 'CLUB_OFFLINE' }
        }
      },

      clubLinkConfirm: async (customerId, code) => {
        const { data, error } = await supabase.rpc('club_link_confirm', {
          p_customer: customerId, p_code: code, p_rules_version: CLUB_RULES_VERSION,
        })
        if (error) return { error: clubRefusal(error.message) ?? 'generic' }
        const r = data as { error?: string; customer_id?: string; status?: ClubStatus }
        if (r.error) return { error: r.error }
        if (r.status) set((state) => ({ clubStatuses: { ...state.clubStatuses, [customerId]: r.status! } }))
        await get().refreshCustomer(customerId)
        // the card link goes to the address just confirmed; the cashier hears when it did not (Codex final #7)
        const sent = await get().clubSendCard(customerId)
        return { ok: true, mailed: 'ok' in sent && sent.mailed }
      },

      clubPhoneConfirm: async (customerId) => {
        const { error } = await supabase.rpc('club_phone_confirm', { p_customer: customerId })
        if (error) return { error: clubRefusal(error.message) ?? 'generic' }
        await get().refreshCustomer(customerId)
        return { ok: true }
      },

      clubSendCard: async (customerId) => {
        try {
          const res = await fetch('/api/club', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'sendCard', customerId }),
          })
          const json = await res.json().catch(() => ({})) as { error?: string; mailed?: boolean }
          return res.ok ? { ok: true, mailed: json.mailed === true } : { error: json.error ?? 'generic' }
        } catch {
          return { error: 'CLUB_OFFLINE' }
        }
      },

      clubRevoke: async (customerId) => {
        try {
          const res = await fetch('/api/club', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'revoke', customerId }),
          })
          const json = await res.json().catch(() => ({})) as { error?: string; mailed?: boolean }
          if (!res.ok) return { error: json.error ?? 'generic' }
          await get().refreshCustomer(customerId)
          return { ok: true, mailed: json.mailed === true }
        } catch {
          return { error: 'CLUB_OFFLINE' }
        }
      },

      clubAnonymize: async (customerId) => {
        const { error } = await supabase.rpc('club_anonymize', { p_customer: customerId })
        if (error) return { error: clubRefusal(error.message) ?? 'generic' }
        await get().refreshCustomer(customerId)
        set((state) => {
          const { [customerId]: _gone, ...rest } = state.clubStatuses
          return { clubStatuses: rest }
        })
        return { ok: true }
      },

      clubRotateCode: async () => {
        const { data, error } = await supabase.rpc('club_rotate_code')
        if (error) { failedT(error, 'club.err.generic'); return null }
        const code = String((data as { code?: string })?.code ?? '')
        set((state) => ({ settings: { ...state.settings, clubCode: code } }))
        return code || null
      },

      clubStatusOf: (customerId) => get().clubStatuses[customerId] ?? null,

      clubForget: (customerId) => set((state) => ({ clubStatuses: withoutStatus(state.clubStatuses, customerId) })),

      // updateSettings writes a key by its TS name; the club's keys are dotted (club.threshold), so its own write
      setClubThreshold: async (value) => {
        const prev = get().settings
        set({ settings: { ...prev, clubThreshold: value } })
        const orgId = get().currentOrg?.id
        const { error } = await supabase.from('settings').upsert(
          { ...(orgId ? { org_id: orgId } : {}), key: 'club.threshold', value: value == null ? '' : String(value) },
          { onConflict: 'org_id,key' },
        )
        if (failedT(error, 'store.x.3')) { set({ settings: prev }); return false }
        return true
      },

      // ---- 178 (1.58) — price groups ----
      refreshCustomer: async (id) => {
        const gen = bootGen   // lands only in the shop it was read for (1.47)
        const { data, error } = await supabase.from('customers').select('*').eq('id', id).maybeSingle()
        if (error) { console.error('customer refresh error', error); return }   // the cached row stays
        if (gen !== bootGen) return
        set((state) => ({ customers: customerRefreshed(state.customers, id, data ? mapCustomer(data) : null) }))
        if (!get().priceGroupsRead) void get().refreshPriceGroups()
      },

      refreshStockLots: async () => {
        const gen = bootGen   // lands only in the shop it was read for (1.47)
        const epoch = ++lotsEpoch   // 1.62 — and only if no newer read started meanwhile
        const rows: StockLot[] = []
        // a page at a time: PostgREST answers at most 1 000 rows
        for (let from = 0; ; from += 1000) {
          const { data, error } = await supabase.from('stock_lots')
            .select('id, product_id, branch_id, expiry_date, qty, received_qty, received_at')
            .gt('qty', 0).order('id').range(from, from + 999)
          if (error) { console.error('stock_lots load error', error); return }   // keep what is on screen; loaded stays as it was
          for (const r of data ?? []) {
            rows.push({ id: r.id, product_id: r.product_id, branch_id: r.branch_id, expiry_date: r.expiry_date ?? null,
              qty: num(r.qty), received_qty: num(r.received_qty), received_at: r.received_at })
          }
          if ((data ?? []).length < 1000) break
        }
        if (!lotsEpochOk(epoch, lotsEpoch, gen, bootGen)) return
        set({ stockLots: rows, stockLotsLoaded: true })
      },

      writeOffLot: async (lotId, qty, note) => {
        const { error } = await supabase.rpc('write_off_lot', { p_lot_id: lotId, p_qty: qty, p_note: note ?? null })
        if (failedT(error, 'store.x.lotWriteOff')) return false
        await Promise.all([get().refreshStockLots(), get().refreshProducts({ quiet: true })])
        return true
      },

      editLot: async (lotId, expiry, qty) => {
        const { error } = await supabase.rpc('edit_lot', { p_lot_id: lotId, p_expiry_date: expiry, p_qty: qty })
        if (failedT(error, 'store.x.lotEdit')) return false
        await Promise.all([get().refreshStockLots(), get().refreshProducts({ quiet: true })])
        return true
      },

      setTrackExpiry: async (productId, on, expiry) => {
        const { error } = await supabase.rpc('set_track_expiry', { p_product_id: productId, p_on: on, p_expiry_date: expiry ?? null })
        if (failedT(error, 'store.x.lotTrack')) return false
        set((state) => ({ products: state.products.map((p) => (p.id === productId ? { ...p, track_expiry: on } : p)) }))
        await Promise.all([get().refreshStockLots(), get().refreshProducts({ quiet: true })])
        return true
      },

      refreshPriceGroups: async () => {
        const gen = bootGen   // lands only in the shop it was read for (1.47)
        const { data, error } = await supabase.from('price_groups').select('*').order('created_at').order('id')
        if (error) { console.error('price_groups load error', error); return }   // non-critical, like held carts
        if (gen !== bootGen) return
        set({ priceGroups: (data ?? []).map(mapPriceGroup), priceGroupsRead: true })
      },

      addPriceGroup: async (g) => {
        const { data, error } = await supabase
          .from('price_groups')
          .insert({ name: g.name.trim(), rule: g.rule, pct: g.rule === 'pct' ? g.pct : null })
          .select()
          .single()
        if (failedT(error, 'store.priceGroupFailed') || !data) return null
        const pg = mapPriceGroup(data)
        set((state) => ({ priceGroups: [...state.priceGroups.filter((x) => x.id !== pg.id), pg] }))
        return pg
      },

      updatePriceGroup: async (id, patch) => {
        const { data, error } = await supabase.from('price_groups').update(patch).eq('id', id).select().single()
        if (failedT(error, 'store.priceGroupFailed') || !data) return false
        const pg = mapPriceGroup(data)
        set((state) => ({ priceGroups: state.priceGroups.map((x) => (x.id === id ? pg : x)) }))
        return true
      },

      // 180 (1.58) — the box, in one owner-only transaction (set_product_pack)
      setProductPack: async (productId, pack) => {
        const { data, error } = await supabase.rpc('set_product_pack', {
          p_product: productId,
          p_qty: pack?.qty ?? null,
          p_price: pack?.price ?? null,
          p_barcode: pack?.barcode ?? null,
        })
        if (error) {
          toast.error(t(packErrorKey(error.message ?? '')), { duration: 8000 })
          return false
        }
        // only the box fields: the row's quantity is not this lens's (multi-branch)
        const row = (data as { product?: { pack_qty?: unknown; pack_barcode?: string | null } } | null)?.product
        if (row) {
          set((state) => ({
            products: state.products.map((p) => (p.id === productId
              ? { ...p, pack_qty: row.pack_qty == null ? null : num(row.pack_qty), pack_barcode: row.pack_barcode ?? null }
              : p)),
          }))
        }
        await get().refreshPromotions()
        return true
      },

      refreshPromotions: async () => {
        const gen = bootGen   // 1.47: lands only in the shop it was read for (refreshLive)
        const [promos, items] = await Promise.all([
          allRows<any>((a, b) => supabase.from('promotions').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          allRows<any>((a, b) => supabase.from('promotion_items').select('*').order('id').range(a, b)),
        ])
        if (promos.error || items.error || !promos.data || !items.data || gen !== bootGen) return
        const byPromo = new Map<string, PromotionItem[]>()
        for (const r of items.data) {
          const list = byPromo.get(r.promotion_id) ?? []
          list.push({ product_id: r.product_id, quantity: num(r.quantity) || 1, promo_price: r.promo_price == null ? null : num(r.promo_price) })
          byPromo.set(r.promotion_id, list)
        }
        set({
          promotions: promos.data.map((r: any): Promotion => ({
            id: r.id, name: r.name, kind: r.kind,
            pct: r.pct == null ? null : num(r.pct),
            bundle_price: r.bundle_price == null ? null : num(r.bundle_price),
            starts_at: r.starts_at ?? null, ends_at: r.ends_at ?? null,
            active: !!r.active, created_at: r.created_at,
            items: byPromo.get(r.id) ?? [],
            pack_product_id: r.pack_product_id ?? null,   // 180 (1.58): a product's box price
          })),
        })
      },

      savePromotion: async (p) => {
        const row = {
          name: p.name.trim(), kind: p.kind,
          pct: p.kind === 'price' ? p.pct : null,
          bundle_price: p.kind === 'bundle' ? p.bundle_price : null,
          starts_at: p.starts_at, ends_at: p.ends_at, active: p.active,
        }
        let id = p.id
        if (id) {
          const { error } = await supabase.from('promotions').update(row).eq('id', id)
          if (failedT(error, 'store.x.72')) return false
          const { error: delErr } = await supabase.from('promotion_items').delete().eq('promotion_id', id)
          if (failedT(delErr, 'store.x.73')) return false
        } else {
          const { data, error } = await supabase.from('promotions').insert(row).select('id').single()
          if (failedT(error, 'store.x.72') || !data) return false
          id = (data as any).id as string
        }
        if (p.items.length > 0) {
          const { error } = await supabase.from('promotion_items').insert(p.items.map((it) => ({
            promotion_id: id, product_id: it.product_id,
            quantity: p.kind === 'bundle' ? it.quantity : 1,
            promo_price: p.kind === 'price' ? it.promo_price : null,
          })))
          if (failedT(error, 'store.x.73')) { await get().refreshPromotions(); return false }
        }
        await get().refreshPromotions()
        return true
      },

      setPromotionActive: async (id, active) => {
        const prev = get().promotions
        set({ promotions: prev.map((x) => (x.id === id ? { ...x, active } : x)) })
        const { error } = await supabase.from('promotions').update({ active }).eq('id', id)
        if (failedT(error, 'store.x.74')) set({ promotions: prev })
      },

      deletePromotion: async (id) => {
        const { error } = await supabase.from('promotions').delete().eq('id', id)
        if (failedT(error, 'store.x.75')) return
        set((state) => ({ promotions: state.promotions.filter((x) => x.id !== id) }))
      },

      refreshCustomers: async () => {
        const { data, error } = await allRows((a, b) =>
          supabase.from('customers').select('*').order('created_at', { ascending: false }).order('id').range(a, b))
        if (!error && data) set({ customers: data.map(mapCustomer) })
      },

      /**
       * 106 — the database said another device changed these tables. Re-read
       * only those slices, with the queries hydrate() uses, so the same RLS
       * decides what a cashier gets. Quiet on purpose: a failed read leaves
       * the copy as it was, and the next signal or focus refresh tries again.
       *
       * Sales are read from shortly before the newest one held — new rows
       * arrive, and a fiscal status or return written meanwhile comes with
       * them. The window's OLDER edge (089) is never touched, so the history
       * stays one contiguous range.
       */
      refreshLive: async (tables) => {
        // 1.47 (Codex's audit): what this read brings lands only in the shop it
        // was read for — signed out and into another shop on the same device, a
        // late answer must not repaint the new shop's lists (the 1.24 generation).
        const gen = bootGen
        const put: typeof set = (partial) => { if (gen === bootGen) set(partial) }
        const want = new Set(tables)
        const has = (...t: string[]) => t.some((x) => want.has(x))
        const jobs: Promise<unknown>[] = []

        if (has('products', 'product_stock')) jobs.push(get().refreshProducts({ quiet: true }))
        if (has('promotions')) jobs.push(get().refreshPromotions())
        if (has('price_groups')) jobs.push(get().refreshPriceGroups())   // 178 (1.58)
        // 200 (1.71) — an add-on rule changed on another device: the till's cart and the waiters' confirms add the cup now,
        // not after a reload (the 1.70 review's M1). The boot's own read; it lands only in the session that asked.
        if (has('product_addons')) jobs.push(get().reloadAddonRules())
        if (has('stock_lots', 'product_stock')) jobs.push(get().refreshStockLots())   // 1.61
        if (has('shifts')) jobs.push(refreshShifts())

        if (has('sales')) jobs.push((async () => {
          const { sales, salesLoadedFrom } = get()
          const newest = sales.reduce((m, s) => (s.created_at > m ? s.created_at : m), '')
          // Ten minutes of overlap: created_at is the server's clock at the
          // INSERT, so a sale committed a moment after one already held can
          // carry the earlier stamp. mergeById makes the overlap free.
          const since = newest
            ? new Date(new Date(newest).getTime() - 10 * 60_000).toISOString()
            : (salesLoadedFrom ?? new Date(Date.now() - 86_400_000).toISOString())
          // 140: a replayed sale is stamped when it HAPPENED — maybe an hour
          // back — so „new" also means „newly arrived" (received_at).
          const fresh = await allRows((a, b) => supabase.from('sales').select('*').or(liveSalesFilter(since)).order('created_at', { ascending: false }).order('id').range(a, b))
          const freshItems = await linesOf(fresh)
          if (fresh.error || freshItems.error) return
          put((state) => ({
            sales: sortSalesDesc(mergeById(state.sales, (fresh.data ?? []).map(mapSale))),
            saleItems: mergeById(state.saleItems, (freshItems.data ?? []).map(mapSaleItem)),
          }))
        })())

        if (has('customers', 'customer_payments')) jobs.push((async () => {
          const [c, p] = await Promise.all([
            allRows((a, b) => supabase.from('customers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
            allRows((a, b) => supabase.from('customer_payments').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          ])
          if (!c.error && c.data) put({ customers: c.data.map(mapCustomer) })
          if (!p.error && p.data) put({ customerPayments: p.data.map(mapCustomerPayment) })
        })())

        // 107 — a movement written on another device (its expense, if any,
        // arrives through the `expenses` signal of the same transaction).
        if (has('supplier_returns')) jobs.push(get().refreshSupplierReturns())   // 170
        // 1.65 (191) — only a device that opened those screens holds them; the rest never asks
        if (has('demand_records') && get().demands !== null) jobs.push(get().loadDemands())
        if (has('supplier_orders', 'supplier_order_items') && get().supplierOrders !== null) jobs.push(get().loadSupplierOrders())

        if (has('prepaid_moves')) jobs.push(get().refreshPrepaidMoves())   // 174
        // 1.66 (192) — only a device that opened the tables screen holds them; the rest never asks
        if (has('dining_halls', 'dining_tables', 'table_orders', 'table_order_lines', 'table_order_batches', 'table_visits') && get().bills !== null) {
          jobs.push(get().loadTables())
        }
        // 1.69 (198) — the guests' requests, where this device reads them: who sees a request is read from the party's
        // visit and its waiter, so a change of requests, visits, bills or cashiers re-reads them
        if (has('table_requests', 'table_request_lines', 'table_visits', 'table_orders', 'cashiers') && get().guestRequests !== null) {
          jobs.push(get().loadGuestRequests())
        }
        // 1.67 (193) — the cashier rows (a waiter switched off falls closed on his phone; his dishes go to the pool)
        if (has('cashiers')) jobs.push((async () => {
          const { data, error } = await allRows((a, b) => supabase.from('cashiers').select(CASHIER_COLUMNS).order('created_at', { ascending: true }).order('id').range(a, b))
          if (!error && data) put({ cashiers: data.map(mapCashier) })
          if (get().waiterReady !== null) await get().loadWaiterReady()
        })())
        // 1.67 (193) — the waiter's feed and the kitchen board, where this device opened them
        if (has('table_orders', 'table_order_lines') && get().waiterReady !== null) jobs.push(get().loadWaiterReady())
        if (has('table_orders', 'table_order_lines')) {
          const km = get().kitchenMode
          if (km && get().kitchenCards !== null) jobs.push(get().loadKitchenQueue(km.station))
          // 1.68 — a till that is also the bar holds the bar's queue (G11): the duty is the signal, not a first read
          // that may have been dropped while the shop loaded (the 1.68 walk, W6)
          else if (tillDutiesFor(get().tillDuties, get().currentOrg?.id, get().activeBranchId).bar) {
            jobs.push(get().loadKitchenQueue('bar'))
          }
        }

        if (has('cash_movements')) jobs.push((async () => {
          const { data, error } = await allRows((a, b) => supabase.from('cash_movements').select('*').order('created_at', { ascending: false }).order('id').range(a, b))
          if (!error && data) put({ cashMovements: data.map(mapCashMovement) })
        })())

        if (has('purchases', 'suppliers', 'supplier_payments')) jobs.push((async () => {
          // 134: the purchases, not every line of every purchase (see hydrate).
          const [s, pu, sp] = await Promise.all([
            allRows((a, b) => supabase.from('suppliers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
            allRows((a, b) => supabase.from('purchases').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
            allRows((a, b) => supabase.from('supplier_payments').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
          ])
          // 1.34: both replace what is on screen (liveApplied, see createPurchase).
          if (!s.error && s.data) { liveApplied++; put({ suppliers: s.data.map(mapSupplier) }) }
          if (!pu.error && pu.data) { liveApplied++; put({ purchases: pu.data.map(mapPurchase) }) }
          if (!sp.error && sp.data) put({ supplierPayments: sp.data.map(mapSupplierPayment) })
        })())

        if (has('expenses')) jobs.push((async () => {
          const { data, error } = await allRows((a, b) => supabase.from('expenses').select('*').order('expense_date', { ascending: false }).order('id').range(a, b))
          if (!error && data) put({ expenses: data.map(mapExpense) })
        })())

        // 121 — a monthly cost changed on another device, or the morning run
        // booked one (its rows arrive through the `expenses` signal).
        if (has('recurring_expenses')) jobs.push(refreshRecurring())

        // 122 — a till removed something, or a price changed. A cashier's
        // device cannot read either log, so it does not ask.
        if (has('cart_removals', 'price_changes') && get().userRole !== 'cashier') jobs.push((async () => {
          const since = get().salesLoadedFrom ?? windowStart()
          const { removals, prices } = await readLossLogs(since)
          if (removals && has('cart_removals')) put({ cartRemovals: removals })
          if (prices && has('price_changes')) put({ priceChanges: prices })
        })())

        // 1.57 (177) — an owner's switch changed on another device: only the switches are read
        // again (Codex, final review #2: a till kept selling gift cards for hours after the
        // owner had turned them off). 1.63 (Codex #I5) — and the club's threshold and code and the
        // bonus switch and rate, so a till never prices or earns on a setting the owner changed.
        if (has('settings')) jobs.push((async () => {
          const { data, error } = await supabase.from('settings').select('key, value')
            .or('key.like.feature.*,key.like.club.*,key.like.table.*,key.eq.loyaltyEnabled,key.eq.loyaltyPct')
          if (error || !data) return
          const kv = Object.fromEntries((data as { key: string; value: string }[]).map((r) => [r.key, r.value]))
          // 196 (1.68) — and the café's rate and its free-on-pay switch
          put({ settings: { ...get().settings, features: featuresFromKv(kv), ...clubSettingsFromKv(kv), ...tableSettingsFromKv(kv) } })
        })())

        if (has('stock_transfers')) jobs.push((async () => {
          const [t, ti] = await Promise.all([
            allRows((a, b) => supabase.from('stock_transfers').select('*').order('created_at', { ascending: false }).order('id').range(a, b)),
            allRows((a, b) => supabase.from('stock_transfer_items').select('*').order('id').range(a, b)),
          ])
          if (!t.error && t.data && !ti.error && ti.data) put({ stockTransfers: t.data.map(mapStockTransfer), stockTransferItems: ti.data.map(mapStockTransferItem) })
        })())

        await Promise.allSettled(jobs)
      },

      updateCustomer: async (id, c) => {
        // 1.63 — the club identity is the club RPCs' (188's guard), and two of its columns are generated
        const { error } = await supabase.from('customers').update(withoutClubColumns(c)).eq('id', id)
        if (failedT(error, 'store.customerUpdateFailed')) return
        set((state) => ({ customers: state.customers.map((x) => (x.id === id ? { ...x, ...c } : x)) }))
      },

      getInvoice: async (saleId) => {
        const { data, error } = await supabase.from('invoices').select('*').eq('sale_id', saleId).maybeSingle()
        if (error) { console.error('invoice read failed', error); return null }
        return (data as import('./invoice').InvoiceRow | null) ?? null
      },

      issueInvoice: async (saleId, customerId) => {
        const { data, error } = await supabase.rpc('issue_invoice', { p_sale_id: saleId, p_customer_id: customerId })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('BUYER_REQUIRED')) { toast.error(t('store.x.76')); return null }
          if (m.includes('NOT_A_SALE')) { toast.error(t('store.x.77')); return null }
          failedT(error, 'store.x.78')
          return null
        }
        return data as import('./invoice').InvoiceRow
      },

      deleteCustomer: async (id) => {
        // `customer_payments.customer_id` cascades on delete, so deleting a
        // customer who owes, or who has ever paid, erases a ledger and writes a
        // debt off with no row saying so (audit, 2026-09-11). Refuse it here —
        // the one home of the rule — until the balance is settled.
        const c = get().customers.find((x) => x.id === id)
        if (c && Math.abs(c.balance) >= 0.005) {
          toast.error(t(c.balance > 0 ? 'store.customerHasDebt' : 'store.customerHasCredit', { name: c.name, sum: `₾${Math.abs(c.balance).toFixed(2)}` }))
          return false
        }
        if (c && Math.abs(c.bonus_balance) >= 0.005) {
          toast.error(t('store.customerHasBonus', { name: c.name, sum: `₾${c.bonus_balance.toFixed(2)}` }))
          return false
        }
        if (get().customerPayments.some((p) => p.customer_id === id)) {
          toast.error(t('store.customerHasPayments'))
          return false
        }
        const { error } = await supabase.from('customers').delete().eq('id', id)
        if (failedT(error, 'store.customerDeleteFailed')) return false
        set((state) => ({ customers: state.customers.filter((x) => x.id !== id) }))
        return true
      },

      payCustomer: async (customerId, amount, note, method = 'cash', cardProvider) => {
        const openShiftRow = selectOpenShift(get())
        const before = get().customers.find((c) => c.id === customerId)?.balance   // 1.47: paymentLanded
        const { data, error } = await supabase.rpc('pay_customer', {
          p_customer_id: customerId,
          p_amount: amount,
          p_note: note ?? null,
          p_shift_id: openShiftRow?.id ?? null,
          p_method: method,
          p_card_provider: cardProvider ?? null,
        })
        if (error || !data) {
          // 1.47 (Codex's review): an answer lost on the network may have been
          // lost AFTER the commit. The debt is read again before anything is
          // said and before the dialog's button wakes up: moved by exactly this
          // amount, the payment was booked (lib/offline.ts paymentLanded).
          if (isNetworkError(error)) {
            await get().refreshLive(['customers', 'customer_payments'])
            if (paymentLanded(before, get().customers.find((c) => c.id === customerId)?.balance, amount)) return true
          }
          if (isShiftClosed(error)) await refreshShifts()
          failedT(error, 'store.paymentFailed')
          return false
        }
        const payment = mapCustomerPayment((data as any).payment)
        const customer = mapCustomer((data as any).customer)
        set((state) => ({
          customerPayments: [payment, ...state.customerPayments],
          customers: state.customers.map((c) => (c.id === customerId ? customer : c)),
        }))
        return true
      },

      // ---- Cashiers ----
      addCashier: async (c) => {
        const { data, error } = await supabase
          .from('cashiers')
          .insert({ name: c.name, pin: c.pin, branch_id: c.branch_id ?? null, max_discount_pct: c.max_discount_pct ?? null })
          .select(CASHIER_COLUMNS)
          .single()
        if (error) {
          if ((error as { code?: string }).code === '23505') toast.error(t('store.x.79'))
          else failedT(error, 'store.x.80')
          return null
        }
        if (!data) return null
        const cash = mapCashier(data)
        set((state) => ({ cashiers: [...state.cashiers, cash] }))
        return cash
      },

      updateCashier: async (id, c) => {
        const { error } = await supabase.from('cashiers').update(c).eq('id', id)
        if (error) {
          if ((error as { code?: string }).code === '23505') toast.error(t('store.x.79'))
          else failedT(error, 'store.x.81')
          return false
        }
        // 116: the PIN just written does not go into the store (it is
        // persisted on the device, and the till must not hold PINs).
        const { pin: _written, ...rest } = c
        set((state) => ({ cashiers: state.cashiers.map((x) => (x.id === id ? { ...x, ...rest } : x)) }))
        return true
      },

      cashierPins: async () => {
        const { data, error } = await supabase.rpc('cashier_pins')
        if (error) { console.error('cashier_pins', error); return null }
        return new Map(((data ?? []) as { id: string; pin: string }[]).map((r) => [r.id, r.pin]))
      },

      deleteCashier: async (id) => {
        const { error } = await supabase.from('cashiers').delete().eq('id', id)
        if (failedT(error, 'store.x.82')) return
        set((state) => ({ cashiers: state.cashiers.filter((x) => x.id !== id) }))
      },

      // ---- Shifts (ცვლები) ----
      openShiftWithPin: async (pin, openingCash) => {
        // 116 — the PIN goes to the server, which finds the cashier (and
        // counts a wrong PIN); the name on the shift is the database's.
        const { data, error } = await supabase.rpc('open_shift_with_pin', {
          p_pin: pin,
          p_opening_cash: openingCash,
          p_branch_id: get().activeBranchId, // shifts are per-branch since 041
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('PIN_LOCKED')) toast.error(t('store.pinLocked'), { duration: 8000 })
          else if (m.includes('SHIFT_ALREADY_OPEN')) toast.error(t('store.shiftAlreadyOpen'))
          // 066. The dialog filters PINs by branch already, so reaching this
          // means a stale tab or a replayed request — say which branch, or the
          // cashier hunts for a typo in a PIN that is perfectly correct.
          else if (m.includes('CASHIER_WRONG_BRANCH')) toast.error(t('store.cashierWrongBranch'))
          // 1.67 (193) — a waiter's PIN opens no shift (nor does a waiter login)
          else if (m.includes('SHIFT_WAITER_FORBIDDEN')) toast.error(t('store.shiftWaiterForbidden'))
          // 067. The dialog catches this from its own copy of the shifts, but
          // that copy is a cache — another till may have opened a shift for
          // her a minute ago. The server names the branch; the race path
          // (the unique index firing) cannot, hence the fallback wording.
          else if (m.includes('CASHIER_ALREADY_OPEN')) {
            const where = m.split('CASHIER_ALREADY_OPEN:')[1]?.trim()
            toast.error(where
              ? t('store.cashierOpenAt', { where })
              : t('store.cashierOpenElsewhere'))
          }
          else failedT(error, 'store.openFailed')
          return null
        }
        // A wrong PIN is RETURNED, not raised (the server counts it).
        if ((data as { refused?: string } | null)?.refused) {
          toast.error(t('store.pinWrong'))
          return null
        }
        const shift = mapShift(data)
        set((state) => ({ shifts: [shift, ...state.shifts] }))
        return shift
      },

      closeShift: async (shiftId, closingCash, takeout) => {
        const { data, error } = await supabase.rpc('close_shift', {
          p_shift_id: shiftId,
          p_closing_cash: closingCash,
          ...(takeout
            ? {
                p_float: takeout.float, p_takeout_reason: takeout.reason, p_takeout_note: takeout.note?.trim() || null,
                // 120 — only a bank take-out names one
                p_takeout_bank: takeout.reason === 'bank' ? takeout.bank ?? null : null,
                // 137 — the cashier's salary, and the owner's PIN above the
                // ceiling. Omitted when 0: a shop without a salary sends the
                // very call 1.17 sent.
                ...(takeout.salary ? { p_salary: takeout.salary } : {}),
                ...(takeout.overridePin ? { p_override_pin: takeout.overridePin } : {}),
              }
            : {}),
        })
        if (error) {
          const m = (error as { message?: string }).message ?? ''
          if (m.includes('PIN_LOCKED')) { toast.error(t('store.ownerPinLocked'), { duration: 8000 }); return null }
          // 137 — the server names the real ceiling (a salary already paid this
          // shift, or the owner changed it): keep it, the dialog proposes it.
          const limit = salaryLimitFrom(m)
          if (limit != null) {
            set({ salaryCeilingHint: { shiftId, day: businessDay(new Date(), get().settings.dayStartHour), limit } })
            toast.error(t('close.err.salaryLimit', { sum: `₾${limit.toFixed(2)}` }), { duration: 7000 })
            return null
          }
          const said = CLOSE_REFUSALS.find(([code]) => m.includes(code))?.[1]
          if (said) { console.error('closeShift', error); toast.error(t(said)); return null }
        }
        if (failedT(error, 'store.closeFailed') || !data) return null
        // 137 — a wrong money PIN comes back { refused } (counted by the
        // server, not raised): the shift is still open.
        if ((data as { refused?: string }).refused) { toast.error(t('close.err.pinWrong'), { duration: 6000 }); return null }
        const shift = mapShift(data)
        const salaryRow = (data as { salary_expense?: unknown }).salary_expense
        set((state) => ({
          shifts: state.shifts.map((s) => (s.id === shiftId ? shift : s)),
          // 137 — the salary it booked, for whoever reads the books (a
          // cashier's copy stays without, as RLS would leave it)
          expenses: salaryRow && state.userRole !== 'cashier' ? [mapExpense(salaryRow), ...state.expenses] : state.expenses,
          salaryCeilingHint: null,
        }))
        return shift
      },

      // ---- The drawer (107): money in or out during a shift, with a reason ----
      recordCashMovement: async (m) => {
        const shift = selectOpenShift(get())
        if (!shift) { toast.error(t('store.shiftRequired')); return false }
        // Sales queue offline; this does not. The server takes the shift's row
        // lock, which is what keeps a movement from landing after the count.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          toast.error(t('store.drawerOffline'))
          return false
        }
        const { data, error } = await supabase.rpc('record_cash_movement', {
          p_shift_id: shift.id,
          p_direction: m.direction,
          p_amount: m.amount,
          p_reason: m.reason,
          p_note: m.note?.trim() || null,
          p_expense_category: m.reason === 'expense' ? (m.expenseCategory ?? 'other') : null,
          p_bank: m.reason === 'bank' ? m.bank ?? null : null,   // 120
        })
        if (error || !data) {
          if (error) await drawerRefused(error, 'store.drawerFailed')
          return false
        }
        const movement = mapCashMovement((data as any).movement)
        const expenseRow = (data as any).expense
        set((state) => ({
          cashMovements: [movement, ...state.cashMovements],
          // The expense it booked, for whoever reads the books. A cashier's
          // copy stays as RLS would leave it: without.
          expenses: expenseRow && state.userRole !== 'cashier'
            ? [mapExpense(expenseRow), ...state.expenses]
            : state.expenses,
        }))
        return true
      },

      deleteCashMovement: async (id) => {
        const { error } = await supabase.rpc('delete_cash_movement', { p_id: id })
        if (error) {
          await drawerRefused(error, 'store.drawerDeleteFailed')
          return false
        }
        set((state) => ({
          cashMovements: state.cashMovements.filter((x) => x.id !== id),
          // ON DELETE CASCADE took its expense on the server; match it here.
          expenses: state.expenses.filter((x) => x.cash_movement_id !== id),
        }))
        return true
      },

      // ---- Operational expenses (ხარჯები) ----
      addExpense: async (e) => {
        const { data, error } = await supabase
          .from('expenses')
          .insert({
            category: e.category,
            amount: e.amount,
            note: e.note?.trim() || null,
            vendor_tin: e.vendor_tin?.trim() || null,
            ...(e.expense_date ? { expense_date: e.expense_date } : {}),
            // Which branch is paying — the one the CALLER is looking at,
            // falling back to the device's own.
            //
            // It used to be `activeBranchId` unconditionally, which meant an
            // owner viewing branch B on the accounting page could enter B's
            // rent, have it filed against branch A, and watch the row never
            // appear in the list he was looking at. The natural response is to
            // type it again, and now the books hold the rent twice.
            //
            // RLS re-checks the branch is ours (`branch_in_org`, migration
            // 060): a WITH CHECK on org_id alone would accept a row in the
            // right org aimed at another tenant's shop.
            ...((e.branch_id ?? get().activeBranchId) ? { branch_id: e.branch_id ?? get().activeBranchId } : {}),
          })
          .select()
          .single()
        if (failedT(error, 'store.x.83') || !data) return
        set((state) => ({ expenses: [mapExpense(data), ...state.expenses] }))
        toast.success(t('store.x.84'))
      },

      deleteExpense: async (id) => {
        const { error } = await supabase.from('expenses').delete().eq('id', id)
        if (failedT(error, 'store.x.85')) return
        set((state) => ({ expenses: state.expenses.filter((x) => x.id !== id) }))
      },

      // ---- Monthly costs (121): the database books them; these ask it to ----
      saveRecurringExpense: async (r) => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          toast.error(t('store.x.86'))
          return null
        }
        const { data, error } = await supabase.rpc('save_recurring_expense', {
          p_id: r.id ?? null,
          p_category: r.category,
          p_amount: r.amount,
          p_day: r.day,
          p_mode: r.mode,
          p_note: r.note?.trim() || null,
          p_vendor_tin: r.vendor_tin?.trim() || null,
          p_branch_id: r.branch_id ?? null,
          p_active: r.active ?? true,
          p_this_month: r.thisMonth ?? false,
        })
        if (error || !data) {
          if (error) await recurringRefused(error, 'store.x.87')
          return null
        }
        const recurring = mapRecurringExpense((data as any).recurring)
        const booked: Expense[] = (((data as any).expenses ?? []) as any[]).map(mapExpense)
        set((state) => ({
          recurringExpenses: state.recurringExpenses.some((x) => x.id === recurring.id)
            ? state.recurringExpenses.map((x) => (x.id === recurring.id ? recurring : x))
            : [...state.recurringExpenses, recurring],
          expenses: booked.length ? [...booked, ...state.expenses].sort(byExpenseDateDesc) : state.expenses,
        }))
        return { recurring, booked }
      },

      answerRecurringExpense: async (id, amount) => {
        const { data, error } = await supabase.rpc('book_recurring_expense', { p_id: id, p_amount: amount })
        if (error || !data) {
          if (error) await recurringRefused(error, amount == null ? 'store.x.88' : 'store.x.89')
          return false
        }
        const recurring = mapRecurringExpense((data as any).recurring)
        const row = (data as any).expense
        set((state) => ({
          recurringExpenses: state.recurringExpenses.map((x) => (x.id === recurring.id ? recurring : x)),
          expenses: row ? [mapExpense(row), ...state.expenses].sort(byExpenseDateDesc) : state.expenses,
        }))
        return true
      },

      deleteRecurringExpense: async (id) => {
        const { error } = await supabase.rpc('delete_recurring_expense', { p_id: id })
        if (error) {
          await recurringRefused(error, 'store.x.90')
          return false
        }
        set((state) => ({
          recurringExpenses: state.recurringExpenses.filter((x) => x.id !== id),
          // ON DELETE SET NULL (recurring_id) cleared the link on the server.
          expenses: state.expenses.map((x) => (x.recurring_id === id ? { ...x, recurring_id: null } : x)),
        }))
        return true
      },

      // ---- Fiscal result of a sale (called after the device responds) ----
      updateSaleFiscal: async (saleId, fiscal) => {
        const patch = {
          fiscal_status: fiscal.fiscal_status,
          fiscal_id: fiscal.fiscal_id ?? null,
          fiscal_data: fiscal.fiscal_data ?? null,
          fiscalized_at: fiscal.fiscal_status === 'success' ? new Date().toISOString() : null,
        }
        const { error } = await supabase.from('sales').update(patch).eq('id', saleId)
        if (failedT(error, 'store.x.91')) return
        set((state) => ({
          sales: state.sales.map((s) => (s.id === saleId ? { ...s, ...patch } : s)),
        }))
      },

      // ---- 128: a unit's story by its IMEI / serial ----
      lookupSerial: async (serial) => {
        const s = normalizeSerial(serial)
        if (!s) return null
        const { data, error } = await supabase.rpc('serial_lookup', { p_serial: s })
        if (error || !data) {
          console.error('serial_lookup', error)
          return null
        }
        const d = data as { serial?: string; events?: any[] }
        return {
          serial: d.serial ?? s,
          events: (d.events ?? []).map((e) => ({
            sale_id: e.sale_id,
            type: e.type === 'return' ? 'return' : 'sale',
            created_at: e.created_at,
            reversal_of: e.reversal_of ?? null,
            branch_name: e.branch_name ?? null,
            cashier_name: e.cashier_name ?? null,
            customer_name: e.customer_name ?? null,
            product_id: e.product_id ?? null,
            product_name: e.product_name ?? '',
            unit_price: num(e.unit_price),
            quantity: num(e.quantity),
            total_price: num(e.total_price),
            warranty_until: e.warranty_until ?? null,
          })),
        }
      },

      exchangeCandidates: async ({ productId = null, modelId = null, receipt = null, before = null }) => {
        const { data, error } = await supabase.rpc('exchange_candidates', {
          p_product_id: productId, p_model_id: modelId, p_receipt: receipt, p_before: before,
        })
        if (error || !data) { console.error('exchange_candidates', error); return null }
        return mapCandidatePage(data)
      },

      createExchange: async (req) => {
        const shift = selectOpenShift(get())
        if (!shift) return { ok: false, key: 'shift', params: {}, definitive: true }
        // What was on screen when this left (the 1.24 generation, the 1.34 liveApplied lesson).
        const gen = bootGen
        const orgId = get().currentOrg?.id
        const branchId = get().activeBranchId
        const seen = liveApplied
        const { data, error } = await supabase.rpc('create_exchange', {
          p_original: req.originalId,
          p_back: req.back,
          p_new: req.next,
          p_paid_cash: req.paidCash,
          p_paid_card: req.paidCard,
          p_card_provider: req.cardProvider,
          p_shift_id: shift.id,
          p_branch_id: branchId,
          p_client_ref: req.clientRef,
          p_override_pin: req.overridePin,
        })
        if (error) {
          // A lost reply may have committed: the caller retries with the SAME ref and the server replays.
          if (isNetworkError(error)) return { ok: false, key: 'lost', params: {}, definitive: false }
          const e = exchangeErrorOf(String((error as { message?: string }).message ?? ''))
          return { ok: false, key: e.key, params: e.params, definitive: true }
        }
        const d = data as any
        if (d?.refused) return { ok: false, key: 'pin', params: {}, definitive: true }
        const ret = mapSale(d.return.sale)
        const sale = mapSale(d.sale.sale)
        const retItems = ((d.return.items ?? []) as any[]).map(mapSaleItem)
        const saleItems = ((d.sale.items ?? []) as any[]).map(mapSaleItem)
        const out: ExchangeOutcome = {
          ok: true, exchangeId: String(d.exchange_id), credited: num(d.credited), difference: num(d.difference),
          ret, sale, retItems, saleItems,
        }
        // Another shop, branch or session took the screen while this was in flight: not our cache.
        if (gen !== bootGen || get().currentOrg?.id !== orgId || get().activeBranchId !== branchId) return out
        set((state) => ({
          sales: prependUnique([sale, ret], state.sales),
          saleItems: appendUnique(state.saleItems, [...retItems, ...saleItems]),
        }))
        // A replay, or a live refresh that may already hold this exchange: read the shelf, never add twice.
        if (d.replayed === true || liveApplied !== seen) {
          await get().refreshProducts({ quiet: true })
          return out
        }
        const delta = stockDelta(retItems, saleItems)
        set((state) => ({
          products: state.products.map((p) => {
            const dq = delta.get(p.id)
            if (!dq || p.is_service) return p   // 169 — a service's stock never moves
            return {
              ...p,
              quantity: Math.max(0, roundWeight(p.quantity + dq)),
              ...(p.org_quantity !== undefined ? { org_quantity: Math.max(0, roundWeight(p.org_quantity + dq)) } : {}),
            }
          }),
        }))
        return out
      },

      setCategoryUnitRules: async (categoryIds, patch) => {
        if (categoryIds.length === 0) return 0
        const touched = new Set<string>()
        if (patch.warranty_months !== undefined) {
          const { data, error } = await supabase.from('products')
            .update({ warranty_months: patch.warranty_months })
            .in('category_id', categoryIds)
            .select('id')
          if (failedT(error, 'wh.bulk.failed')) return null
          for (const r of (data ?? []) as { id: string }[]) touched.add(r.id)
        }
        if (patch.track_serial !== undefined) {
          // A kilogram has no serial number: switching ON reaches pieces only.
          let q = supabase.from('products')
            .update({ track_serial: patch.track_serial })
            .in('category_id', categoryIds)
          if (patch.track_serial) q = q.eq('unit', 'piece')
          const { data, error } = await q.select('id')
          if (failedT(error, 'wh.bulk.failed')) return null
          for (const r of (data ?? []) as { id: string }[]) touched.add(r.id)
        }
        const cats = new Set(categoryIds)
        set((state) => ({
          products: state.products.map((p) => {
            if (!cats.has(p.category_id)) return p
            const next = { ...p }
            if (patch.warranty_months !== undefined) next.warranty_months = patch.warranty_months
            if (patch.track_serial !== undefined && (!patch.track_serial || p.unit === 'piece')) next.track_serial = patch.track_serial
            return next
          }),
        }))
        return touched.size
      },

      // ---- 130: repairs ----
      pendingRepairCharge: null,
      pendingRepairIntake: null,

      listRepairs: async () => {
        const { data, error } = await supabase.from('repairs').select('*')
          .order('created_at', { ascending: false }).limit(1000)
        if (error) { console.error('repairs list', error); return null }
        return (data ?? []).map(mapRepair)
      },

      addRepair: async (draft) => {
        const { data, error } = await supabase.from('repairs').insert({
          ...draft,
          serial: draft.serial ? normalizeSerial(draft.serial) : null,
          branch_id: draft.branch_id ?? get().activeBranchId ?? null,
        }).select('*').single()
        if (failedT(error, 'rep.err.save')) return null
        return mapRepair(data)
      },

      updateRepair: async (id, patch) => {
        const { data, error } = await supabase.from('repairs').update(patch).eq('id', id).select('*').single()
        if (failedT(error, 'rep.err.save')) return null
        return mapRepair(data)
      },

      // ---- 153: the shop's website ----
      refreshStoreSite: async () => {
        const g = bootGen
        const seq = ++siteSeq
        const orgId = get().currentOrg?.id
        if (!orgId || !isBrowserOnline()) return
        const { data, error } = await supabase.from('store_sites')
          .select('slug, enabled, listing, tagline, hero_url, phone, delivery_fee, free_delivery_from')
          .eq('org_id', orgId).maybeSingle()
        // another shop (or a sign-out) since the question was asked → not ours;
        // a newer read or a save since → its answer stands
        if (g !== bootGen || get().currentOrg?.id !== orgId || seq !== siteSeq) return
        if (error) { console.error('store site', error); return }
        set({ storeSite: mapStoreSite(data) })
      },

      updateStoreSite: async (patch) => {
        const { data, error } = await supabase.rpc('update_store_site', {
          p_tagline: patch.tagline, p_hero_url: patch.hero_url, p_phone: patch.phone,
          p_delivery_fee: patch.delivery_fee, p_free_delivery_from: patch.free_delivery_from, p_listing: patch.listing,
        })
        if (error) {
          const m = /STORE_INVALID:([a-z]+)/.exec((error as { message?: string }).message ?? '')
          if (m) toast.error(t('set.store.err.invalid', { field: t(`set.store.f.${m[1]}` as Key) }))
          else failedT(error, 'set.store.err.save')
          return false
        }
        siteSeq++ // a read still on its way is older than this
        set({ storeSite: mapStoreSite(data) })
        return true
      },

      storeListingCount: async () => {
        const { data, error } = await supabase.rpc('store_listing_count')
        if (error || !data) { console.error('store listing count', error); return null }
        const d = data as { manual?: unknown; auto?: unknown }
        return { manual: Number(d.manual ?? 0), auto: Number(d.auto ?? 0) }
      },

      setCategoryOnline: async (categoryIds, action) => {
        if (categoryIds.length === 0) return 0
        const { data, error } = await supabase.rpc('set_category_online', { p_category_ids: categoryIds, p_action: action })
        // 154: the mode was changed on another device — this one reads it again
        // and the dialog's buttons follow; nothing was written
        if (error && /MODE_CHANGED|NO_STORE/.test((error as { message?: string }).message ?? '')) {
          toast.error(t('wh.online.modeChanged'))
          void get().refreshStoreSite()
          return null
        }
        if (failedT(error, 'wh.bulk.failed')) return null
        // Many rows may have changed: the delta read (or, past its cap, the
        // full one) brings them — the marks are never guessed here.
        await get().refreshProducts({ quiet: true })
        return Number(data ?? 0)
      },

      // ---- B2: online orders (151, 152) ----
      listOnlineOrders: async () => {
        const { data, error } = await supabase.from('online_orders').select(ONLINE_ORDER_SELECT)
          .order('created_at', { ascending: false }).limit(500)
        if (error) { console.error('online orders list', error); return null }
        return (data ?? []).map(mapOnlineOrder)
      },

      setOnlineOrderStatus: async (id, status, reason, branchId) => {
        const { error } = await supabase.rpc('set_online_order_status', {
          p_order_id: id, p_status: status, p_reason: reason ?? null, p_branch_id: branchId ?? null,
        })
        if (!error) return true
        const key = onlineErrorKey((error as { message?: string }).message ?? '')
        if (key) toast.error(t(key))
        else failedT(error, 'oo.err.save')
        return false
      },
    }),
    {
      name: 'pos-ui-prefs',
      storage: createJSONStorage(() => safeLocalStorage),
      // Only persist lightweight UI prefs locally. All real data lives in Supabase.
      partialize: (state) => ({
        isDesktopSidebarCollapsed: state.isDesktopSidebarCollapsed,
        // POS survival kit for offline refreshes: enough to render the grid,
        // keep the shift, and sell. Sales/history stay server-only (heavy and
        // not needed to operate the till). The PRODUCTS are not here since
        // 2026-09-26 — 10 000 of them overflow localStorage; their copy is in
        // IndexedDB (lib/product-cache.ts, the subscription below).
        categories: state.categories,
        settings: state.settings,
        pin: state.pin,
        // with the pin it describes — an offline reload must not forget that
        // the pin above is only the default
        pinIsDefault: state.pinIsDefault,
        cashiers: state.cashiers,
        shifts: state.shifts,
        // branches + active branch survive offline refreshes so queued sales
        // keep their branch and selectOpenShift finds the right shift
        branches: state.branches,
        activeBranchId: state.activeBranchId,
        // 086: the till prices from these; an offline refresh must still see them
        promotions: state.promotions,
        // kiosk lock must survive a refresh — otherwise F5 escapes the kiosk
        kioskMode: state.kioskMode,
        // 1.67 — the kitchen lock survives a refresh like the kiosk's
        kitchenMode: state.kitchenMode,
        // 1.68 — this device's café duties (with the shop and the branch they belong to)
        tillDuties: state.tillDuties,
        currentOrg: state.currentOrg,
        isPlatformAdmin: state.isPlatformAdmin,
        userRole: state.userRole,
        // Persisted WITH the cache it describes, or a reload would forget
        // whose shop is in localStorage and trust it again.
        cachedUserId: state.cachedUserId,
      }),
    }
  )
)

// The products' offline copy follows the list: a second after it settles,
// for the user it belongs to, with the meta that belongs to it (1.25). An
// emptied list is clearTenantCache's business (it deletes the copy), never a
// reason to save an empty one.
if (typeof window !== 'undefined') {
  useStore.subscribe((state, prev) => {
    if (state.products === prev.products && state.serviceRows === prev.serviceRows) return
    // 196 (1.68) — the service product's rows travel with the copy: the next boot's delta counts them
    const all = state.serviceRows.length > 0 ? [...state.products, ...state.serviceRows] : state.products
    const snap = cacheSnapshot(
      { cachedUserId: state.cachedUserId, orgId: state.currentOrg?.id, userRole: state.userRole, products: all },
      { watermark: productsWatermark, lens: productsLens, local: productsLocal },
    )
    if (snap) scheduleProductCache(snap)
  })
  window.addEventListener('pagehide', flushProductCache)
}
